import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { PromptManagementPage } from './PromptManagementPage'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8099/lume-tuber/api'
const pageSize = 200
const NOTE_DETAIL_NAV_KEY = 'lume-note-detail-navigation'
const SEMANTIC_SEARCH_STATE_KEY = 'lume-semantic-search-state'
// TODO(phase2): split this admin shell into focused page/components before adding more note-management flows.
const api = axios.create({ baseURL: BASE_URL, withCredentials: true })

const pageMeta = {
  dashboard: ['概览', '采集数据总览与内容运营漏斗'],
  notes: ['笔记库', '全量采集笔记的检索、打标与入池'],
  noteDetail: ['笔记详情', '查看笔记内容、媒体、标签与评论'],
  tags: ['标签管理', '标签分组、配色与使用情况'],
  pool: ['内容池', '优质内容审核、圈选与下游分发'],
  prompts: ['Prompt 管理', 'Prompt 版本、生效状态与操作日志'],
  collectionTasks: ['采集任务', '按关键词、账号或笔记链接配置采集源'],
  authors: ['作者库', '作者画像与小红书号补全情况'],
  extensions: ['插件', '采集助手下载与环境配置'],
  settings: ['设置', '接口、下游与成员权限'],
}

const navGroups = [
  { title: '工作台', items: [{ view: 'dashboard', label: '概览', icon: '▦' }] },
  {
    title: '数据资产',
    items: [
      { view: 'notes', label: '笔记库', icon: '▤', badge: 'notes' },
      { view: 'authors', label: '作者库', icon: '◎' },
    ],
  },
  {
    title: '内容运营',
    items: [
      { view: 'tags', label: '标签管理', icon: '◇' },
      { view: 'pool', label: '内容池', icon: '◈', badge: 'pool' },
      { view: 'prompts', label: 'Prompt 管理', icon: '⌘' },
    ],
  },
  {
    title: '采集配置',
    items: [
      { view: 'collectionTasks', label: '采集任务', icon: '⌕' },
      { view: 'extensions', label: '插件', icon: '▣' },
    ],
  },
]

const seedTagGroups = [
  { id: 'content', name: '内容方向', color: 'violet', tags: ['美白', '护肤教程', '成分党', '英语启蒙', '亲子阅读'] },
  { id: 'audience', name: '受众人群', color: 'blue', tags: ['新手妈妈', '学生党', '敏感肌', '0-3岁', '3-6岁'] },
  { id: 'format', name: '内容形式', color: 'rose', tags: ['干货清单', '测评', '教程', '种草', 'vlog'] },
  { id: 'quality', name: '质量等级', color: 'amber', tags: ['爆款', '优质', '普通', '低质'] },
]

const fallbackContentPools = [
  { id: 1, name: '美白内容池' },
  { id: 2, name: '英语启蒙池' },
  { id: 3, name: '亲子阅读池' },
]
const tagGroupColors = ['violet', 'blue', 'rose', 'amber']
const keywordSearchTypes = [
  { value: 'all', label: '图文+视频' },
  { value: 'image', label: '仅图文' },
  { value: 'video', label: '仅视频' },
]
const keywordSortRules = [
  { value: 'composite', label: '综合' },
  { value: 'latest', label: '最新' },
  { value: 'likes', label: '最多点赞' },
  { value: 'comments', label: '最多评论' },
  { value: 'collects', label: '最多收藏' },
]
const keywordPublishTimes = [
  { value: 'all', label: '不限' },
  { value: 'day', label: '一天内' },
  { value: 'week', label: '一周内' },
  { value: 'halfYear', label: '半年内' },
]
const taskStatusOptions = [
  { value: '', label: '全部状态' },
  { value: 'draft', label: '草稿' },
  { value: 'active', label: '启用' },
  { value: 'paused', label: '暂停' },
  { value: 'completed', label: '已完成' },
  { value: 'archived', label: '归档' },
]
const taskTypeOptions = [
  { value: '', label: '全部类型' },
  { value: 'keyword', label: '关键词采集' },
  { value: 'account', label: '账号采集' },
  { value: 'note', label: '笔记链接' },
]
const publishTimeFilters = [
  { value: '', label: '发布时间不限' },
  { value: 'day', label: '一天内', days: 1 },
  { value: 'week', label: '一周内', days: 7 },
  { value: 'halfYear', label: '半年内', days: 183 },
]
const noteSortOptions = [
  { value: 'composite', label: '综合' },
  { value: 'latest', label: '最新' },
  { value: 'likes', label: '最多点赞' },
  { value: 'comments', label: '最多评论' },
  { value: 'collects', label: '最多收藏' },
  { value: 'shares', label: '最多分享' },
  { value: 'publishTime', label: '发布时间' },
]
const poolStatusLabels = {
  all: '全部',
  review: '待审核',
  approved: '已通过',
  pooled: '待审核',
  used: '已归档',
  archived: '已归档',
  rejected: '已拒绝',
}
const poolLifecycleLabels = {
  draft: '待发布',
  active: '已发布',
  archived: '已归档',
}
const poolWorkspaceTabs = [
  { value: 'review', label: '样本审核' },
  { value: 'search', label: '语义检索' },
  { value: 'index', label: '向量索引' },
]
const semanticDimensions = [
  { value: 'title_body', label: '主体文本' },
  { value: 'image', label: '图片语义' },
  { value: 'comment', label: '评论语义' },
  { value: 'ops_review', label: '运营备注' },
]
const semanticDimensionLabels = Object.fromEntries(semanticDimensions.map(item => [item.value, item.label]))
const semanticDimensionUi = {
  title_body: {
    cardHint: '待处理',
    coveredHint: '标题/正文已覆盖',
    pendingHint: '笔记内容变更',
    action: '重建主体文本',
  },
  image: {
    cardHint: '待补齐',
    coveredHint: '图片分析已覆盖',
    pendingHint: '图片分析节点',
    action: '重试图片向量化',
  },
  comment: {
    cardHint: '未参与/待补齐',
    coveredHint: '评论摘要已覆盖',
    pendingHint: '评论入库/摘要节点',
    inactiveHint: '无可用评论文本，不参与检索',
    action: '重跑评论摘要',
    inactiveLabel: '不参与',
  },
  ops_review: {
    cardHint: '待更新',
    coveredHint: '运营备注已覆盖',
    pendingHint: '审核备注变更',
    action: '重建运营备注',
  },
}
const semanticStatusLabels = {
  source_unresolved: '来源未绑定',
  pending: '待处理',
  partial_searchable: '部分可检索',
  searchable: '可检索',
  stale: '待刷新',
  retry_scheduled: '待重试',
  manual_required: '需处理',
  disabled: '不可用',
  skipped: '已跳过',
  source_pending: '等材料',
  text_ready: '文本就绪',
  embedding: '向量生成中',
  running: '处理中',
  ready: '已完成',
  failed: '失败',
}
const semanticDimensionStatusFilters = [
  'searchable',
  'text_ready',
  'embedding',
  'source_pending',
  'stale',
  'retry_scheduled',
  'manual_required',
  'disabled',
  'skipped',
  'failed',
]
const semanticDimensionStatusNotSearchable = '__not_searchable'
const semanticDimensionStatusGroupLabels = {
  [semanticDimensionStatusNotSearchable]: '未覆盖/待处理',
}
const semanticEventTypeLabels = {
  NOTE_VECTOR_ADMITTED: '索引记录准入',
  NOTE_VECTOR_REENABLED: '索引记录恢复',
  NOTE_VECTOR_DISABLED: '索引记录停用',
  NOTE_VECTOR_STATUS_CHANGED: '索引状态变更',
  NOTE_ROW_BINDING_FAILED: '笔记来源绑定失败',
  DIMENSION_SOURCE_CHANGED: '维度材料变更',
  DIMENSION_IDENTITY_INVALIDATED: '向量身份失效',
  EMBEDDING_CLAIMED: '向量化任务领取',
  EMBEDDING_COMPLETED: '向量化完成',
  EMBEDDING_FAILED: '向量化失败',
  EMBEDDING_CLAIM_RELEASED: '向量化任务释放',
  EMBEDDING_DISCARDED_NOT_APPROVED: '向量化任务丢弃',
  IMAGE_ANALYSIS_CLAIMED: '图片语义提取领取',
  IMAGE_ANALYSIS_FINISHED: '图片语义提取完成',
  IMAGE_ANALYSIS_ABORTED_NOT_APPROVED: '图片语义提取中止',
  IMAGE_ANALYSIS_COMPLETED: '图片分析完成',
  IMAGE_ANALYSIS_FAILED: '图片分析失败',
  IMAGE_DIMENSION_UNAVAILABLE: '图片语义不可用',
  ADMIN_DIMENSION_RETRY_REQUESTED: '人工触发维度重试',
  ADMIN_DIMENSION_SKIPPED: '人工标记维度跳过',
  ADMIN_RETRY_REJECTED: '重试请求被拒绝',
}
const semanticErrorLabels = {
  DIMENSION_SOURCE_EMPTY: '维度材料为空：图片语义分析没有产出可向量化文本',
  NOTE_ROW_UNRESOLVED: '笔记来源未绑定，无法重试',
  POOL_NOTE_NOT_APPROVED: '样本未通过审核，任务已中止',
  IMAGE_MEDIA_EMPTY: '没有可分析的图片素材',
  IMAGE_OUTPUT_EMPTY: '图片语义分析没有返回可检索文本',
  IMAGE_FAILED: '图片语义分析失败',
  EMBEDDING_IDENTITY_CHANGED: '向量模型配置已变化，需要重新生成',
}
const emptyKeywordForm = {
  name: '',
  keyword: '',
  keywordItems: [],
  taskType: 'keyword',
  status: 'draft',
  noteNum: 20,
  searchType: 'image',
  sortRule: 'composite',
  publishTime: 'all',
  minLikes: 0,
  minComments: 0,
  minCollects: 0,
  minShares: 0,
  defaultPoolId: 0,
  copiedFromId: 0,
}

function getHashView() {
  const view = window.location.hash.replace(/^#\/?/, '').split('?')[0]
  if (view === 'keywords') return 'collectionTasks'
  if (['topicConfig', 'topic-config', 'tasks'].includes(view)) return 'pool'
  return pageMeta[view] ? view : 'dashboard'
}

function getNavView(view) {
  if (view === 'noteDetail') return 'notes'
  if (['topic-config', 'tasks'].includes(view)) return 'pool'
  return view
}

function getHashParams() {
  const [, query = ''] = window.location.hash.split('?')
  return new URLSearchParams(query)
}

function emitHashChange() {
  try {
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  } catch {
    window.dispatchEvent(new Event('hashchange'))
  }
}

function normalizeHashTarget(hash) {
  const value = String(hash || '').trim()
  if (!value) return '#notes'
  return value.startsWith('#') ? value : `#${value}`
}

function navigate(view, params = {}) {
  const search = new URLSearchParams(params)
  window.location.hash = search.toString() ? `${view}?${search.toString()}` : view
  emitHashChange()
}

function currentExtensionEnv() {
  const host = window.location.hostname
  if (host === 'tuber.lumeseed.com') {
    return {
      key: 'prod',
      label: '生产',
      apiBaseUrl: 'https://tuber.lumeseed.com/lume-tuber/api',
      downloadUrl: '/downloads/extensions/lume-tuber-assistant-prod-latest.zip',
    }
  }
  if (host === 'tuber-test.elingoai.com') {
    return {
      key: 'test',
      label: '测试',
      apiBaseUrl: 'https://tuber-test.elingoai.com/lume-tuber/api',
      downloadUrl: '/downloads/extensions/lume-tuber-assistant-test-latest.zip',
    }
  }
  return {
    key: 'local',
    label: '本地',
    apiBaseUrl: BASE_URL,
    downloadUrl: '',
  }
}

function contentScrollTop() {
  return document.querySelector('.content')?.scrollTop || 0
}

function openNoteDetail(note = {}, context = {}) {
  const noteId = note.note_id || note.noteId || ''
  const key = noteKey(note)
  try {
    sessionStorage.setItem('lume-note-detail-return', JSON.stringify({
      hash: window.location.hash || '#notes',
      scrollTop: contentScrollTop(),
    }))
    sessionStorage.removeItem(NOTE_DETAIL_NAV_KEY)
    sessionStorage.setItem('lume-note-detail-note', JSON.stringify(note))
    if (Array.isArray(context.notes) && context.notes.length) {
      sessionStorage.setItem(NOTE_DETAIL_NAV_KEY, JSON.stringify(buildNoteDetailNavigation(context.notes, note)))
    }
  } catch {
    // Session storage is a navigation hint only; detail route can still render from route params.
  }
  navigate('noteDetail', { noteKey: key, noteId })
}

function readStoredNoteDetail() {
  try {
    return JSON.parse(sessionStorage.getItem('lume-note-detail-note') || 'null')
  } catch {
    return null
  }
}

function toNavigationNote(note, index = 0) {
  const normalized = normalizeNote(note, index)
  const key = noteKey(normalized)
  return {
    key,
    noteKey: key,
    id: normalized.id,
    noteUrlId: normalized.noteUrlId,
    note_id: normalized.note_id,
    noteId: normalized.note_id,
    title: normalized.title,
    author: normalized.author,
    authorCode: normalized.authorCode,
    detailUrl: normalized.detailUrl,
    coverImage: normalized.coverImage,
    media: note.media || normalized.media,
    likesNum: normalized.likesNum,
    commentsNum: normalized.commentsNum,
    collectsNum: normalized.collectsNum,
    sharesNum: normalized.sharesNum,
    collectionTaskId: normalized.collectionTaskId,
    collectionTaskName: normalized.collectionTaskName,
    keyword: normalized.keyword,
    description: normalized.description,
    isVideo: normalized.isVideo,
    createdTime: normalized.createdTime,
    collectedTime: normalized.collectedTime,
    publishTime: normalized.publishTime,
    publishTs: normalized.publishTs,
    tagIds: Array.isArray(note.tagIds) ? note.tagIds : [],
    tags: Array.isArray(note.tags) ? note.tags : [],
    poolId: Number(note.poolId || 0),
    poolStatus: note.poolStatus || '',
    quality: Number(note.quality || note.qualityScore || normalized.qualityScore || autoScore(normalized)),
    rowIndex: index,
  }
}

function buildNoteDetailNavigation(notes, currentNote) {
  const items = (notes || []).map(toNavigationNote).filter(item => item.key)
  const currentKey = noteKey(currentNote)
  const currentNoteId = String(currentNote.note_id || currentNote.noteId || '').trim()
  const currentIndex = findNavigationIndex(items, currentKey, currentNoteId, currentNote)
  return {
    version: 1,
    sourceHash: window.location.hash || '#notes',
    createdAt: Date.now(),
    currentKey,
    currentNoteId,
    currentIndex,
    items,
  }
}

function readStoredNoteNavigation() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(NOTE_DETAIL_NAV_KEY) || 'null')
    if (!parsed || !Array.isArray(parsed.items)) return null
    return {
      ...parsed,
      items: parsed.items.filter(item => item && (item.key || item.noteKey || item.note_id || item.noteId)),
    }
  } catch {
    return null
  }
}

function findNavigationIndex(items, routeKey, routeNoteId, note) {
  const key = String(routeKey || '').trim()
  const id = String(routeNoteId || '').trim()
  const noteDerivedKey = note ? noteKey(note) : ''
  const noteId = String(note?.note_id || note?.noteId || '').trim()
  const byKey = key
    ? items.findIndex(item => String(item.key || item.noteKey || noteKey(item)).trim() === key)
    : -1
  if (byKey >= 0) return byKey
  const byNoteKey = noteDerivedKey
    ? items.findIndex(item => String(item.key || item.noteKey || noteKey(item)).trim() === noteDerivedKey)
    : -1
  if (byNoteKey >= 0) return byNoteKey
  const byRouteId = id
    ? items.findIndex(item => String(item.note_id || item.noteId || '').trim() === id)
    : -1
  if (byRouteId >= 0) return byRouteId
  return noteId
    ? items.findIndex(item => String(item.note_id || item.noteId || '').trim() === noteId)
    : -1
}

function findStoredNavigationNote(routeKey, routeNoteId) {
  const navigation = readStoredNoteNavigation()
  const items = navigation?.items || []
  const index = findNavigationIndex(items, routeKey, routeNoteId)
  return index >= 0 ? items[index] : null
}

function resolveNoteNavigation(note, routeKey, routeNoteId) {
  const navigation = readStoredNoteNavigation()
  const items = navigation?.items || []
  const index = findNavigationIndex(items, routeKey, routeNoteId, note)
  return {
    index,
    total: items.length,
    prev: index > 0 ? items[index - 1] : null,
    next: index >= 0 && index < items.length - 1 ? items[index + 1] : null,
  }
}

function backFromNoteDetail() {
  let stored = null
  try {
    stored = JSON.parse(sessionStorage.getItem('lume-note-detail-return') || 'null')
  } catch {
    stored = null
  }
  const targetHash = normalizeHashTarget(stored?.hash && !String(stored.hash).includes('noteDetail') ? stored.hash : '#notes')
  window.location.hash = targetHash
  emitHashChange()
  setTimeout(() => {
    const scroller = document.querySelector('.content')
    if (scroller) scroller.scrollTop = Number(stored.scrollTop || 0)
  }, 350)
}

function poolStatusLabel(status) {
  return poolStatusLabels[status] || '已入池'
}

function normalizePoolReviewStatus(status) {
  if (status === 'pooled') return 'review'
  if (status === 'used') return 'archived'
  if (status === 'reject') return 'rejected'
  return status || ''
}

function semanticSearchSignature({ poolId, query, topN, pageSize: semanticPageSize, tagIds = [], tagMatch }) {
  return JSON.stringify({
    poolId: Number(poolId || 0),
    query: String(query || '').trim(),
    topN: Number(topN || 0),
    pageSize: Number(semanticPageSize || 0),
    tagIds: (tagIds || []).map(Number).filter(Boolean),
    tagMatch,
  })
}

function readSemanticSearchState(poolId) {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SEMANTIC_SEARCH_STATE_KEY) || 'null')
    if (!parsed || Number(parsed.poolId || 0) !== Number(poolId || 0)) return null
    return parsed
  } catch {
    return null
  }
}

function isRestorableSemanticSearchState(state) {
  return Boolean(state?.result && Array.isArray(state.result.data))
}

function writeSemanticSearchState(poolId, state) {
  try {
    sessionStorage.setItem(SEMANTIC_SEARCH_STATE_KEY, JSON.stringify({
      ...state,
      poolId: Number(poolId || 0),
      savedAt: Date.now(),
    }))
  } catch {
    // Best-effort navigation cache; the query can still be rerun manually.
  }
}

function clearSemanticSearchState(poolId) {
  try {
    const current = readSemanticSearchState(poolId)
    if (current || !poolId) sessionStorage.removeItem(SEMANTIC_SEARCH_STATE_KEY)
  } catch {}
}

function poolLifecycleLabel(status) {
  return poolLifecycleLabels[status] || poolLifecycleLabels.active
}

function normalizePoolFilter(status) {
  const value = String(status || '').trim()
  return ['all', 'review', 'approved', 'rejected', 'archived'].includes(value) ? value : 'review'
}

function normalizePoolWorkspace(tab) {
  const value = String(tab || '').trim()
  return ['review', 'search', 'index'].includes(value) ? value : 'review'
}

function isPoolActive(pool) {
  if (typeof pool?.status === 'string') return pool.status === 'active'
  return Number(pool?.status ?? 1) === 1
}

function taskStatusLabel(status) {
  return taskStatusOptions.find(item => item.value === status)?.label || (Number(status) === 1 ? '启用' : '暂停')
}

function taskStatusTone(status) {
  if (status === 'active' || Number(status) === 1) return 'ok'
  if (status === 'completed') return 'pool'
  return 'wait'
}

function taskStatusHint(status) {
  return {
    draft: '待完善配置',
    active: '插件会自动读取执行',
    paused: '已暂停执行',
    completed: '已完成，启用后可重新采集',
    archived: '已归档，列表保留可查',
  }[status] || '等待处理'
}

function formatDate(value) {
  if (!value) return ''
  const raw = String(value)
  const time = raw.length === 10 ? Number(raw) * 1000 : Number(raw)
  const date = Number.isFinite(time) ? new Date(time) : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('zh-CN', { hour12: false })
}

function formatShortDate(value) {
  if (!value) return ''
  const raw = String(value)
  const time = raw.length === 10 ? Number(raw) * 1000 : Number(raw)
  const date = Number.isFinite(time) ? new Date(time) : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (num) => String(num).padStart(2, '0')
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function compactNumber(value) {
  const n = Number(value || 0)
  if (n >= 100000000) return `${(n / 100000000).toFixed(1)}亿`
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`
  return String(n)
}

function compactNoteLink(value) {
  const text = String(value || '').trim()
  const match = text.match(/\/(?:explore|discovery\/item|search_result)\/([a-zA-Z0-9]+)/)
  return match ? `笔记 ${match[1].slice(0, 8)}…` : text.replace(/^https?:\/\//, '').slice(0, 28)
}

function isXhsNoteLink(value) {
  try {
    const url = new URL(String(value || '').trim())
    return /(^|\.)xiaohongshu\.com$/i.test(url.hostname)
      && /\/(?:explore|discovery\/item|search_result)\/[a-zA-Z0-9]+/.test(url.pathname)
  } catch {
    return false
  }
}

function collectionContentItems(task, fallback = '') {
  if (!task) return fallback ? [fallback] : []
  const taskType = task.taskType || 'keyword'
  const items = ['keyword', 'note'].includes(taskType) && Array.isArray(task.keywordItems) && task.keywordItems.length
    ? task.keywordItems
    : [task.keyword || fallback]
  return Array.from(new Set(items.map(item => String(item || '').trim()).filter(Boolean)))
}

function collectionContentLabel(task, fallback = '') {
  const items = collectionContentItems(task, fallback)
  if (!items.length) return '未设置采集内容'
  const first = task?.taskType === 'note' ? compactNoteLink(items[0]) : items[0]
  return items.length > 1 ? `${first} +${items.length - 1}` : first
}

function CollectionContentTokens({ task, fallback = '', limit = 3, compact = false }) {
  const taskType = task?.taskType || 'keyword'
  const items = collectionContentItems(task, fallback)
  if (!items.length) return <span className="muted">-</span>
  return (
    <div className={`keyword-token-list ${taskType === 'note' ? 'note-link-list' : ''} ${compact ? 'compact-token-list' : ''}`}>
      {items.slice(0, limit).map(item => {
        const invalidNoteLink = taskType === 'note' && !isXhsNoteLink(item)
        const text = invalidNoteLink ? '无效链接' : taskType === 'note' ? compactNoteLink(item) : item
        return <span className={`keyword-token ${taskType === 'note' ? 'link-token' : ''} ${invalidNoteLink ? 'invalid' : ''}`} title={item} key={item}>{text}</span>
      })}
      {items.length > limit ? <span className="keyword-token more-token">+{items.length - limit}</span> : null}
    </div>
  )
}

function parseCount(value) {
  if (value == null) return 0
  if (typeof value === 'number') return value
  const text = String(value).trim().toLowerCase()
  const num = parseFloat(text.replace(/[^\d.]/g, '')) || 0
  if (text.includes('w') || text.includes('万')) return Math.round(num * 10000)
  if (text.includes('k')) return Math.round(num * 1000)
  return Math.round(num)
}

function noteKey(note) {
  const noteId = String(note.note_id || note.noteId || '').trim()
  const taskId = Number(note.collectionTaskId || note.taskId || 0)
  if (taskId && noteId) return `${taskId}:${noteId}`
  return String(noteId || note.id || note.detailUrl || note.link || note.title)
}

function collectionTaskIdFromNoteKey(value) {
  const match = String(value || '').trim().match(/^(\d+):/)
  return Number(match?.[1] || 0)
}

function positiveInteger(value) {
  const parsed = Number(value || 0)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0
}

function requireNoteUrlId(note) {
  const noteUrlId = positiveInteger(note?.noteUrlId)
  if (!noteUrlId) {
    throw new Error('笔记缺少有效的采集快照 ID，请刷新后重试')
  }
  return noteUrlId
}

function indexNoteMeta(rows) {
  const map = {}
  rows.forEach(row => {
    const key = String(row.noteKey || row.note_key || row.note_id || '').trim()
    if (!key) return
    const membership = {
      poolId: Number(row.poolId || 0),
      poolStatus: normalizePoolReviewStatus(row.status || row.poolStatus || ''),
    }
    const add = targetKey => {
      const current = map[targetKey] || { poolId: 0, poolStatus: '', poolMemberships: [] }
      const poolMemberships = current.poolMemberships.some(item => item.poolId === membership.poolId)
        ? current.poolMemberships
        : [...current.poolMemberships, membership]
      map[targetKey] = { ...poolMemberships[0], poolMemberships }
    }
    add(key)
    if (row.note_id) add(String(row.note_id))
  })
  return map
}

function indexNoteTags(rows) {
  const map = {}
  rows.forEach(row => {
    const key = String(row.noteKey || row.note_key || row.note_id || '').trim()
    const tagId = Number(row.tagId || 0)
    if (!key || !tagId) return
    map[key] = Array.from(new Set([...(map[key] || []), tagId]))
    if (row.note_id) {
      const noteIdKey = String(row.note_id)
      map[noteIdKey] = Array.from(new Set([...(map[noteIdKey] || []), tagId]))
    }
  })
  return map
}

function countNoteTagUsage(rows) {
  const usage = new Map()
  rows.forEach(row => {
    const tagId = Number(row.tagId || 0)
    const noteKey = String(row.noteKey || row.note_key || row.note_id || '').trim()
    if (!tagId || !noteKey) return
    const bucket = usage.get(tagId) || new Set()
    bucket.add(noteKey)
    usage.set(tagId, bucket)
  })
  return new Map(Array.from(usage.entries()).map(([tagId, noteKeys]) => [tagId, noteKeys.size]))
}

function buildTagGroups(groups, tags) {
  const groupMap = new Map(groups.map(group => [Number(group.id), { ...group, tags: [] }]))
  tags.forEach(tag => {
    const group = groupMap.get(Number(tag.groupId))
    if (group) group.tags.push(tag)
  })
  return Array.from(groupMap.values()).filter(group => Number(group.status ?? 1) === 1)
}

function normalizeNote(note, index = 0) {
  const link = note.detailUrl || note.link || note.url || ''
  const noteId = note.note_id || note.noteId || link.match(/explore\/([a-zA-Z0-9]+)/)?.[1] || ''
  const taskId = Number(note.collectionTaskId || note.taskId || 0)
  const id = note.id || (taskId && noteId ? `${taskId}:${noteId}` : noteId) || `note-${index}`
  const media = safeJson(note.mediaContent)
  const normalizedMedia = normalizeMediaContent(media, note.coverImage)
  const collectedTime = note.createdTime || note.updateTime || ''
  const publishTime = note.publishTime || note.publish_time || note.xhsPublishTime || note.xhs_publish_time || note.last_update_time || note.lastUpdateTime || note.timestamp || collectedTime
  return {
    ...note,
    id,
    noteUrlId: positiveInteger(note.noteUrlId || note.note_url_id),
    note_id: noteId,
    title: note.title || '未命名笔记',
    author: note.author || '未知作者',
    authorCode: note.authorCode || '',
    detailUrl: link,
    coverImage: normalizedMedia.cover,
    media: normalizedMedia,
    likesNum: Number(note.likesNum ?? note.likesN ?? parseCount(note.likes)),
    commentsNum: Number(note.commentsNum ?? note.commentNum ?? parseCount(note.comments)),
    collectsNum: Number(note.collectsNum ?? note.collectNum ?? note.favoritesNum ?? note.favoriteNum ?? parseCount(note.collects || note.favorites)),
    sharesNum: Number(note.sharesNum ?? note.shareNum ?? note.share_count ?? note.shareCount ?? parseCount(note.shares || note.share)),
    qualityScore: Number(note.qualityScore ?? note.quality_score ?? note.quality ?? 0),
    collectionTaskId: taskId,
    collectionTaskName: note.collectionTaskName || note.taskName || '',
    keyword: note.keyword || note.kw || note.searchKeyword || '',
    description: note.description || '',
    isVideo: Boolean(note.isVideo || note.type === 'video'),
    createdTime: collectedTime,
    collectedTime,
    collectedTs: toTimestamp(note.updateTime || note.createdTime || collectedTime),
    publishTime,
    publishTs: toTimestamp(publishTime),
  }
}

function toTimestamp(value) {
  if (!value) return 0
  if (value instanceof Date) return value.getTime()
  const text = String(value).trim()
  const numeric = Number(text)
  if (Number.isFinite(numeric) && numeric <= 0) return 0
  if (Number.isFinite(numeric) && numeric > 0) return text.length === 10 ? numeric * 1000 : numeric
  const direct = new Date(text)
  if (!Number.isNaN(direct.getTime())) return direct.getTime()
  const normalized = text
    .replace(/年|\.|\//g, '-')
    .replace(/月/g, '-')
    .replace(/日/g, '')
  const parsed = new Date(normalized)
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime()
}

function safeJson(value) {
  if (!value || typeof value !== 'string') return value || null
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function uniqueList(rows) {
  return Array.from(new Set((rows || []).map(item => String(item || '').trim()).filter(Boolean)))
}

function normalizeMediaContent(media, fallbackCover = '') {
  if (typeof media === 'string') media = safeJson(media)
  const fromItems = Array.isArray(media?.items)
    ? media.items.filter(item => !['cover', 'video'].includes(String(item?.type || ''))).map(item => item?.url || item?.src).filter(Boolean)
    : []
  const arrayItems = Array.isArray(media) ? media.map(item => typeof item === 'string' ? item : item?.url || item?.src).filter(Boolean) : []
  const images = uniqueList([
    ...(Array.isArray(media?.images) ? media.images : []),
    ...(Array.isArray(media?.imageList) ? media.imageList : []),
    ...fromItems,
    ...arrayItems,
  ])
  const videos = uniqueList([
    ...(Array.isArray(media?.videos) ? media.videos : []),
    ...(Array.isArray(media) ? media.filter(item => item?.type === 'video').map(item => item.url) : []),
  ])
  const cover = media?.cover || fallbackCover || images[0] || ''
  const extensionImages = uniqueList(images)
  return {
    cover,
    images: extensionImages,
    extensionImages,
    videos,
    items: [
      ...(cover ? [{ type: 'cover', url: cover }] : []),
      ...extensionImages.map(url => ({ type: 'image', url })),
      ...videos.map(url => ({ type: 'video', url })),
    ],
  }
}

function notePrimaryImage(note) {
  if (!note) return ''
  if (Array.isArray(note.media?.images) && note.media.images[0]) return note.media.images[0]
  if (note.coverImage) return note.coverImage
  const media = normalizeMediaContent(note.mediaContent, note.coverImage)
  return media.images?.[0] || media.cover || ''
}

function loadLocal() {
  try {
    return JSON.parse(localStorage.getItem('lume-admin-state') || '{}')
  } catch {
    return {}
  }
}

function saveLocal(value) {
  try {
    localStorage.setItem('lume-admin-state', JSON.stringify(value))
  } catch {
    // Local storage is optional in tests and restricted browser contexts.
  }
}

function App() {
  return <AdminApp />
}

function AdminApp({ onLogout = () => {} }) {
  const [view, setView] = useState(getHashView)
  const [query, setQuery] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [navOpen, setNavOpen] = useState(false)
  const [toast, setToast] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [notes, setNotes] = useState([])
  const [collectionTasks, setCollectionTasks] = useState([])
  const [authors, setAuthors] = useState([])
  const [tasks, setTasks] = useState([])
  const [comments, setComments] = useState([])
  const [analysis, setAnalysis] = useState([])
  const [contentPools, setContentPools] = useState([])
  const [tagGroups, setTagGroups] = useState([])
  const [tags, setTags] = useState([])
  const [noteTags, setNoteTags] = useState({})
  const [noteTagRows, setNoteTagRows] = useState([])
  const [contentPoolNotes, setContentPoolNotes] = useState({})
  const isMobile = useMediaQuery('(max-width: 760px)')
  const mobileNavButtonRef = useRef(null)
  const sidebarRef = useRef(null)
  const [localState, setLocalState] = useState(() => ({
    settings: {
      downstream: '',
      apiBase: BASE_URL,
    },
    ...loadLocal(),
  }))

  async function logout() {
    setMenuOpen(false)
    try {
      await api.post('/auth/logout')
      onLogout()
    } catch (e) {
      flash(e.response?.data?.message || '退出登录失败，请稍后重试')
    }
  }

  useEffect(() => {
    const onHash = () => {
      setView(getHashView())
      setNavOpen(false)
    }
    window.addEventListener('hashchange', onHash)
    const timer = window.setInterval(() => {
      const nextView = getHashView()
      setView(current => current === nextView ? current : nextView)
    }, 250)
    onHash()
    return () => {
      window.removeEventListener('hashchange', onHash)
      window.clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    if (!isMobile || !navOpen) return undefined
    const drawer = sidebarRef.current
    const focusable = () => Array.from(drawer?.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), [tabindex]:not([tabindex="-1"])') || [])
    const focusTimer = window.requestAnimationFrame(() => focusable()[0]?.focus())
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setNavOpen(false)
        return
      }
      if (event.key !== 'Tab') return
      const items = focusable()
      if (!items.length) return
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.body.classList.add('mobile-nav-open')
    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.cancelAnimationFrame(focusTimer)
      document.body.classList.remove('mobile-nav-open')
      document.removeEventListener('keydown', onKeyDown)
      mobileNavButtonRef.current?.focus()
    }
  }, [isMobile, navOpen])

  useEffect(() => {
    saveLocal(localState)
  }, [localState])

  useEffect(() => {
    reload()
  }, [])

  function flash(message) {
    setToast(message)
    window.clearTimeout(flash.timer)
    flash.timer = window.setTimeout(() => setToast(''), 2200)
  }

  async function reload() {
    setLoading(true)
    setError('')
    try {
      const [noteRes, keywordRes, authorRes, taskRes, commentRes, analysisRes, poolNoteRes, poolRes, tagGroupRes, tagRes, noteTagRes] = await Promise.allSettled([
        api.get('/urls/note-url-list', { params: { page: 1, pageSize } }),
        api.get('/keyword/list'),
        api.get('/author/list', { params: { page: 1, pageSize } }),
        api.get('/task/list', { params: { page: 1, pageSize } }),
        api.get('/comments/list', { params: { page: 1, pageSize } }),
        api.get('/urls/analysis-list'),
        api.get('/urls/content-pool-note-list'),
        api.get('/content-learning/pools'),
        api.get('/urls/tag-group-list'),
        api.get('/urls/tag-list'),
        api.get('/urls/note-tag-list'),
      ])

      const noteRows = pickRows(noteRes)
      const analysisRows = pickRows(analysisRes)
      const analysisNotes = analysisRows.flatMap(record => Array.isArray(record.data) ? record.data : [])
      const merged = mergeNotes([...noteRows, ...analysisNotes])

      setNotes(merged)
      setCollectionTasks(pickRows(keywordRes))
      setAuthors(pickRows(authorRes))
      setTasks(pickRows(taskRes))
      setComments(pickRows(commentRes))
      setAnalysis(analysisRows)
      setContentPoolNotes(indexNoteMeta(pickRows(poolNoteRes)))
      setContentPools(pickRows(poolRes))
      setTagGroups(buildTagGroups(pickRows(tagGroupRes), pickRows(tagRes)))
      setTags(pickRows(tagRes))
      setNoteTagRows(pickRows(noteTagRes))
      setNoteTags(indexNoteTags(pickRows(noteTagRes)))
    } catch (e) {
      setError(e.message || '数据加载失败')
    } finally {
      setLoading(false)
    }
  }

  function updateLocal(updater) {
    setLocalState(current => {
      const next = typeof updater === 'function' ? updater(current) : updater
      return { ...current, ...next }
    })
  }

  async function updateContentPoolNote(targetNotes, patch) {
    const updates = targetNotes.map(note => {
      const key = noteKey(note)
      const current = contentPoolNotes[key] || {}
      const next = { ...current, ...patch }
      const noteUrlId = requireNoteUrlId(note)
      return {
        noteKey: key,
        note_id: note.note_id || note.noteId || '',
        noteUrlId,
        poolId: Number(next.poolId || 0),
        status: normalizePoolReviewStatus(next.poolStatus || next.status || ''),
      }
    })
    setContentPoolNotes(current => {
      const next = { ...current }
      updates.forEach(item => {
        const previous = next[item.noteKey] || (item.note_id ? next[item.note_id] : null) || { poolMemberships: [] }
        const membership = { poolId: item.poolId, poolStatus: normalizePoolReviewStatus(item.status) }
        const poolMemberships = [...(previous.poolMemberships || []).filter(entry => Number(entry.poolId) !== Number(item.poolId)), membership]
        const meta = { ...membership, poolMemberships }
        next[item.noteKey] = meta
        if (item.note_id) next[item.note_id] = meta
      })
      return next
    })
    await api.post('/urls/content-pool-note-upsert', { items: updates })
  }

  async function replaceNoteTags(targetNotes, tagIds) {
    const normalizedTagIds = Array.from(new Set((tagIds || []).map(tagId => Number(tagId || 0)).filter(Boolean)))
    const items = targetNotes.map(note => ({
      noteKey: noteKey(note),
      note_id: note.note_id || note.noteId || '',
      tagIds: normalizedTagIds,
    }))
    setNoteTags(current => {
      const next = { ...current }
      items.forEach(item => {
        next[item.noteKey] = normalizedTagIds
        if (item.note_id) next[item.note_id] = normalizedTagIds
      })
      return next
    })
    await api.post('/urls/note-tags-replace', { items })
  }

  const enrichedNotes = useMemo(() => {
    const keywordPoolMap = new Map(collectionTasks.map(keyword => [String(keyword.name || ''), Number(keyword.defaultPoolId || 0)]))
    const tagMap = new Map(tags.map(tag => [Number(tag.id), tag]))
    return notes.map((note, index) => {
      const key = noteKey(note)
      const meta = contentPoolNotes[key] || {}
      const tagIds = noteTags[key] || []
      const tagNames = tagIds.map(tagId => tagMap.get(Number(tagId))?.name).filter(Boolean)
      const poolId = Number(meta.poolId || 0)
      const poolStatus = meta.poolStatus || ''
      const poolMemberships = meta.poolMemberships || note.poolMemberships || []
      return {
        ...note,
        rowIndex: index,
        tagIds,
        tags: tagNames,
        poolId,
        poolStatus,
        poolMemberships,
        defaultPoolId: keywordPoolMap.get(String(note.keyword || '')) || 0,
        quality: Number(note.qualityScore || note.quality || autoScore(note)),
        status: tagIds.length ? 'tagged' : 'untagged',
      }
    })
  }, [notes, contentPoolNotes, noteTags, tags, collectionTasks])

  const searchedNotes = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return enrichedNotes
    return enrichedNotes.filter(note => [note.title, note.author, note.keyword, note.description, ...(note.tags || [])]
      .join(' ')
      .toLowerCase()
      .includes(q))
  }, [enrichedNotes, query])

  const counts = useMemo(() => ({
    notes: enrichedNotes.length,
    pool: contentPools.reduce((total, pool) => total + Number(pool.reviewCount || 0), 0),
  }), [enrichedNotes, contentPools])

  const [baseTitle, desc] = pageMeta[view]
  const title = baseTitle
  const navView = getNavView(view)
  const showGlobalSearch = !['pool', 'noteDetail', 'collectionTasks', 'prompts'].includes(view)
  const showTopRefresh = !['pool', 'noteDetail', 'prompts'].includes(view)

  return (
    <div className="admin-app">
      <aside
        className={`sidebar ${navOpen ? 'open' : ''}`}
        id="admin-navigation"
        aria-label="主导航"
        aria-hidden={isMobile && !navOpen}
        aria-modal={isMobile && navOpen ? true : undefined}
        inert={isMobile && !navOpen}
        ref={sidebarRef}
        role={isMobile && navOpen ? 'dialog' : undefined}
      >
        <div className="side-brand">
          <div className="brand-mark">LT</div>
          <div>
            <div className="brand-name">Lume Tuber</div>
            <div className="brand-sub">运营控制台</div>
          </div>
          <button className="mobile-nav-close" type="button" aria-label="关闭导航" onClick={() => setNavOpen(false)}>×</button>
        </div>
        <nav className="side-nav">
          {navGroups.map(group => (
            <div className="nav-group" key={group.title}>
              <div className="nav-label">{group.title}</div>
              {group.items.map(item => (
                <button
                  className={`nav-item ${navView === item.view ? 'active' : ''}`}
                  type="button"
                  key={item.view}
                  onClick={() => { setNavOpen(false); navigate(item.view) }}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span>{item.label}</span>
                  {item.badge ? (
                    <span
                      className={`nav-badge ${item.badge === 'pool' ? 'accent' : ''}`}
                      title={item.badge === 'pool' ? '全部内容池待审核数' : undefined}
                    >
                      {compactNumber(counts[item.badge])}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="side-foot">
          <div className="side-api"><span className="dot dot-ok" />{BASE_URL.replace(/^https?:\/\//, '')}</div>
          <div className={`account ${menuOpen ? 'open' : ''}`}>
            <button className="account-btn" type="button" onClick={() => setMenuOpen(open => !open)}>
              <span className="user-ava">A</span>
              <span className="account-meta"><b>admin</b><em>超级管理员</em></span>
              <span className="account-caret">⌃</span>
            </button>
            {menuOpen ? (
              <div className="account-menu">
                <div className="account-card"><span className="user-ava lg">A</span><div><b>admin</b><em>admin@lume-tuber.com</em></div></div>
                <button type="button" onClick={() => { setMenuOpen(false); navigate('settings') }}>设置</button>
                <button type="button" onClick={() => flash('已刷新服务端数据')}>同步状态</button>
                <button type="button" className="danger" onClick={logout}>退出登录</button>
              </div>
            ) : null}
          </div>
        </div>
      </aside>
      {isMobile && navOpen ? <button className="mobile-nav-backdrop" type="button" aria-label="关闭导航" onClick={() => setNavOpen(false)} /> : null}

      <main className="main" inert={isMobile && navOpen}>
        <header className="topbar">
          <div className="topbar-main">
            <button
              className="mobile-nav-trigger"
              type="button"
              aria-label="打开主导航"
              aria-controls="admin-navigation"
              aria-expanded={navOpen}
              onClick={() => setNavOpen(true)}
              ref={mobileNavButtonRef}
            >☰</button>
            <div className="topbar-title">
              <h1>{title}</h1>
              <p>{desc}</p>
            </div>
          </div>
          <div className="top-actions">
            {showGlobalSearch ? (
              <label className="searchbox">
                <span>⌕</span>
                <input value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索笔记、作者、关键词…" />
              </label>
            ) : null}
            {showTopRefresh ? <button className="btn ghost" type="button" onClick={reload}>{loading ? '同步中' : '刷新'}</button> : null}
          </div>
        </header>

        <div className="content">
          {error ? <div className="banner danger">{error}</div> : null}
          {view === 'dashboard' ? <Dashboard notes={enrichedNotes} collectionTasks={collectionTasks} comments={comments} tasks={tasks} analysis={analysis} onJump={navigate} /> : null}
          {view === 'notes' ? <NotesPage searchQuery={query} collectionTasks={collectionTasks} tagGroups={tagGroups} contentPools={contentPools} reload={reload} replaceNoteTags={replaceNoteTags} updateContentPoolNote={updateContentPoolNote} flash={flash} /> : null}
          {view === 'noteDetail' ? <NoteDetailPage notes={enrichedNotes} tagGroups={tagGroups} contentPools={contentPools} reload={reload} replaceNoteTags={replaceNoteTags} updateContentPoolNote={updateContentPoolNote} flash={flash} /> : null}
          {view === 'pool' ? <PoolPage notes={searchedNotes} contentPools={contentPools} tagGroups={tagGroups} reload={reload} flash={flash} /> : null}
          {view === 'prompts' ? <PromptManagementPage apiClient={api} flash={flash} /> : null}
          {view === 'tags' ? <TagsPage noteTagRows={noteTagRows} tagGroups={tagGroups} reload={reload} flash={flash} /> : null}
          {view === 'collectionTasks' ? <KeywordsPage collectionTasks={collectionTasks} notes={enrichedNotes} contentPools={contentPools} reload={reload} flash={flash} onJump={navigate} /> : null}
          {view === 'authors' ? <AuthorsPage authors={authors} notes={enrichedNotes} /> : null}
          {view === 'extensions' ? <ExtensionPage flash={flash} /> : null}
          {view === 'settings' ? <SettingsPage localState={localState} updateLocal={updateLocal} reload={reload} flash={flash} /> : null}
        </div>
      </main>
      {toast ? <div className="toast show">{toast}</div> : null}
    </div>
  )
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia?.(query).matches || false)
  useEffect(() => {
    const media = window.matchMedia?.(query)
    if (!media) return undefined
    const onChange = (event) => setMatches(event.matches)
    setMatches(media.matches)
    media.addEventListener?.('change', onChange)
    return () => media.removeEventListener?.('change', onChange)
  }, [query])
  return matches
}

function pickRows(result) {
  if (result?.status === 'fulfilled') {
    const data = result.value.data
    if (Array.isArray(data)) return data
    if (Array.isArray(data?.data)) return data.data
    if (Array.isArray(data?.list)) return data.list
    if (Array.isArray(data?.comments)) return data.comments
    return []
  }
  if (result?.data) {
    const direct = result.data
    if (Array.isArray(direct)) return direct
    if (Array.isArray(direct?.data)) return direct.data
    if (Array.isArray(direct?.list)) return direct.list
    if (Array.isArray(direct?.comments)) return direct.comments
    return []
  }
  return []
}

function mergeNotes(rows) {
  const map = new Map()
  rows.map(normalizeNote).forEach(note => {
    const taskScope = Number(note.collectionTaskFromId || note.collectionTaskId || 0)
    const rawKey = note.note_id || note.detailUrl || note.title
    const key = taskScope && rawKey ? `${taskScope}:${rawKey}` : rawKey
    if (!key) return
    const current = map.get(key) || {}
    map.set(key, {
      ...current,
      ...note,
      id: current.id || note.id,
      noteUrlId: positiveInteger(current.noteUrlId || note.noteUrlId),
    })
  })
  return Array.from(map.values())
}

function autoScore(note) {
  const likes = Number(note.likesNum || 0)
  const comments = Number(note.commentsNum || 0)
  const collects = Number(note.collectsNum || 0)
  const title = String(note.title || '').trim()
  const description = String(note.description || '').trim()
  const mediaCount = mediaItemCount(note.mediaContent)
  const interactionScore =
    logScore(likes, 100000, 32) +
    logScore(comments, 5000, 16) +
    logScore(collects, 20000, 7)
  const titleScore = scoreTitle(title)
  const descriptionScore = scoreDescription(description)
  const mediaScore = note.isVideo || mediaCount > 0 ? 5 : 0
  return clamp(Math.round(18 + interactionScore + titleScore + descriptionScore + mediaScore), 15, 98)
}

function logScore(value, cap, maxScore) {
  const num = Number(value || 0)
  if (num <= 0) return 0
  return Math.min(1, Math.log10(num + 1) / Math.log10(cap + 1)) * maxScore
}

function scoreTitle(title) {
  const length = title.length
  if (!length) return 0
  if (length >= 8 && length <= 36) return 8
  if (length >= 4 && length <= 60) return 6
  return 3
}

function scoreDescription(description) {
  const length = description.length
  if (!length) return 0
  if (length < 20) return 4
  if (length < 80) return 8
  return 12
}

function mediaItemCount(mediaContent) {
  if (!mediaContent) return 0
  const media = typeof mediaContent === 'string' ? safeJson(mediaContent) : mediaContent
  if (Array.isArray(media)) return media.length
  if (Array.isArray(media?.images)) return media.images.length
  if (Array.isArray(media?.imageList)) return media.imageList.length
  return media ? 1 : 0
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

function suggestTagsForNote(note, tagGroups) {
  const availableTags = tagGroups.flatMap(group => group.tags || [])
  const text = [note.title, note.description, note.keyword].join(' ').toLowerCase()
  const picked = new Set()

  availableTags.forEach(tag => {
    const normalized = String(tag.name).toLowerCase()
    if (normalized && text.includes(normalized)) picked.add(tag.id)
  })

  const pickByName = (name) => {
    const tag = availableTags.find(item => item.name === name)
    if (tag) picked.add(tag.id)
  }

  const rules = [
    [/美白|淡斑|提亮|肤色|护肤|成分|敏感肌/, ['美白']],
    [/护肤|教程|步骤|流程|怎么|方法/, ['护肤教程', '教程']],
    [/成分|烟酰胺|a醇|维c|酸|配方/, ['成分党']],
    [/英语|英文|启蒙|自然拼读|raz|牛津|绘本/, ['英语启蒙']],
    [/亲子|共读|绘本|阅读/, ['亲子阅读']],
    [/学生|宿舍|平价|开学/, ['学生党']],
    [/妈妈|母婴|宝宝|孩子|育儿/, ['新手妈妈']],
    [/敏感|泛红|修护|屏障/, ['敏感肌']],
    [/0-3|0到3|婴幼儿|宝宝/, ['0-3岁']],
    [/3-6|3到6|幼儿园|学龄前/, ['3-6岁']],
    [/清单|合集|攻略|必备|闭眼入/, ['干货清单']],
    [/测评|实测|对比|横评|试用/, ['测评']],
    [/种草|推荐|好物|入手/, ['种草']],
    [/vlog|日常|记录/, ['vlog']],
  ]

  rules.forEach(([pattern, tags]) => {
    if (pattern.test(text)) tags.forEach(pickByName)
  })

  pickByName(note.isVideo ? 'vlog' : '干货清单')

  const score = Number(note.quality || autoScore(note))
  if (score >= 90 || Number(note.likesNum || 0) >= 100000) pickByName('爆款')
  else if (score >= 78 || Number(note.likesNum || 0) >= 30000) pickByName('优质')
  else if (score < 55) pickByName('低质')
  else pickByName('普通')

  return Array.from(picked)
}

function Dashboard({ notes, collectionTasks, comments, tasks, analysis, onJump }) {
  const tagged = notes.filter(note => note.tags.length).length
  const pooled = notes.filter(note => note.poolStatus).length
  const used = notes.filter(note => note.poolStatus === 'used').length
  const activeKeywords = collectionTasks.filter(keyword => keyword.status === 'active' || Number(keyword.status) === 1).length
  const trend = makeTrend(notes, comments)

  return (
    <>
      <div className="kpi-row">
        <Kpi icon="▦" value={compactNumber(notes.length)} label="笔记总数" trend={`+${todayCount(notes)} 今日`} />
        <Kpi icon="❤" value={compactNumber(comments.length)} label="评论总数" trend={`近 ${analysis.length} 次分析`} />
        <Kpi icon="⊙" value={compactNumber(notes.length - tagged)} label="待打标" trend="待处理" />
        <Kpi icon="◈" value={compactNumber(pooled)} label="内容池" trend={`已采用 ${used}`} />
      </div>

      <div className="grid-2">
        <Panel title="采集趋势" action={<Segment labels={['近 7 天', '近 30 天']} />}>
          <div className="chart">
            {trend.map(item => (
              <div className="chart-col" key={item.label}>
                <div className="chart-stack">
                  <i style={{ height: `${item.notePct}%` }} />
                  <b style={{ height: `${item.commentPct}%` }} />
                </div>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="内容运营漏斗">
          <div className="funnel">
            <Funnel label="已采集" value={notes.length} pct={100} />
            <Funnel label="已打标" value={tagged} pct={pct(tagged, notes.length)} />
            <Funnel label="已入池" value={pooled} pct={pct(pooled, notes.length)} />
            <Funnel label="已采用" value={used} pct={pct(used, notes.length)} />
          </div>
        </Panel>
      </div>

      <div className="grid-2">
        <Panel title="关键词采集进度" action={<button className="link" type="button" onClick={() => onJump('collectionTasks')}>查看全部</button>}>
          <div className="kw-progress">
            {collectionTasks.length ? collectionTasks.slice(0, 6).map(keyword => {
              const taskId = Number(keyword.id || 0)
              const searchWord = String(keyword.keyword || keyword.name || '').trim()
              const count = notes.filter(note => {
                if (taskId) return Number(note.collectionTaskId || 0) === taskId
                return searchWord && (note.keyword === searchWord || note.title.includes(searchWord))
              }).length
              return <ProgressRow key={keyword.id || keyword.name} name={keyword.name} count={count} target={Number(keyword.noteNum || 0)} />
            }) : <Empty text="暂无关键词" />}
          </div>
        </Panel>
        <Panel title="最近采集" action={<button className="link" type="button" onClick={() => onJump('notes')}>进入笔记库</button>}>
          <div className="recent-list">
            {notes.slice(0, 6).map((note, index) => <RecentNote note={note} key={noteKey(note)} index={index} />)}
            {!notes.length ? <Empty text="暂无笔记" /> : null}
          </div>
        </Panel>
      </div>

      <Panel title="运行任务概况">
        <div className="task-strip">
          <MiniStat label="活跃关键词" value={activeKeywords} />
          <MiniStat label="待推送任务" value={tasks.filter(task => Number(task.status) === 0).length} />
          <MiniStat label="处理中任务" value={tasks.filter(task => Number(task.status) === 1).length} />
          <MiniStat label="已完成任务" value={tasks.filter(task => Number(task.status) === 2).length} />
        </div>
      </Panel>
    </>
  )
}

function NotesPage({ searchQuery, collectionTasks, tagGroups, contentPools, reload, replaceNoteTags, updateContentPoolNote, flash }) {
  const hashParams = new URLSearchParams((window.location.hash.split('?')[1] || ''))
  const initialTaskId = hashParams.get('collectionTaskId') || ''
  const initialTagId = hashParams.get('tagId') || ''
  const [filters, setFilters] = useState({ keyword: '', collectionTaskId: initialTaskId, tagId: initialTagId, type: '', likes: '', tag: '', pool: '', publishTime: '', sort: 'composite' })
  const [notes, setNotes] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState('')
  const [selected, setSelected] = useState([])
  const [tagTarget, setTagTarget] = useState(null)
  const [poolTarget, setPoolTarget] = useState(null)
  const [poolId, setPoolId] = useState(0)
  const activePools = contentPools.length ? contentPools.filter(isPoolActive) : fallbackContentPools
  const collectionTaskById = useMemo(
    () => new Map(collectionTasks.map(task => [Number(task.id || 0), task])),
    [collectionTasks]
  )

  const loadNotes = useCallback(async ({ signal } = {}) => {
    setListLoading(true)
    setListError('')
    try {
      const res = await api.get('/urls/note-search', {
        signal,
        params: {
          page: 1,
          pageSize,
          q: searchQuery,
          keyword: filters.keyword,
          collectionTaskId: filters.collectionTaskId,
          tagId: filters.tagId,
          type: filters.type,
          minLikes: filters.likes,
          tagStatus: filters.tag,
          poolStatus: filters.pool,
          publishTime: filters.publishTime,
          sort: filters.sort,
        },
      })
      const rows = Array.isArray(res.data?.data) ? res.data.data : []
      const keywordPoolMap = new Map(collectionTasks.map(keyword => [String(keyword.name || ''), Number(keyword.defaultPoolId || 0)]))
      setNotes(rows.map((note, index) => ({
        ...normalizeNote(note, index),
        ...note,
        rowIndex: index,
        tagIds: note.tagIds || [],
        tags: note.tags || [],
        defaultPoolId: Number(note.defaultPoolId || keywordPoolMap.get(String(note.keyword || '')) || 0),
        quality: Number(note.quality || note.qualityScore || autoScore(note)),
        status: note.status || ((note.tagIds || []).length ? 'tagged' : 'untagged'),
      })))
      setTotalCount(Number(res.data?.totalCount || rows.length))
      setSelected([])
    } catch (e) {
      if (signal?.aborted) return
      setListError(e.message || '笔记筛选加载失败')
      setNotes([])
      setTotalCount(0)
    } finally {
      if (!signal?.aborted) setListLoading(false)
    }
  }, [searchQuery, filters, collectionTasks])

  useEffect(() => {
    const syncNoteFiltersFromHash = () => {
      if (getHashView() !== 'notes') return
      const params = getHashParams()
      const nextTaskId = params.get('collectionTaskId') || ''
      const nextTagId = params.get('tagId') || ''
      setFilters(current => (
        current.collectionTaskId === nextTaskId && current.tagId === nextTagId
          ? current
          : { ...current, collectionTaskId: nextTaskId, tagId: nextTagId }
      ))
    }
    syncNoteFiltersFromHash()
    window.addEventListener('hashchange', syncNoteFiltersFromHash)
    return () => window.removeEventListener('hashchange', syncNoteFiltersFromHash)
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    loadNotes({ signal: controller.signal })
    return () => controller.abort()
  }, [loadNotes])

  const list = notes

  function notesByKeys(keys) {
    return keys.map(key => list.find(note => noteKey(note) === key)).filter(Boolean)
  }

  function selectedKeys() {
    return selected.filter(key => list.some(note => noteKey(note) === key))
  }

  function openTagEditor(note) {
    const keys = note ? [noteKey(note)] : selectedKeys()
    if (!keys.length) {
      flash('请先选择要打标的笔记')
      return
    }
    const sourceNote = note || list.find(item => noteKey(item) === keys[0])
    setTagTarget({ keys, title: note ? sourceNote.title : `批量打标 ${keys.length} 篇`, initialTagIds: sourceNote?.tagIds || [] })
  }

  async function saveTags(tagIds) {
    if (!tagTarget?.keys?.length) return
    const targetNotes = notesByKeys(tagTarget.keys)
    try {
      await replaceNoteTags(targetNotes, tagIds)
      flash(tagTarget.keys.length > 1 ? `已为 ${tagTarget.keys.length} 篇笔记打标` : '已完成打标')
      setTagTarget(null)
      setSelected([])
      await loadNotes()
      reload()
    } catch (e) {
      flash(e.message || '打标保存失败')
    }
  }

  function poolNotes(keys) {
    if (!keys.length) {
      flash('请先选择要入池的笔记')
      return
    }
    const targetNotes = notesByKeys(keys)
    const availablePools = activePools.filter(pool => targetNotes.some(note => !(note.poolMemberships || []).some(item => Number(item.poolId) === Number(pool.id))))
    if (!availablePools.length) {
      flash('当前笔记已加入全部可用内容池')
      return
    }
    const preferredId = targetNotes.find(note => note.defaultPoolId)?.defaultPoolId
    const defaultId = availablePools.some(pool => Number(pool.id) === Number(preferredId)) ? preferredId : availablePools[0]?.id || 0
    setPoolId(Number(defaultId || 0))
    setPoolTarget({ keys: targetNotes.map(noteKey), title: targetNotes.length > 1 ? `批量入池 ${targetNotes.length} 篇` : '加入内容池' })
  }

  async function savePool() {
    if (!poolTarget?.keys?.length || !poolId) return
    const targetNotes = notesByKeys(poolTarget.keys).filter(note => !(note.poolMemberships || []).some(item => Number(item.poolId) === Number(poolId)))
    if (!targetNotes.length) {
      flash('所选笔记均已加入该内容池')
      return
    }
    try {
      await updateContentPoolNote(targetNotes, { poolId: Number(poolId), poolStatus: 'review' })
      flash(targetNotes.length > 1 ? `已将 ${targetNotes.length} 篇加入内容池` : '已加入内容池')
      setPoolTarget(null)
      setSelected([])
      await loadNotes()
      reload()
    } catch (e) {
      flash(e.message || '入池保存失败')
    }
  }

  async function addTagToGroup(groupId, groupName, tag) {
    let targetGroupId = Number(groupId)
    if (!targetGroupId) {
      const groupRes = await api.post('/urls/tag-group-create', { name: groupName, color: tagGroupColors[tagGroups.length % tagGroupColors.length] })
      targetGroupId = Number(groupRes.data?.id || 0)
    }
    await api.post('/urls/tag-create', { groupId: targetGroupId, name: tag })
    await reload()
  }

  const visibleSelected = selectedKeys()

  return (
    <>
      <div className="filterbar">
        <select value={filters.keyword} onChange={event => setFilters({ ...filters, keyword: event.target.value })}>
          <option value="">全部采集内容</option>
          {collectionTasks.map(task => <option value={task.keyword || task.name} key={task.id || task.name}>{collectionContentLabel(task)}</option>)}
        </select>
        <input
          className="task-id-input"
          value={filters.collectionTaskId}
          inputMode="numeric"
          onChange={event => setFilters({ ...filters, collectionTaskId: event.target.value.replace(/[^\d]/g, '') })}
          placeholder="任务ID"
        />
        <select value={filters.tagId} onChange={event => setFilters({ ...filters, tagId: event.target.value })}>
          <option value="">全部标签</option>
          {tagGroups.flatMap(group => (group.tags || []).map(tag => <option value={tag.id} key={tag.id}>{group.name} / {tag.name}</option>))}
        </select>
        <select value={filters.type} onChange={event => setFilters({ ...filters, type: event.target.value })}>
          <option value="">全部类型</option><option value="video">视频</option><option value="image">图文</option>
        </select>
        <select value={filters.likes} onChange={event => setFilters({ ...filters, likes: event.target.value })}>
          <option value="">点赞不限</option><option value="100000">10万+</option><option value="50000">5万+</option><option value="10000">1万+</option>
        </select>
        <select value={filters.tag} onChange={event => setFilters({ ...filters, tag: event.target.value })}>
          <option value="">打标状态</option><option value="tagged">已打标</option><option value="untagged">待打标</option>
        </select>
        <select value={filters.pool} onChange={event => setFilters({ ...filters, pool: event.target.value })}>
          <option value="">入池状态</option><option value="in">已入池</option><option value="out">未入池</option>
        </select>
        <select value={filters.publishTime} onChange={event => setFilters({ ...filters, publishTime: event.target.value })}>
          {publishTimeFilters.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
        </select>
        <select value={filters.sort} onChange={event => setFilters({ ...filters, sort: event.target.value })}>
          {noteSortOptions.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
        </select>
        <span className="flex-spacer" />
        <button className="btn ghost" type="button" onClick={() => exportCsv(list, flash)}>导出</button>
      </div>

      {listError ? <div className="banner danger">{listError}</div> : null}
      <div className="table-card">
        <div className={`table-toolbar ${visibleSelected.length ? 'active' : ''}`}>
          <span>{visibleSelected.length ? `已选 ${visibleSelected.length} 项` : `${listLoading ? '加载中 · ' : ''}共 ${compactNumber(totalCount)} 篇笔记`}</span>
          <span className="flex-spacer" />
          <button className="btn ghost sm" type="button" disabled={!visibleSelected.length} onClick={() => openTagEditor(null)}>批量打标</button>
          <button className="btn primary sm" type="button" disabled={!visibleSelected.length} onClick={() => poolNotes(visibleSelected)}>批量入池</button>
          {visibleSelected.length ? <button className="btn ghost sm" type="button" onClick={() => setSelected([])}>取消</button> : null}
        </div>
        <div className="table-scroll notes-table-scroll">
        <table className="dtable notes-table">
          <colgroup>
            <col className="notes-col-check" />
            <col className="notes-col-note" />
            <col className="notes-col-author" />
            <col className="notes-col-metric" span="4" />
            <col className="notes-col-date" />
            <col className="notes-col-task" />
            <col className="notes-col-content" />
            <col className="notes-col-tag" />
            <col className="notes-col-pool" />
            <col className="notes-col-actions" />
          </colgroup>
          <thead>
            <tr>
              <th className="ck-col"><input type="checkbox" checked={list.length > 0 && visibleSelected.length === list.length} onChange={event => setSelected(event.target.checked ? list.map(noteKey) : [])} /></th>
              <th>笔记</th><th>作者</th><th>点赞</th><th>评论</th><th>收藏</th><th>分享</th><th>发布时间</th><th>采集任务</th><th>采集内容</th><th>标签状态</th><th>入池状态</th><th>操作</th>
            </tr>
          </thead>
          <tbody>
            {list.map((note, index) => {
              const key = noteKey(note)
              const sourceTask = collectionTaskById.get(Number(note.collectionTaskId || 0))
              const noteTags = note.tags || []
              const visibleTags = noteTags.slice(0, 2)
              const poolNames = (note.poolMemberships || []).map(item => contentPools.find(pool => Number(pool.id) === Number(item.poolId))?.name || `#${item.poolId}`)
              return (
                <tr key={key}>
                  <td className="ck-col"><input type="checkbox" checked={selected.includes(key)} onChange={event => setSelected(current => event.target.checked ? [...current, key] : current.filter(id => id !== key))} /></td>
                  <td>
                    <button className="note-cell" type="button" onClick={() => openNoteDetail(note, { notes: list })}>
                      <Cover note={note} index={index} small />
                      <span>{note.title}</span>
                    </button>
                  </td>
                  <td><AuthorInline name={note.author} index={index} /></td>
                  <td className="likes-cell">❤ {compactNumber(note.likesNum)}</td>
                  <td>{compactNumber(note.commentsNum)}</td>
                  <td>{compactNumber(note.collectsNum)}</td>
                  <td>{compactNumber(note.sharesNum)}</td>
                  <td className="muted">{note.publishTs ? formatDate(note.publishTs) : '-'}</td>
                  <td>
                    <div className="note-task-source">
                      <b>{note.collectionTaskName || sourceTask?.name || (note.collectionTaskId ? `任务 #${note.collectionTaskId}` : '-')}</b>
                      {sourceTask ? <span className={`task-type-badge type-${sourceTask.taskType || 'keyword'}`}>{taskTypeOptions.find(item => item.value === (sourceTask.taskType || 'keyword'))?.label || '关键词采集'}</span> : null}
                    </div>
                  </td>
                  <td className="kw-cell"><CollectionContentTokens task={sourceTask} fallback={note.keyword} limit={2} compact /></td>
                  <td>
                    {noteTags.length ? (
                      <div className="note-tag-summary" title={noteTags.join('、')}>
                        {visibleTags.map(tag => <Chip tag={tag} key={tag} tagGroups={tagGroups} />)}
                        {noteTags.length > visibleTags.length ? <span className="summary-more">+{noteTags.length - visibleTags.length}</span> : null}
                      </div>
                    ) : <span className="empty-state-label">待打标</span>}
                  </td>
                  <td>
                    {poolNames.length ? (
                      <div className="note-pool-summary" title={poolNames.join('、')}>
                        <span className="pool-count">已入 {poolNames.length} 个池</span>
                        <span className="pool-name">{poolNames[0]}{poolNames.length > 1 ? ` +${poolNames.length - 1}` : ''}</span>
                      </div>
                    ) : <span className="empty-state-label">未入池</span>}
                  </td>
                  <td className="notes-action-cell">
                    <div className="row-actions">
                      <button className="btn ghost sm" type="button" onClick={() => openTagEditor(note)}>打标</button>
                      <button className="btn primary sm" type="button" onClick={() => poolNotes([key])}>入池</button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>
        {!list.length ? <Empty text="暂无符合条件的笔记" /> : null}
      </div>
      {tagTarget ? (
        <TagModal
          title={tagTarget.title}
          initialTagIds={tagTarget.initialTagIds}
          tagGroups={tagGroups}
          onCreateTag={addTagToGroup}
          onSave={saveTags}
          onClose={() => setTagTarget(null)}
        />
      ) : null}
      {poolTarget ? (
        <Modal title={poolTarget.title} onClose={() => setPoolTarget(null)}>
          <FormRow label="内容池">
            <select value={poolId} onChange={event => setPoolId(Number(event.target.value))}>
              <option value={0}>请选择内容池</option>
              {activePools.filter(pool => notesByKeys(poolTarget.keys).some(note => !(note.poolMemberships || []).some(item => Number(item.poolId) === Number(pool.id)))).map(pool => <option value={pool.id} key={pool.id}>{pool.name}</option>)}
            </select>
          </FormRow>
          <div className="modal-actions"><button className="btn ghost" type="button" onClick={() => setPoolTarget(null)}>取消</button><button className="btn primary" type="button" disabled={!poolId} onClick={savePool}>确认入池</button></div>
        </Modal>
      ) : null}
    </>
  )
}

function resolveNoteDetail(notes, routeKey, routeNoteId) {
  const stored = readStoredNoteDetail()
  const navNote = findStoredNavigationNote(routeKey, routeNoteId)
  const isMatch = note => {
    const key = noteKey(note)
    const id = String(note.note_id || note.noteId || '').trim()
    return (routeKey && key === routeKey) || (routeNoteId && id === routeNoteId)
  }
  const fresh = (notes || []).filter(Boolean).find(isMatch)
  if (fresh) {
    if (stored && isMatch(stored)) return { ...stored, ...fresh }
    return navNote ? { ...navNote, ...fresh } : fresh
  }
  if (stored && isMatch(stored)) return stored
  return navNote
}

function normalizeDetailNote(rawNote, tagGroups) {
  if (!rawNote) return null
  const base = normalizeNote(rawNote)
  const tagMap = new Map(tagGroups.flatMap(group => group.tags || []).map(tag => [Number(tag.id), tag.name]))
  const tagIds = Array.isArray(rawNote.tagIds) ? rawNote.tagIds.map(Number).filter(Boolean) : []
  return {
    ...base,
    ...rawNote,
    note_id: rawNote.note_id || base.note_id,
    coverImage: rawNote.coverImage || base.coverImage,
    media: rawNote.media || base.media,
    likesNum: Number(rawNote.likesNum ?? base.likesNum),
    commentsNum: Number(rawNote.commentsNum ?? base.commentsNum),
    collectsNum: Number(rawNote.collectsNum ?? base.collectsNum),
    sharesNum: Number(rawNote.sharesNum ?? base.sharesNum),
    tagIds,
    tags: Array.isArray(rawNote.tags) && rawNote.tags.length
      ? rawNote.tags
      : tagIds.map(tagId => tagMap.get(Number(tagId))).filter(Boolean),
    quality: Number(rawNote.quality || rawNote.qualityScore || base.qualityScore || autoScore(rawNote)),
  }
}

function NoteDetailPage({ notes, tagGroups, contentPools, reload, replaceNoteTags, updateContentPoolNote, flash }) {
  const [routeHash, setRouteHash] = useState(() => window.location.hash)
  useEffect(() => {
    const onHash = () => setRouteHash(window.location.hash)
    window.addEventListener('hashchange', onHash)
    onHash()
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  const params = useMemo(() => {
    const [, query = ''] = routeHash.split('?')
    return new URLSearchParams(query)
  }, [routeHash])
  const routeKey = params.get('noteKey') || ''
  const routeNoteId = params.get('noteId') || ''
  const [rawNote, setRawNote] = useState(() => resolveNoteDetail(notes, routeKey, routeNoteId))
  const note = useMemo(() => normalizeDetailNote(rawNote, tagGroups), [rawNote, tagGroups])
  const [comments, setComments] = useState([])
  const [commentPage, setCommentPage] = useState(1)
  const [mediaIndex, setMediaIndex] = useState(0)
  const [loadedImageKey, setLoadedImageKey] = useState('')
  const [tagTarget, setTagTarget] = useState(null)
  const [poolMemberships, setPoolMemberships] = useState([])
  const [poolPickerOpen, setPoolPickerOpen] = useState(false)
  const [targetPoolId, setTargetPoolId] = useState(0)
  const activePools = contentPools.length ? contentPools.filter(isPoolActive) : fallbackContentPools
  const media = note?.media || { images: [], videos: [] }
  const carouselImages = media.images || []
  const activeImage = carouselImages[mediaIndex] || ''
  const activeImageKey = activeImage ? `${noteKey(note)}:${mediaIndex}:${activeImage}` : ''
  const imageReady = !activeImage || loadedImageKey === activeImageKey
  const noteNavigation = useMemo(() => resolveNoteNavigation(note, routeKey, routeNoteId), [note, routeKey, routeNoteId])
  const navPosition = noteNavigation.index >= 0 && noteNavigation.total > 1
    ? `${noteNavigation.index + 1} / ${noteNavigation.total}`
    : ''
  const poolStatusItems = useMemo(() => {
    const items = poolMemberships
      .map(item => ({
        key: `${item.poolId}-${item.id || item.noteKey || item.reviewRemark}`,
        poolName: item.poolName || contentPools.find(pool => Number(pool.id) === Number(item.poolId))?.name || `内容池 #${item.poolId}`,
        status: poolStatusLabel(item.reviewStatus || item.rawStatus),
        text: String(item.reviewRemark || '').trim(),
      }))
    const fallbackText = String(note?.reviewRemark || '').trim()
    if (items.length) {
      if (fallbackText && !items.some(item => item.text === fallbackText)) {
        const emptyIndex = items.findIndex(item => !item.text)
        if (emptyIndex >= 0) {
          items[emptyIndex] = { ...items[emptyIndex], text: fallbackText }
        } else {
          items.push({ key: 'note-review-remark', poolName: '审核描述', status: '', text: fallbackText })
        }
      }
      return items
    }
    if (note?.poolStatus) {
      return [{
        key: `note-pool-${note.poolId || 'fallback'}`,
        poolName: contentPools.find(pool => Number(pool.id) === Number(note.poolId))?.name || (note.poolId ? `内容池 #${note.poolId}` : '内容池'),
        status: poolStatusLabel(note.poolStatus),
        text: fallbackText,
      }]
    }
    return []
  }, [poolMemberships, contentPools, note?.poolId, note?.poolStatus, note?.reviewRemark])

  useEffect(() => {
    setRawNote(resolveNoteDetail(notes, routeKey, routeNoteId))
    setMediaIndex(0)
  }, [notes, routeKey, routeNoteId])

  useEffect(() => {
    if (!activeImage) setLoadedImageKey('')
  }, [activeImageKey, activeImage])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.Image !== 'function') return
    ;[noteNavigation.prev, noteNavigation.next]
      .map(notePrimaryImage)
      .filter(Boolean)
      .forEach(url => {
        const image = new window.Image()
        image.src = url
      })
  }, [noteNavigation.prev, noteNavigation.next])

  useEffect(() => {
    const keyword = routeNoteId || routeKey
    if (!keyword) return
    let cancelled = false
    async function loadFreshNote() {
      const [detailRes, tagRes] = await Promise.all([
        routeNoteId
          ? api.get('/urls/get-note-detail', {
              params: {
                note_id: routeNoteId,
                noteUrlId: positiveInteger(rawNote?.noteUrlId),
                collectionTaskId: Number(
                  rawNote?.collectionTaskId || collectionTaskIdFromNoteKey(routeKey),
                ),
              },
            })
          : api.get('/urls/note-search', { params: { page: 1, pageSize: 1, q: keyword } }),
        api.get('/urls/note-tag-list'),
      ])
      if (cancelled) return
      const detailRows = routeNoteId ? [detailRes.data?.data || detailRes.data].filter(Boolean) : pickRows(detailRes)
      const fresh = detailRows.find(item => {
        const key = noteKey(item)
        const id = String(item.note_id || item.noteId || '').trim()
        return (routeKey && key === routeKey) || (routeNoteId && id === routeNoteId)
      })
      if (!fresh) return
      const freshKey = noteKey(fresh)
      const tagIds = pickRows(tagRes)
        .filter(item => String(item.noteKey || item.note_key || '').trim() === freshKey || String(item.note_id || '').trim() === String(fresh.note_id || ''))
        .map(item => Number(item.tagId || 0))
        .filter(Boolean)
      setRawNote(current => {
        const next = { ...(current || {}), ...fresh, tagIds: Array.from(new Set(tagIds)) }
        try {
          sessionStorage.setItem('lume-note-detail-note', JSON.stringify(next))
        } catch {}
        return next
      })
    }
    loadFreshNote()
      .catch(() => {
        if (cancelled) return
      })
    return () => {
      cancelled = true
    }
  }, [routeKey, routeNoteId, rawNote?.noteUrlId, rawNote?.collectionTaskId])

  useEffect(() => {
    if (!note?.note_id) {
      setComments([])
      return
    }
    setCommentPage(1)
    api.get('/comments/getByNoteId', { params: { note_id: note.note_id } })
      .then(res => setComments(res.data?.comments || []))
      .catch(() => setComments([]))
  }, [note?.note_id])

  const loadNotePools = useCallback(async () => {
    if (!note) {
      setPoolMemberships([])
      return
    }
    try {
      const res = await api.get('/content-learning/note-pools', { params: { noteKey: noteKey(note), noteId: note.note_id || '' } })
      setPoolMemberships(pickRows(res))
    } catch {
      setPoolMemberships([])
    }
  }, [note])

  useEffect(() => {
    loadNotePools()
  }, [loadNotePools])

  function moveMedia(step) {
    if (!carouselImages.length) return
    setMediaIndex(current => (current + step + carouselImages.length) % carouselImages.length)
  }

  function openAdjacentNote(target) {
    if (!target) return
    const targetKey = target.key || target.noteKey || noteKey(target)
    const targetNoteId = target.note_id || target.noteId || ''
    try {
      sessionStorage.setItem('lume-note-detail-note', JSON.stringify(target))
    } catch {}
    setRawNote(target)
    navigate('noteDetail', { noteKey: targetKey, noteId: targetNoteId })
  }

  async function addTagToGroup(groupId, groupName, tag) {
    let targetGroupId = Number(groupId)
    if (!targetGroupId) {
      const groupRes = await api.post('/urls/tag-group-create', { name: groupName, color: tagGroupColors[tagGroups.length % tagGroupColors.length] })
      targetGroupId = Number(groupRes.data?.id || 0)
    }
    await api.post('/urls/tag-create', { groupId: targetGroupId, name: tag })
    await reload()
  }

  async function saveTags(tagIds) {
    if (!note) return
    try {
      await replaceNoteTags([note], tagIds)
      const tagMap = new Map(tagGroups.flatMap(group => group.tags || []).map(tag => [Number(tag.id), tag.name]))
      const nextTags = tagIds.map(tagId => tagMap.get(Number(tagId))).filter(Boolean)
      const nextNote = { ...note, tagIds, tags: nextTags }
      setRawNote(current => ({ ...current, tagIds, tags: nextTags }))
      try {
        sessionStorage.setItem('lume-note-detail-note', JSON.stringify(nextNote))
      } catch {}
      setTagTarget(null)
      await reload()
      flash('已完成打标')
    } catch (e) {
      flash(e.message || '打标保存失败')
    }
  }

  async function applyAiSuggestion() {
    if (!note) return
    const nextIds = Array.from(new Set([...(note.tagIds || []), ...suggestTagsForNote(note, tagGroups)]))
    await saveTags(nextIds)
    flash(nextIds.length ? `已应用 ${nextIds.length} 个 AI 建议标签` : '暂无可用建议标签')
  }

  async function setMeta(patch, successText) {
    if (!note) return
    try {
      await updateContentPoolNote([note], patch)
      setRawNote(current => ({ ...current, ...patch }))
      await reload()
      flash(successText)
    } catch (e) {
      flash(e.message || '保存失败')
    }
  }

  function openPoolPicker() {
    const joinedPoolIds = new Set(poolMemberships.map(item => Number(item.poolId)))
    const nextPool = activePools.find(pool => !joinedPoolIds.has(Number(pool.id))) || activePools[0]
    setTargetPoolId(Number(nextPool?.id || 0))
    setPoolPickerOpen(true)
  }

  async function addToPool() {
    if (!note || !targetPoolId) return
    try {
      const noteUrlId = requireNoteUrlId(note)
      const res = await api.post(`/content-learning/pools/${targetPoolId}/notes`, {
        noteKey: noteKey(note),
        note_id: note.note_id || '',
        noteUrlId,
      })
      const saved = res.data?.data || res.data
      setPoolMemberships(current => [saved, ...current.filter(item => Number(item.poolId) !== Number(saved.poolId))])
      const nextNote = { ...note, poolId: saved.poolId || note.poolId || 0, poolStatus: saved.reviewStatus || note.poolStatus || 'review' }
      setRawNote(current => ({ ...current, poolId: nextNote.poolId, poolStatus: nextNote.poolStatus }))
      try {
        sessionStorage.setItem('lume-note-detail-note', JSON.stringify(nextNote))
      } catch {}
      setPoolPickerOpen(false)
      await reload()
      flash('已加入内容池待审核')
    } catch (e) {
      flash(e.response?.data?.message || e.message || '加入内容池失败')
    }
  }

  if (!note) {
    return (
      <div className="note-detail-page">
        <div className="detail-toolbar">
          <button className="btn ghost" type="button" onClick={backFromNoteDetail}>返回列表</button>
        </div>
        <div className="table-card"><Empty text="没有找到这篇笔记" /></div>
      </div>
    )
  }

  return (
    <div className="note-detail-page">
      <section className="note-detail-hero">
        <div className="note-detail-media">
          <div className="detail-left-toolbar">
            <button className="btn ghost" type="button" onClick={backFromNoteDetail}>返回列表</button>
            <span className="detail-id">{note.note_id || noteKey(note)}</span>
          </div>
          <div className="note-carousel detail-carousel">
            {activeImage ? (
              <a className={`note-carousel-image ${imageReady ? 'ready' : 'loading'}`} href={activeImage} target="_blank" rel="noreferrer" key={activeImageKey}>
                <img
                  src={activeImage}
                  alt=""
                  loading="eager"
                  decoding="async"
                  onLoad={() => setLoadedImageKey(activeImageKey)}
                  onError={() => setLoadedImageKey(activeImageKey)}
                />
              </a>
            ) : (
              <div className="note-carousel-empty">暂无图片</div>
            )}
            {activeImage && !imageReady ? <div className="note-carousel-empty note-carousel-loading">图片加载中</div> : null}
            {carouselImages.length > 1 ? (
              <>
                <button className="carousel-nav prev" type="button" onClick={() => moveMedia(-1)} aria-label="上一张">‹</button>
                <button className="carousel-nav next" type="button" onClick={() => moveMedia(1)} aria-label="下一张">›</button>
                <div className="carousel-dots">
                  {carouselImages.map((url, index) => (
                    <button className={index === mediaIndex ? 'active' : ''} type="button" key={`${url}-${index}`} onClick={() => setMediaIndex(index)} aria-label={`第 ${index + 1} 张`} />
                  ))}
                </div>
              </>
            ) : null}
            <div className="carousel-count">{carouselImages.length ? `${mediaIndex + 1} / ${carouselImages.length}` : '0 / 0'}</div>
          </div>
          {media.videos?.length ? (
            <div className="media-video-list">
              {media.videos.map((url, index) => <a href={url} target="_blank" rel="noreferrer" key={url}>视频素材 {index + 1}</a>)}
            </div>
          ) : null}
        </div>

        <div className="note-detail-side">
          <div className="note-detail-main">
            <div className="detail-title-row">
              <h2>{note.title}</h2>
              {noteNavigation.index >= 0 && noteNavigation.total > 1 ? (
                <div className="note-adjacent-nav" aria-label="相邻笔记导航">
                  <button className="btn ghost sm" type="button" disabled={!noteNavigation.prev} onClick={() => openAdjacentNote(noteNavigation.prev)}>‹ 上一篇</button>
                  {navPosition ? <span className="note-nav-count">{navPosition}</span> : null}
                  <button className="btn ghost sm" type="button" disabled={!noteNavigation.next} onClick={() => openAdjacentNote(noteNavigation.next)}>下一篇 ›</button>
                </div>
              ) : null}
            </div>
            <div className="drawer-author"><AuthorInline name={note.author} index={note.rowIndex} /> · 来源关键词「{note.keyword || '-'}」</div>
            <div className="detail-metrics">
              <Metric value={compactNumber(note.likesNum)} label="点赞" />
              <Metric value={compactNumber(note.commentsNum || comments.length)} label="评论" />
              <Metric value={compactNumber(note.collectsNum)} label="收藏" />
              <Metric value={compactNumber(note.sharesNum)} label="分享" />
              <Metric value={note.isVideo ? '视频' : '图文'} label="类型" />
              <Metric value={formatShortDate(note.publishTime || note.createdTime)} label="发布" />
            </div>

            <section className="detail-section detail-section-body">
              <div className="drawer-section-h">正文</div>
              <p className="drawer-desc">{note.description || '暂无正文。可通过插件重新采集详情，admin-web 会在这里展示完整内容。'}</p>
            </section>

            <div className="detail-side-stack">
              <section className="detail-section detail-section-score">
                <div className="drawer-section-h">质量评分</div>
                <div className="score-box">
                  <strong>{note.quality}</strong>
                  <div>
                    <span className="muted">综合互动热度、标题、正文与媒体完整度</span>
                    <div className="score-track"><i style={{ width: `${note.quality}%` }} /></div>
                  </div>
                </div>
              </section>

              <section className="detail-section detail-section-pool">
                <div className="drawer-section-h">
                  <span>入池状态</span>
                  <div className="section-actions">
                    <button className="link" type="button" disabled={!activePools.length} onClick={openPoolPicker}>加入内容池</button>
                  </div>
                </div>
                {poolStatusItems.length ? (
                  <div className="pool-membership-list">
                    {poolStatusItems.map(item => (
                      <div className="pool-membership-card" key={item.key}>
                        <div className="pool-membership-head">
                          <b>{item.poolName}</b>
                          {item.status ? <em>{item.status}</em> : null}
                        </div>
                        {item.text ? <p>{item.text}</p> : <span className="muted">暂无审核描述</span>}
                      </div>
                    ))}
                  </div>
                ) : <span className="muted">未入池</span>}
              </section>

              <section className="detail-section detail-section-tags">
                <div className="drawer-section-h">
                  <span>打标</span>
                  <div className="section-actions">
                    <button className="link" type="button" onClick={applyAiSuggestion}>AI 建议</button>
                    <button className="link" type="button" onClick={() => setTagTarget({ title: '编辑标签', initialTagIds: note.tagIds || [] })}>打标</button>
                  </div>
                </div>
                <div className="selected-tags drawer-selected-tags">
                  {note.tags.length ? note.tags.map(tag => <Chip tag={tag} key={tag} tagGroups={tagGroups} />) : <span className="muted">未选择标签</span>}
                </div>
              </section>
            </div>
          </div>

          <section className="table-card note-detail-comments">
            <div className="table-toolbar">
              <div><strong>评论</strong><span>{comments.length} 条根评论</span></div>
            </div>
            <CommentPreview comments={comments} page={commentPage} onPageChange={setCommentPage} />
          </section>
        </div>
      </section>

      {tagTarget ? (
        <TagModal
          title={tagTarget.title}
          initialTagIds={tagTarget.initialTagIds}
          tagGroups={tagGroups}
          onCreateTag={addTagToGroup}
          onSave={saveTags}
          onClose={() => setTagTarget(null)}
        />
      ) : null}
      {poolPickerOpen ? (
        <Modal title="加入内容池" onClose={() => setPoolPickerOpen(false)}>
          <FormRow label="内容池">
            <select value={targetPoolId} onChange={event => setTargetPoolId(Number(event.target.value))}>
              <option value={0}>请选择内容池</option>
              {activePools.map(pool => {
                const joined = poolMemberships.some(item => Number(item.poolId) === Number(pool.id))
                return <option value={pool.id} disabled={joined} key={pool.id}>{pool.name}{joined ? '（已加入）' : ''}</option>
              })}
            </select>
          </FormRow>
          <div className="modal-actions">
            <button className="btn ghost" type="button" onClick={() => setPoolPickerOpen(false)}>取消</button>
            <button className="btn primary" type="button" disabled={!targetPoolId} onClick={addToPool}>确认入池</button>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}

function TagModal({ title, initialTagIds, tagGroups, onCreateTag, onSave, onClose }) {
  const [tagIds, setTagIds] = useState(() => Array.from(new Set(initialTagIds || [])))
  const [customTag, setCustomTag] = useState('')
  const [selectedGroupId, setSelectedGroupId] = useState(() => tagGroups[0]?.id || '__new__')
  const [newGroupName, setNewGroupName] = useState('')
  const selectedGroup = tagGroups.find(group => String(group.id) === String(selectedGroupId))
  const visibleTags = selectedGroup?.tags || []
  const isNewGroup = selectedGroupId === '__new__'

  function toggle(tagId) {
    setTagIds(current => current.includes(tagId) ? current.filter(item => item !== tagId) : [...current, tagId])
  }

  async function addCustomTag() {
    const tag = customTag.trim()
    if (!tag) return
    const groupName = isNewGroup ? newGroupName.trim() : selectedGroup?.name
    if (!groupName) return
    const groupId = isNewGroup ? 0 : selectedGroup.id
    await onCreateTag(groupId, groupName, tag)
    if (!isNewGroup) setSelectedGroupId(groupId)
    setCustomTag('')
    setNewGroupName('')
  }

  return (
    <Modal title={title} onClose={onClose}>
      <div className="selected-tags">
        {tagIds.length ? tagIds.map(tagId => {
          const tag = tagGroups.flatMap(group => group.tags || []).find(item => Number(item.id) === Number(tagId))
          return (
          <button className="tag-token" type="button" key={tagId} onClick={() => toggle(tagId)} title="点击删除标签">
            {tag?.name || tagId}<span>×</span>
          </button>
        )}) : <span className="muted">未选择标签</span>}
      </div>
      <div className="tag-type-bar">
        <span>标签类型</span>
        <select value={selectedGroupId} onChange={event => setSelectedGroupId(event.target.value)}>
          {tagGroups.map(group => <option value={group.id} key={group.id}>{group.name}</option>)}
          <option value="__new__">新建类型</option>
        </select>
      </div>
      {isNewGroup ? (
        <div className="custom-tag-row tag-type-create">
          <input value={newGroupName} onChange={event => setNewGroupName(event.target.value)} placeholder="新建标签类型" />
        </div>
      ) : null}
      <div className="tag-editor modal-tag-editor">
        {visibleTags.length ? visibleTags.map(tag => <button className={`sel-tag ${tagIds.includes(tag.id) ? 'on' : ''}`} key={tag.id} type="button" onClick={() => toggle(tag.id)}>{tag.name}</button>) : <span className="muted">先新增这个类型下的标签</span>}
      </div>
      <div className="custom-tag-row">
        <input value={customTag} onChange={event => setCustomTag(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') addCustomTag() }} placeholder="新增自定义标签" />
        <button className="btn ghost" type="button" onClick={addCustomTag}>添加</button>
      </div>
      <div className="modal-actions">
        <button className="btn ghost" type="button" onClick={onClose}>取消</button>
        <button className="btn primary" type="button" onClick={() => onSave(tagIds)}>保存标签</button>
      </div>
    </Modal>
  )
}

function TagsPage({ noteTagRows, tagGroups, reload, flash }) {
  const [groupFormOpen, setGroupFormOpen] = useState(false)
  const [tagForm, setTagForm] = useState(null)
  const [groupForm, setGroupForm] = useState({ name: '', color: tagGroupColors[0] })
  const [tagName, setTagName] = useState('')
  const tagUsage = useMemo(() => countNoteTagUsage(noteTagRows), [noteTagRows])

  async function createGroup() {
    const name = groupForm.name.trim()
    if (!name) return
    try {
      await api.post('/urls/tag-group-create', { name, color: groupForm.color || tagGroupColors[0] })
      setGroupFormOpen(false)
      setGroupForm({ name: '', color: tagGroupColors[0] })
      await reload()
      flash('标签分组已创建')
    } catch (e) {
      flash(e.response?.data?.message || e.message || '标签分组创建失败')
    }
  }

  function openTagForm(group) {
    setTagForm(group)
    setTagName('')
  }

  async function createTag() {
    const name = tagName.trim()
    if (!name || !tagForm?.id) return
    try {
      await api.post('/urls/tag-create', { groupId: Number(tagForm.id), name })
      setTagForm(null)
      setTagName('')
      await reload()
      flash('标签已创建')
    } catch (e) {
      flash(e.response?.data?.message || e.message || '标签创建失败')
    }
  }

  async function setTagStatus(tag, status) {
    try {
      await api.put(`/urls/tag-update/${tag.id}`, { status })
      await reload()
      flash(status === 'active' || Number(status) === 1 ? '标签已启用' : '标签已停用')
    } catch (e) {
      flash(e.response?.data?.message || e.message || '标签保存失败')
    }
  }

  function openTagNotes(tag) {
    navigate('notes', { tagId: tag.id })
  }

  return (
    <>
      <div className="tags-toolbar">
        <span className="muted">维护笔记打标使用的标签分组和标签项。</span>
        <span className="flex-spacer" />
        <button className="btn ghost" type="button" onClick={reload}>刷新</button>
        <button className="btn primary" type="button" onClick={() => setGroupFormOpen(true)}>新建分组</button>
      </div>
      <div className="tag-groups">
        {tagGroups.map(group => (
          <section className="tag-group" key={group.id}>
            <div className="tag-group-h">
              <span className="swatch" style={{ background: group.color || tagGroupColors[0] }} />
              <div>
                <strong>{group.name}</strong>
                <span>{(group.tags || []).length} 个标签</span>
              </div>
              <button className="btn ghost sm" type="button" onClick={() => openTagForm(group)}>新增标签</button>
            </div>
            <div className="tag-list">
              {(group.tags || []).map(tag => {
                const isActive = tag.status === undefined || Number(tag.status) === 1 || tag.status === 'active'
                return (
                  <div className={`tag-row ${isActive ? '' : 'off'}`} key={tag.id || tag.name}>
                    <Chip tag={tag.name} tagGroups={tagGroups} />
                    <button
                      className="tag-usage"
                      type="button"
                      onClick={() => openTagNotes(tag)}
                    >
                      {tagUsage.get(Number(tag.id)) || 0} 篇
                    </button>
                    <button className="tag-toggle" type="button" onClick={() => setTagStatus(tag, isActive ? 0 : 1)}>{isActive ? '停用' : '启用'}</button>
                  </div>
                )
              })}
              {!(group.tags || []).length ? <Empty text="暂无标签" /> : null}
            </div>
          </section>
        ))}
        {!tagGroups.length ? <Empty text="暂无标签分组" /> : null}
      </div>
      {groupFormOpen ? (
        <Modal title="新建标签分组" onClose={() => setGroupFormOpen(false)}>
          <FormRow label="分组名称"><input value={groupForm.name} onChange={event => setGroupForm({ ...groupForm, name: event.target.value })} placeholder="例如 功效" /></FormRow>
          <FormRow label="颜色">
            <div className="color-picks">
              {tagGroupColors.map(color => (
                <button className={`color-pick ${groupForm.color === color ? 'on' : ''}`} style={{ background: color }} type="button" key={color} onClick={() => setGroupForm({ ...groupForm, color })} />
              ))}
            </div>
          </FormRow>
          <div className="modal-actions"><button className="btn ghost" type="button" onClick={() => setGroupFormOpen(false)}>取消</button><button className="btn primary" type="button" disabled={!groupForm.name.trim()} onClick={createGroup}>创建分组</button></div>
        </Modal>
      ) : null}
      {tagForm ? (
        <Modal title={`新增标签 · ${tagForm.name}`} onClose={() => setTagForm(null)}>
          <FormRow label="标签名称"><input value={tagName} autoFocus onChange={event => setTagName(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') createTag() }} placeholder="例如 美白" /></FormRow>
          <div className="modal-actions"><button className="btn ghost" type="button" onClick={() => setTagForm(null)}>取消</button><button className="btn primary" type="button" disabled={!tagName.trim()} onClick={createTag}>创建标签</button></div>
        </Modal>
      ) : null}
    </>
  )
}

function NoteDrawer({ note, tagGroups, contentPools, replaceNoteTags, updateContentPoolNote, onEditTags, onChanged, onClose, flash }) {
  const [comments, setComments] = useState([])
  const [commentPage, setCommentPage] = useState(1)
  const [displayTags, setDisplayTags] = useState(() => note.tags || [])
  const [mediaIndex, setMediaIndex] = useState(0)
  const media = note.media || normalizeMediaContent(note.mediaContent, note.coverImage)
  const carouselImages = media.images || []
  const activeImage = carouselImages[mediaIndex] || ''
  useEffect(() => {
    if (!note.note_id) return
    setCommentPage(1)
    api.get('/comments/getByNoteId', { params: { note_id: note.note_id } })
      .then(res => setComments(res.data?.comments || []))
      .catch(() => setComments([]))
  }, [note.note_id])

  useEffect(() => {
    setDisplayTags(note.tags || [])
    setMediaIndex(0)
  }, [note])

  function moveMedia(step) {
    if (!carouselImages.length) return
    setMediaIndex(current => (current + step + carouselImages.length) % carouselImages.length)
  }

  async function setMeta(patch) {
    await updateContentPoolNote([note], patch)
  }

  async function applyAiSuggestion() {
    const nextIds = Array.from(new Set([...(note.tagIds || []), ...suggestTagsForNote(note, tagGroups)]))
    const tagMap = new Map(tagGroups.flatMap(group => group.tags || []).map(tag => [Number(tag.id), tag.name]))
    setDisplayTags(nextIds.map(tagId => tagMap.get(Number(tagId))).filter(Boolean))
    try {
      await replaceNoteTags([note], nextIds)
      await onChanged?.()
      flash(nextIds.length ? `已应用 ${nextIds.length} 个 AI 建议标签` : '暂无可用建议标签')
    } catch (e) {
      flash(e.message || '建议标签保存失败')
    }
  }

  return (
    <>
      <div className="drawer-mask show" onClick={onClose} />
      <aside className="drawer show">
        <div className="drawer-head">
          <div><span>笔记详情</span><b>{note.note_id || '无 note_id'}</b></div>
          <button className="icon-btn" type="button" onClick={onClose}>×</button>
        </div>
        <div className="drawer-body">
          <div className="note-carousel">
            {activeImage ? (
              <a className="note-carousel-image" href={activeImage} target="_blank" rel="noreferrer">
                <img src={activeImage} alt="" />
              </a>
            ) : (
              <div className="note-carousel-empty">暂无图片</div>
            )}
            {carouselImages.length > 1 ? (
              <>
                <button className="carousel-nav prev" type="button" onClick={() => moveMedia(-1)} aria-label="上一张">‹</button>
                <button className="carousel-nav next" type="button" onClick={() => moveMedia(1)} aria-label="下一张">›</button>
                <div className="carousel-dots">
                  {carouselImages.map((url, index) => (
                    <button className={index === mediaIndex ? 'active' : ''} type="button" key={`${url}-${index}`} onClick={() => setMediaIndex(index)} aria-label={`第 ${index + 1} 张`} />
                  ))}
                </div>
              </>
            ) : null}
            <div className="carousel-count">{carouselImages.length ? `${mediaIndex + 1} / ${carouselImages.length}` : '0 / 0'}</div>
          </div>
          {media.videos.length ? (
            <div className="media-video-list">
              {media.videos.map((url, index) => <a href={url} target="_blank" rel="noreferrer" key={url}>视频素材 {index + 1}</a>)}
            </div>
          ) : null}
          <h2 className="drawer-title">{note.title}</h2>
          <div className="drawer-author"><AuthorInline name={note.author} index={note.rowIndex} /> · 来源关键词「{note.keyword || '-'}」</div>
          <div className="drawer-metrics">
            <Metric value={compactNumber(note.likesNum)} label="点赞" />
            <Metric value={compactNumber(note.commentsNum || comments.length)} label="评论" />
            <Metric value={note.isVideo ? '视频' : '图文'} label="类型" />
          </div>
          <p className="drawer-desc">{note.description || '暂无正文。可通过插件重新采集详情，admin-web 会在这里展示完整内容。'}</p>

          <section className="drawer-section">
            <div className="drawer-section-h">质量评分</div>
            <div className="score-box">
              <strong>{note.quality}</strong>
              <div>
                <span className="muted">综合互动热度、标题、正文与媒体完整度</span>
                <div className="score-track"><i style={{ width: `${note.quality}%` }} /></div>
              </div>
            </div>
          </section>

          <section className="drawer-section">
            <div className="drawer-section-h">入池状态</div>
            <div className="selected-tags drawer-selected-tags">
              {note.poolStatus ? <Chip tag={poolStatusLabel(note.poolStatus)} /> : <span className="muted">未入池</span>}
              {note.poolId ? <span className="muted">{contentPools.find(pool => Number(pool.id) === Number(note.poolId))?.name || `内容池 #${note.poolId}`}</span> : null}
            </div>
          </section>

          <section className="drawer-section">
            <div className="drawer-section-h">
              <span>打标</span>
              <div className="section-actions">
                <button className="link" type="button" onClick={applyAiSuggestion}>AI 建议</button>
                <button className="link" type="button" onClick={() => onEditTags({ ...note, tags: displayTags })}>打标</button>
              </div>
            </div>
            <div className="selected-tags drawer-selected-tags">
              {displayTags.length ? displayTags.map(tag => <Chip tag={tag} key={tag} tagGroups={tagGroups} />) : <span className="muted">未选择标签</span>}
            </div>
          </section>

          <section className="drawer-section">
            <div className="drawer-section-h">评论</div>
            <CommentPreview comments={comments} page={commentPage} onPageChange={setCommentPage} />
          </section>
        </div>
        <div className="drawer-foot">
          {note.detailUrl ? <a className="btn ghost" href={note.detailUrl} target="_blank" rel="noreferrer">打开原文</a> : <button className="btn ghost" disabled>打开原文</button>}
          <button className="btn ghost" type="button" onClick={async () => { try { const lowTag = tagGroups.flatMap(group => group.tags || []).find(tag => tag.name === '低质'); if (lowTag) await replaceNoteTags([note], [lowTag.id]); await setMeta({ poolId: 0, poolStatus: '' }); await onChanged?.(); flash('已标记为低质'); onClose() } catch (e) { flash(e.message || '保存失败') } }}>标记低质</button>
          <button className="btn primary" type="button" onClick={async () => { try { await setMeta({ poolId: note.defaultPoolId || contentPools[0]?.id || 0, poolStatus: 'review' }); await onChanged?.(); flash('已加入内容池待审核'); onClose() } catch (e) { flash(e.message || '保存失败') } }}>加入内容池</button>
        </div>
      </aside>
    </>
  )
}

function PoolPage({ notes, contentPools, tagGroups = [], reload, flash }) {
  const pools = contentPools.length ? contentPools : fallbackContentPools
  const [poolId, setPoolId] = useState(pools[0]?.id || 0)
  const [detailPoolId, setDetailPoolId] = useState(() => Number(getHashParams().get('poolId') || 0))
  const [poolFilter, setPoolFilter] = useState('review')
  const [workspaceTab, setWorkspaceTab] = useState(() => normalizePoolWorkspace(getHashParams().get('tab')))
  const [poolNotes, setPoolNotes] = useState([])
  const [loadingNotes, setLoadingNotes] = useState(false)
  const [poolLoadError, setPoolLoadError] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [editingPool, setEditingPool] = useState(null)
  const [selectedSampleIds, setSelectedSampleIds] = useState([])
  const [reviewDialog, setReviewDialog] = useState(null)
  const [pendingPoolAction, setPendingPoolAction] = useState(null)
  const [poolActionLoading, setPoolActionLoading] = useState(false)
  const [form, setForm] = useState({ name: '', description: '' })
  const noteMap = useMemo(() => {
    const map = new Map()
    notes.forEach(note => {
      map.set(noteKey(note), note)
      if (note.note_id) map.set(String(note.note_id), note)
    })
    return map
  }, [notes])
  const selectedPool = pools.find(pool => Number(pool.id) === Number(detailPoolId || poolId)) || pools[0]
  const visiblePools = pools
  const statusFilters = [
    ['all', '全部', '查看当前内容池内全部笔记。'],
    ['review', '待审核', '新入池样本，人工判断是否适合学习。'],
    ['approved', '已通过', '可被学习任务选择。'],
    ['rejected', '已拒绝', '低质或偏题样本，默认不进入学习。'],
    ['archived', '已归档', '不再参与审核流转，保留历史记录。'],
  ]

  useEffect(() => {
    if (!pools.length) return
    if (!poolId || !pools.some(pool => Number(pool.id) === Number(poolId))) {
      setPoolId(Number(pools[0].id))
    }
  }, [pools, poolId])

  useEffect(() => {
    const syncDetailFromHash = () => {
      if (getHashView() !== 'pool') return
      const params = getHashParams()
      const nextDetailPoolId = Number(params.get('poolId') || 0)
      const nextPoolFilter = normalizePoolFilter(params.get('status'))
      const nextWorkspaceTab = normalizePoolWorkspace(params.get('tab'))
      setDetailPoolId(nextDetailPoolId)
      setWorkspaceTab(nextWorkspaceTab)
      if (nextDetailPoolId) {
        setPoolId(nextDetailPoolId)
        setPoolFilter(nextPoolFilter)
      }
    }
    syncDetailFromHash()
    window.addEventListener('hashchange', syncDetailFromHash)
    return () => window.removeEventListener('hashchange', syncDetailFromHash)
  }, [])

  useEffect(() => {
    if (!detailPoolId) return
    loadPoolNotes()
  }, [detailPoolId])

  useEffect(() => {
    setSelectedSampleIds([])
  }, [detailPoolId, poolFilter])

  async function loadPoolNotes() {
    setLoadingNotes(true)
    setPoolLoadError('')
    setPoolNotes([])
    try {
      const res = await api.get(`/content-learning/pools/${detailPoolId}/notes`)
      setPoolNotes(pickRows(res))
    } catch (e) {
      const message = e.response?.data?.message || e.message || '内容池样本加载失败'
      setPoolLoadError(message)
      flash(message)
    } finally {
      setLoadingNotes(false)
    }
  }

  async function createPool() {
    if (!form.name.trim()) return
    try {
      const res = await api.post('/content-learning/pools', {
        name: form.name.trim(),
        description: form.description,
      })
      const created = res.data?.data || res.data
      setCreateOpen(false)
      setForm({ name: '', description: '' })
      await reload()
      if (created?.id) setPoolId(Number(created.id))
      flash('内容池已创建')
    } catch (e) {
      flash(e.response?.data?.message || e.message || '内容池创建失败')
    }
  }

  function openCreatePool() {
    setEditingPool(null)
    setForm({ name: '', description: '' })
    setCreateOpen(true)
  }

  function openEditPool(pool) {
    setEditingPool(pool)
    setForm({
      name: pool.name || '',
      description: pool.description || '',
    })
    setCreateOpen(true)
  }

  async function savePool() {
    if (!editingPool?.id) return createPool()
    if (!form.name.trim()) return
    try {
      await api.put(`/content-learning/pools/${editingPool.id}`, {
        name: form.name.trim(),
        description: form.description,
      })
      setCreateOpen(false)
      setEditingPool(null)
      await reload()
      flash('内容池已保存')
    } catch (e) {
      flash(e.response?.data?.message || e.message || '内容池保存失败')
    }
  }

  function publishPool(pool) {
    if (pool.status === 'active') return
    setPendingPoolAction({ type: 'publish', pool })
  }

  function archivePool(pool) {
    if (pool.status === 'archived') return
    setPendingPoolAction({ type: 'archive', pool })
  }

  async function confirmPoolAction() {
    if (!pendingPoolAction?.pool?.id) return
    const { type, pool } = pendingPoolAction
    setPoolActionLoading(true)
    try {
      await api.put(`/content-learning/pools/${pool.id}`, { status: type === 'publish' ? 'active' : 'archived' })
      await reload()
      setPendingPoolAction(null)
      if (type === 'archive' && Number(detailPoolId) === Number(pool.id)) {
        setDetailPoolId(0)
        navigate('pool')
      }
      flash(type === 'publish' ? '内容池已发布' : '内容池已归档')
    } catch (e) {
      flash(e.response?.data?.message || e.message || (type === 'publish' ? '内容池发布失败' : '内容池归档失败'))
    } finally {
      setPoolActionLoading(false)
    }
  }

  async function saveReviewSample(sample, reviewStatus, reviewRemark) {
    await api.put(`/content-learning/pool-samples/${sample.id}/review`, { reviewStatus, reviewRemark })
    setPoolNotes(current => current.map(item => item.id === sample.id ? { ...item, reviewStatus, reviewRemark } : item))
    await reload()
  }

  async function reviewSample(sample, reviewStatus) {
    try {
      await saveReviewSample(sample, reviewStatus, sample.reviewRemark || '')
      flash({ review: '已恢复待审', approved: '已审核通过', rejected: '已审核拒绝', archived: '已归档' }[reviewStatus])
    } catch (e) {
      flash(e.response?.data?.message || e.message || '审核状态保存失败')
    }
  }

  function openReviewDialog({ sample = null, reviewStatus, mode = 'single' }) {
    const isBatch = mode === 'batch'
    const isRejected = reviewStatus === 'rejected'
    const isApproved = reviewStatus === 'approved'
    const isRemarkEdit = reviewStatus === 'approved' && sample?.reviewStatus === 'approved'
    setReviewDialog({
      mode,
      sample,
      reviewStatus,
      title: isRemarkEdit ? '修改审核描述' : isBatch ? (isRejected ? '批量拒绝' : '批量通过') : (isRejected ? '拒绝笔记' : '通过笔记'),
      label: isRejected ? '拒绝原因' : '审核描述',
      placeholder: isRejected ? '填写拒绝原因' : '填写圈选理由',
      value: isBatch ? '' : (sample?.reviewRemark || ''),
      required: isBatch || isRejected || (isApproved && !isRemarkEdit),
    })
  }

  async function submitReviewDialog(value) {
    if (!reviewDialog) return
    const reviewRemark = value.trim()
    if (reviewDialog.required && !reviewRemark) {
      flash(`${reviewDialog.label}必须填写`)
      return
    }
    try {
      if (reviewDialog.mode === 'batch') {
        await reviewSelectedSamples(reviewDialog.reviewStatus, reviewRemark, { throwOnError: true })
      } else {
        await saveReviewSample(reviewDialog.sample, reviewDialog.reviewStatus, reviewRemark)
        flash(reviewDialog.reviewStatus === 'approved' ? '已保存审核描述' : '已审核拒绝')
      }
      setReviewDialog(null)
    } catch (e) {
      flash(e.response?.data?.message || e.message || '审核状态保存失败')
    }
  }

  async function reviewSelectedSamples(reviewStatus, reviewRemark = '', options = {}) {
    if (!selectedSampleIds.length) return
    try {
      await Promise.all(selectedSampleIds.map(id => api.put(`/content-learning/pool-samples/${id}/review`, { reviewStatus, reviewRemark })))
      const selectedSet = new Set(selectedSampleIds.map(Number))
      setPoolNotes(current => current.map(item => selectedSet.has(Number(item.id)) ? { ...item, reviewStatus, reviewRemark } : item))
      setSelectedSampleIds([])
      await reload()
      flash({ review: '已批量恢复待审', approved: '已批量通过', rejected: '已批量拒绝', archived: '已批量归档' }[reviewStatus])
    } catch (e) {
      flash(e.response?.data?.message || e.message || '批量审核失败')
      if (options.throwOnError) throw e
    }
  }

  function toggleSampleSelected(sampleId, checked) {
    const id = Number(sampleId)
    setSelectedSampleIds(current => {
      const next = new Set(current.map(Number))
      if (checked) next.add(id)
      else next.delete(id)
      return Array.from(next)
    })
  }

  function toggleVisibleSamples(checked) {
    const visibleIds = filteredPoolNotes.map(item => Number(item.id))
    setSelectedSampleIds(current => {
      const next = new Set(current.map(Number))
      visibleIds.forEach(id => {
        if (checked) next.add(id)
        else next.delete(id)
      })
      return Array.from(next)
    })
  }

  function noteForSample(sample) {
    const fromApi = sample.note || {}
    const fromLocal = noteMap.get(sample.noteKey) || noteMap.get(sample.note_id) || {}
    return {
      ...fromLocal,
      ...fromApi,
      noteUrlId: positiveInteger(sample.noteUrlId || fromApi.noteUrlId || fromLocal.noteUrlId),
      note_id: sample.note_id || fromApi.note_id || fromLocal.note_id,
    }
  }

  function detailNoteForSample(sample) {
    const note = noteForSample(sample)
    return {
      ...note,
      note_id: sample.note_id || note.note_id,
      poolId: sample.poolId || detailPoolId,
      poolStatus: normalizePoolReviewStatus(sample.reviewStatus),
      reviewRemark: sample.reviewRemark || note.reviewRemark || '',
    }
  }

  function openPoolDetail(pool) {
    setDetailPoolId(Number(pool.id))
    setPoolId(Number(pool.id))
    const nextFilter = 'review'
    setPoolFilter(nextFilter)
    setWorkspaceTab('review')
    navigate('pool', { poolId: pool.id, status: nextFilter, tab: 'review' })
  }

  function selectPoolDetail(nextPoolId) {
    const id = Number(nextPoolId)
    setDetailPoolId(id)
    setPoolId(id)
    const nextFilter = 'review'
    setPoolFilter(nextFilter)
    setWorkspaceTab('review')
    navigate('pool', { poolId: id, status: nextFilter, tab: 'review' })
  }

  function selectPoolFilter(nextFilter) {
    const status = normalizePoolFilter(nextFilter)
    setPoolFilter(status)
    if (detailPoolId) navigate('pool', { poolId: detailPoolId, status, tab: workspaceTab })
  }

  function selectWorkspaceTab(nextTab) {
    const tab = normalizePoolWorkspace(nextTab)
    setWorkspaceTab(tab)
    if (detailPoolId) navigate('pool', { poolId: detailPoolId, status: poolFilter, tab })
  }

  function backToPoolList() {
    setDetailPoolId(0)
    navigate('pool')
  }

  const filteredPoolNotes = poolFilter === 'all' ? poolNotes : poolNotes.filter(item => item.reviewStatus === poolFilter)
  const filteredPoolDetailNotes = filteredPoolNotes.map(detailNoteForSample)
  const selectedSet = new Set(selectedSampleIds.map(Number))
  const selectedVisibleCount = filteredPoolNotes.filter(item => selectedSet.has(Number(item.id))).length
  const allVisibleSelected = filteredPoolNotes.length > 0 && selectedVisibleCount === filteredPoolNotes.length
  const poolStats = {
    all: selectedPool?.noteCount || poolNotes.length,
    review: selectedPool?.reviewCount || poolNotes.filter(item => item.reviewStatus === 'review').length,
    approved: selectedPool?.approvedCount || poolNotes.filter(item => item.reviewStatus === 'approved').length,
    rejected: selectedPool?.rejectedCount || poolNotes.filter(item => item.reviewStatus === 'rejected').length,
    archived: selectedPool?.archivedCount || poolNotes.filter(item => item.reviewStatus === 'archived').length,
  }

  if (!detailPoolId) {
    return (
      <>
        <div className="table-toolbar">
          <div>
            <strong>内容池</strong>
            <span>待发布内容池可配置和检查；笔记入池只允许选择已发布内容池。</span>
          </div>
          <div className="filterbar pool-list-toolbar">
            <button className="btn ghost" type="button" onClick={reload}>刷新</button>
            <button className="btn primary" type="button" onClick={openCreatePool}>新建内容池</button>
          </div>
        </div>
        <div className="table-card">
          <table className="dtable pool-list-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>名称 / 描述</th>
                <th>笔记</th>
                <th>待审核</th>
                <th>已通过</th>
                <th>已拒绝</th>
                <th>发布状态</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
            {visiblePools.map(pool => (
                <tr key={pool.id}>
                  <td>#{pool.id}</td>
                  <td><button className="pool-title-link" type="button" onClick={() => openPoolDetail(pool)}><strong>{pool.name}</strong><span>{pool.description || '无描述'}</span></button></td>
                  <td>{pool.noteCount || 0}</td>
                  <td>{pool.reviewCount || 0}</td>
                  <td>{pool.approvedCount || 0}</td>
                  <td>{pool.rejectedCount || 0}</td>
                  <td><Pill tone={pool.status === 'active' ? 'ok' : 'wait'}>{poolLifecycleLabel(pool.status)}</Pill></td>
                  <td>{formatShortDate(pool.createdTime)}</td>
                  <td>
                    {pool.status === 'draft' ? <button className="link" type="button" onClick={() => openEditPool(pool)}>编辑</button> : null}
                    {pool.status === 'draft' ? (
                      <>{' · '}<button className="link" type="button" onClick={() => publishPool(pool)}>发布内容池</button></>
                    ) : null}
                    {pool.status !== 'archived' ? (
                      <>{pool.status === 'draft' ? ' · ' : ''}<button className="link danger-link" type="button" onClick={() => archivePool(pool)}>归档</button></>
                    ) : <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
              {!visiblePools.length ? <tr><td colSpan={9}><Empty text="暂无内容池" /></td></tr> : null}
            </tbody>
          </table>
        </div>
        {createOpen ? (
          <PoolCreateModal mode={editingPool ? 'edit' : 'create'} form={form} setForm={setForm} onClose={() => { setCreateOpen(false); setEditingPool(null) }} onSubmit={savePool} />
        ) : null}
        {pendingPoolAction ? (
          <PoolLifecycleConfirmModal
            action={pendingPoolAction.type}
            pool={pendingPoolAction.pool}
            loading={poolActionLoading}
            onClose={() => setPendingPoolAction(null)}
            onConfirm={confirmPoolAction}
          />
        ) : null}
      </>
    )
  }

  return (
    <>
      <div className="pool-sticky-nav">
        <button className="link pool-back-link" type="button" onClick={backToPoolList}>← 返回内容池列表</button>
        <div className="pool-context-main">
          <label className="pool-switcher">
            <select aria-label="选择内容池" value={detailPoolId} onChange={event => selectPoolDetail(event.target.value)}>{pools.map(pool => <option value={pool.id} key={pool.id}>{pool.name}</option>)}</select>
          </label>
          <Pill tone={selectedPool?.status === 'active' ? 'ok' : 'wait'}>{poolLifecycleLabel(selectedPool?.status)}</Pill>
        </div>
        <p className="pool-context-desc">{selectedPool?.description || '维护内容池内笔记准入状态，确保后续学习任务只消费已通过样本。'}</p>
        <div className="workspace-tabs pool-workspace-tabs" role="tablist" aria-label="内容池工作区">
          {poolWorkspaceTabs.map(tab => (
            <button
              className={`workspace-tab ${workspaceTab === tab.value ? 'active' : ''}`}
              type="button"
              role="tab"
              aria-selected={workspaceTab === tab.value}
              key={tab.value}
              onClick={() => selectWorkspaceTab(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {workspaceTab === 'review' ? (
        <>
      <section className="panel pool-status-panel">
        <div className="panel-head pool-review-head">
          <div>
            <h3>内容池看板</h3>
            <span className="hint">点击状态卡筛选下方笔记列表</span>
          </div>
          <div className="filterbar pool-review-actions">
            {selectedPool?.status === 'draft' ? <button className="btn ghost" type="button" onClick={() => openEditPool(selectedPool)}>编辑内容池</button> : null}
            {selectedPool?.status === 'draft' ? <button className="btn primary" type="button" onClick={() => publishPool(selectedPool)}>发布内容池</button> : null}
            {selectedPool?.status !== 'archived' ? <button className="btn ghost" type="button" onClick={() => archivePool(selectedPool)}>归档内容池</button> : null}
            <button className="btn ghost" type="button" onClick={reload}>刷新</button>
            <button className="btn ghost" type="button" onClick={() => exportCsv(poolNotes.map(noteForSample), flash)}>导出</button>
          </div>
        </div>
        <div className="pool-status-grid">
          {statusFilters.map(([status, label, hint]) => (
            <button className={`pool-status-card ${poolFilter === status ? 'active' : ''}`} key={status} type="button" onClick={() => selectPoolFilter(status)}>
              <span>{label}</span>
              <strong>{poolStats[status] || 0}</strong>
              <em>{hint}</em>
            </button>
          ))}
        </div>
      </section>

      <section className="table-card pool-notes-card">
        <div className="table-toolbar">
          <div>
            <strong>笔记列表</strong>
            <span>{poolStatusLabel(poolFilter)} · {filteredPoolNotes.length} 篇</span>
          </div>
          <div className="filterbar pool-note-toolbar">
            <button className="btn ghost sm" type="button" disabled={!filteredPoolNotes.length} onClick={() => toggleVisibleSamples(!allVisibleSelected)}>{allVisibleSelected ? '取消全选' : '全选当前页'}</button>
            <button className="btn ghost sm" type="button" disabled={!selectedSampleIds.length} onClick={() => openReviewDialog({ reviewStatus: 'approved', mode: 'batch' })}>批量通过</button>
            <button className="btn ghost sm" type="button" disabled={!selectedSampleIds.length} onClick={() => openReviewDialog({ reviewStatus: 'rejected', mode: 'batch' })}>批量拒绝</button>
            <button className="btn ghost sm" type="button" disabled={!selectedSampleIds.length} onClick={() => reviewSelectedSamples('archived')}>批量归档</button>
            <select disabled><option>质量分降序</option></select>
          </div>
        </div>
        {poolLoadError ? <div className="banner danger pool-error">{poolLoadError}</div> : null}
        <div className="table-scroll pool-note-scroll">
        <table className="dtable pool-note-table">
          <colgroup>
            <col className="pool-note-col-check" />
            <col className="pool-note-col-note" />
            <col className="pool-note-col-author" />
            <col className="pool-note-col-interaction" />
            <col className="pool-note-col-content" />
            <col className="pool-note-col-tag" />
            <col className="pool-note-col-remark" />
            <col className="pool-note-col-actions" />
          </colgroup>
          <thead>
            <tr>
              <th className="ck-col"><input type="checkbox" checked={allVisibleSelected} disabled={!filteredPoolNotes.length} onChange={event => toggleVisibleSamples(event.target.checked)} /></th>
              <th>笔记</th>
              <th>作者</th>
              <th>互动</th>
              <th>采集内容</th>
              <th>标签状态</th>
              <th>审核描述</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredPoolNotes.map((sample, index) => {
              const note = noteForSample(sample)
              const detailNote = filteredPoolDetailNotes[index]
              return (
                <tr key={sample.id}>
                  <td className="ck-col"><input type="checkbox" checked={selectedSet.has(Number(sample.id))} onChange={event => toggleSampleSelected(sample.id, event.target.checked)} /></td>
                  <td>
                    <button className="note-cell note-cell-text" type="button" onClick={() => openNoteDetail(detailNote, { notes: filteredPoolDetailNotes })}>
                      <span>{note.title || '未命名笔记'}</span>
                      <em>{sample.note_id || sample.noteKey}</em>
                    </button>
                  </td>
                  <td>{note.author || '-'}</td>
                  <td>
                    <div className="pool-note-interactions">
                      <span>赞 {compactNumber(note.likesNum || 0)}</span>
                      <span>评 {compactNumber(note.commentsNum || 0)}</span>
                      <span>藏 {compactNumber(note.collectsNum || 0)}</span>
                    </div>
                  </td>
                  <td>{note.keyword || '-'}</td>
                  <td><Pill tone={(note.tagIds || []).length ? 'ok' : 'wait'}>{(note.tagIds || []).length ? '已打标' : '待打标'}</Pill></td>
                  <td>
                    {sample.reviewStatus === 'approved' ? (
                      <div className="review-remark-cell">
                        <span>{sample.reviewRemark || '-'}</span>
                        <button className="link" type="button" onClick={() => openReviewDialog({ sample, reviewStatus: 'approved' })}>{sample.reviewRemark ? '修改' : '补充描述'}</button>
                      </div>
                    ) : (
                      <span className="muted">{sample.reviewRemark || '-'}</span>
                    )}
                  </td>
                  <td>
                    {sample.reviewStatus === 'review' ? (
                      <><button className="link" type="button" onClick={() => openReviewDialog({ sample, reviewStatus: 'approved' })}>通过</button> · <button className="link" type="button" onClick={() => openReviewDialog({ sample, reviewStatus: 'rejected' })}>拒绝</button> · <button className="link" type="button" onClick={() => reviewSample(sample, 'archived')}>归档</button></>
                    ) : sample.reviewStatus === 'approved' ? (
                      <><button className="link" type="button" onClick={() => reviewSample(sample, 'review')}>退回待审</button> · <button className="link" type="button" onClick={() => openReviewDialog({ sample, reviewStatus: 'rejected' })}>拒绝</button> · <button className="link" type="button" onClick={() => reviewSample(sample, 'archived')}>归档</button></>
                    ) : sample.reviewStatus === 'rejected' ? (
                      <><button className="link" type="button" onClick={() => reviewSample(sample, 'review')}>恢复待审</button> · <button className="link" type="button" onClick={() => reviewSample(sample, 'archived')}>归档</button></>
                    ) : <button className="link" type="button" onClick={() => reviewSample(sample, 'review')}>恢复待审</button>}
                  </td>
                </tr>
              )
            })}
            {!filteredPoolNotes.length ? (
              <tr>
                <td colSpan={8}><Empty text={loadingNotes ? '加载中' : '暂无内容'} /></td>
              </tr>
            ) : null}
          </tbody>
        </table>
        </div>
      </section>
        </>
      ) : null}

      {workspaceTab === 'search' ? (
        <SemanticSearchWorkspace pool={selectedPool} poolId={Number(detailPoolId || selectedPool?.id || 0)} tagGroups={tagGroups} flash={flash} />
      ) : null}

      {workspaceTab === 'index' ? (
        <SemanticIndexWorkspace pool={selectedPool} flash={flash} />
      ) : null}

      {createOpen ? (
        <PoolCreateModal mode={editingPool ? 'edit' : 'create'} form={form} setForm={setForm} onClose={() => { setCreateOpen(false); setEditingPool(null) }} onSubmit={savePool} />
      ) : null}
      {reviewDialog ? (
        <ReviewRemarkModal dialog={reviewDialog} onClose={() => setReviewDialog(null)} onSubmit={submitReviewDialog} />
      ) : null}
      {pendingPoolAction ? (
        <PoolLifecycleConfirmModal
          action={pendingPoolAction.type}
          pool={pendingPoolAction.pool}
          loading={poolActionLoading}
          onClose={() => setPendingPoolAction(null)}
          onConfirm={confirmPoolAction}
        />
      ) : null}
    </>
  )
}

function SemanticSearchWorkspace({ pool, poolId, tagGroups = [], flash }) {
  const effectivePoolId = Number(poolId || pool?.id || getHashParams().get('poolId') || 0)
  const initialSearchState = useMemo(() => {
    const cached = readSemanticSearchState(effectivePoolId)
    return isRestorableSemanticSearchState(cached) ? cached : null
  }, [effectivePoolId])
  const [query, setQuery] = useState(() => initialSearchState?.query || '')
  const [topN, setTopN] = useState(() => Number(initialSearchState?.topN || 12))
  const [pageSize, setPageSize] = useState(() => Number(initialSearchState?.pageSize || 6))
  const [selectedTagIds, setSelectedTagIds] = useState(() => (initialSearchState?.selectedTagIds || []).map(Number).filter(Boolean))
  const [tagMatch, setTagMatch] = useState(() => initialSearchState?.tagMatch || 'all')
  const [result, setResult] = useState(() => initialSearchState?.result || null)
  const [page, setPage] = useState(() => Number(initialSearchState?.page || initialSearchState?.result?.currentPage || 1))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [tagPickerOpen, setTagPickerOpen] = useState(false)
  const [activeTagGroupKey, setActiveTagGroupKey] = useState('')
  const [indexLogActive, setIndexLogActive] = useState(null)
  const [indexLogEvents, setIndexLogEvents] = useState([])
  const [indexLogLoading, setIndexLogLoading] = useState(false)
  const tagPickerRef = useRef(null)
  const tagFilterKey = selectedTagIds.join(',')
  const searchableTagGroups = useMemo(
    () => tagGroups
      .map(group => ({ ...group, tags: (group.tags || []).filter(tag => Number(tag.id || 0) > 0) }))
      .filter(group => group.tags.length),
    [tagGroups]
  )
  const tagById = useMemo(
    () => new Map(searchableTagGroups.flatMap(group => group.tags.map(tag => [Number(tag.id), { ...tag, groupName: group.name, color: tag.color || group.color }]))),
    [searchableTagGroups]
  )
  const selectedTags = selectedTagIds.map(tagId => tagById.get(Number(tagId))).filter(Boolean)
  const activeTagGroup = searchableTagGroups.find(group => String(group.id || group.name) === String(activeTagGroupKey)) || searchableTagGroups[0]
  const filterSignature = useMemo(
    () => semanticSearchSignature({
      poolId: effectivePoolId,
      query,
      topN,
      pageSize,
      tagIds: selectedTagIds,
      tagMatch,
    }),
    [effectivePoolId, query, topN, pageSize, tagFilterKey, tagMatch]
  )
  const [resultSignature, setResultSignature] = useState(() => initialSearchState?.resultSignature || '')
  const canSearch = effectivePoolId > 0
  const didMountRef = useRef(false)
  const skipNextClearRef = useRef(false)
  const restoredStateRef = useRef(isRestorableSemanticSearchState(initialSearchState))

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    if (skipNextClearRef.current) {
      skipNextClearRef.current = false
      return
    }
    if (result && resultSignature && resultSignature === filterSignature) return
    if (!result && !restoredStateRef.current && isRestorableSemanticSearchState(readSemanticSearchState(effectivePoolId))) return
    setResult(null)
    setResultSignature('')
    setPage(1)
    clearSemanticSearchState(effectivePoolId)
  }, [filterSignature, result, resultSignature, effectivePoolId])

  useLayoutEffect(() => {
    if (!effectivePoolId || restoredStateRef.current || result) return
    const cached = readSemanticSearchState(effectivePoolId)
    if (!isRestorableSemanticSearchState(cached)) return
    restoredStateRef.current = true
    skipNextClearRef.current = true
    setQuery(cached.query || '')
    setTopN(Number(cached.topN || 12))
    setPageSize(Number(cached.pageSize || 6))
    setSelectedTagIds((cached.selectedTagIds || []).map(Number).filter(Boolean))
    setTagMatch(cached.tagMatch || 'all')
    setResult(cached.result)
    setPage(Number(cached.page || cached.result?.currentPage || 1))
    setResultSignature(cached.resultSignature || '')
  }, [effectivePoolId, result])

  useEffect(() => {
    if (!tagPickerOpen) return undefined
    const onPointerDown = event => {
      if (!tagPickerRef.current?.contains(event.target)) setTagPickerOpen(false)
    }
    const onKeyDown = event => {
      if (event.key === 'Escape') setTagPickerOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [tagPickerOpen])

  useEffect(() => {
    if (!searchableTagGroups.length) {
      setActiveTagGroupKey('')
      return
    }
    const hasActive = searchableTagGroups.some(group => String(group.id || group.name) === String(activeTagGroupKey))
    if (!hasActive) setActiveTagGroupKey(searchableTagGroups[0].id || searchableTagGroups[0].name)
  }, [searchableTagGroups, activeTagGroupKey])

  function toggleTag(tagId) {
    const normalized = Number(tagId)
    if (!normalized) return
    setSelectedTagIds(current => (
      current.includes(normalized)
        ? current.filter(item => item !== normalized)
        : [...current, normalized]
    ))
  }

  function persistSearchState(nextState = {}) {
    const nextResult = nextState.result ?? result
    if (!nextResult || !Array.isArray(nextResult.data)) return
    writeSemanticSearchState(effectivePoolId, {
      query,
      topN,
      pageSize,
      selectedTagIds,
      tagMatch,
      result,
      page,
      resultSignature,
      ...nextState,
    })
  }

  async function runSearch(nextPage = 1) {
    if (!canSearch && nextPage === 1) return
    setLoading(true)
    setError('')
    try {
      const payload = result?.searchToken
        ? { poolId: effectivePoolId, searchToken: result.searchToken, page: nextPage, pageSize }
        : {
          poolId: effectivePoolId,
          query: query.trim(),
          topN,
          page: nextPage,
          pageSize,
          tagIds: selectedTagIds,
          tagMatch,
        }
      const res = await api.post('/semantic/admin/query', payload)
      const data = res.data?.data || res.data || {}
      const nextPageNumber = Number(data.currentPage || nextPage)
      setResult(data)
      setResultSignature(filterSignature)
      setPage(nextPageNumber)
      persistSearchState({
        result: data,
        page: nextPageNumber,
        resultSignature: filterSignature,
      })
      if (!data.resultCount) flash(query.trim() ? '语义查询完成，当前条件下没有结果' : '标签筛选完成，当前条件下没有结果')
    } catch (e) {
      const code = e.response?.data?.code || ''
      const message = code === 'QUERY_REQUIRED' && !query.trim()
        ? '当前后端仍按语义检索校验 query，请重启服务端或确认 admin 标签筛选分支已部署'
        : (e.response?.data?.message || e.message || '语义查询失败')
      setError(message)
      flash(message)
    } finally {
      setLoading(false)
    }
  }

  function openResultNoteDetail(item) {
    if (!item?.note) return
    persistSearchState()
    openNoteDetail(item.note, { notes: semanticNavigationNotes })
  }

  async function openIndexLog(poolNoteId) {
    const normalizedPoolNoteId = Number(poolNoteId || 0)
    if (!normalizedPoolNoteId) return
    setIndexLogLoading(true)
    setIndexLogActive(null)
    setIndexLogEvents([])
    try {
      const [vectorRes, eventsRes] = await Promise.all([
        api.get(`/semantic/admin/note-vectors/${normalizedPoolNoteId}`),
        api.get('/semantic/admin/events', { params: { poolNoteId: normalizedPoolNoteId, limit: 80 } }),
      ])
      const vector = vectorRes.data?.data || vectorRes.data || null
      const events = eventsRes.data?.data?.data || eventsRes.data?.data || []
      if (!vector?.poolNoteId) throw new Error('当前样本暂无索引记录')
      setIndexLogActive(vector)
      setIndexLogEvents(Array.isArray(events) ? events : [])
    } catch (e) {
      const message = e.response?.data?.message || e.message || '索引日志加载失败'
      flash(message)
    } finally {
      setIndexLogLoading(false)
    }
  }

  const rows = result?.data || []
  const semanticNavigationNotes = rows.map(item => item.note).filter(Boolean)
  const semanticQuery = result?.semanticQuery || {}
  const diagnostics = result?.diagnostics || {}
  const filtersChanged = Boolean(result && resultSignature && resultSignature !== filterSignature)
  const intentSource = String(semanticQuery.intentSource || '')
  const intentStatus = result
    ? (result.mode === 'tag_filter' || intentSource === 'tag_filter'
      ? '仅按标签筛选，未使用语义理解'
      : (intentSource === 'no-approved-precheck' ? '没有可检索样本，未进行语义理解' : '已根据输入内容理解需求'))
    : '待查询'
  const queryModeLabel = query.trim()
    ? '语义查询'
    : (selectedTagIds.length ? '标签筛选' : '内容池浏览')
  const resultCountLabel = result
    ? `${result.resultCount || 0} 条${result.searchToken ? ` · token ${result.searchToken}` : ` · ${queryModeLabel}`}`
    : `当前内容池：${pool?.name || '-'}`
  const elapsedLabel = diagnostics.elapsedMs !== undefined ? `${diagnostics.elapsedMs}ms` : '-'
  const recallDimensionLabel = formatRecallSourceSummary(diagnostics.recallDimensions).sourceText || '-'
  const selectedTagLabel = selectedTags.length
    ? selectedTags.map(tag => tag.name).join(' + ')
    : '不限标签'
  const tagPickerLabel = selectedTags.length ? `已选 ${selectedTags.length} 项` : '请选择标签'

  return (
    <section className="semantic-workspace">
      <div className="semantic-query-panel">
        <div className="semantic-query-controls">
          <div className={`semantic-tag-filter ${tagPickerOpen ? 'picker-open' : ''}`}>
            <div className="semantic-filter-field semantic-tag-cascade" ref={tagPickerRef}>
              <span className="semantic-filter-label">标签</span>
              <button
                className="semantic-filter-trigger"
                type="button"
                aria-expanded={tagPickerOpen}
                onClick={() => setTagPickerOpen(open => !open)}
              >
                {selectedTags.length ? <span>{tagPickerLabel}</span> : (
                  <span>
                    <span className="semantic-hidden-test-label" aria-hidden="true">选择标签</span>
                    <span aria-hidden="true">{tagPickerLabel}</span>
                  </span>
                )}
                <b>⌄</b>
              </button>
              {tagPickerOpen ? (
              <div className="semantic-tag-panel">
                {searchableTagGroups.length ? (
                  <>
                    <div className="semantic-tag-group-list">
                      {searchableTagGroups.map(group => {
                        const key = group.id || group.name
                        const active = String(activeTagGroup?.id || activeTagGroup?.name) === String(key)
                        return (
                          <button
                            className={active ? 'active' : ''}
                            type="button"
                            key={key}
                            onClick={() => setActiveTagGroupKey(key)}
                          >
                            {group.name}
                          </button>
                        )
                      })}
                    </div>
                    <div className="semantic-tag-option-pane">
                      {(activeTagGroup?.tags || []).map(tag => (
                        <label className={`semantic-tag-option ${selectedTagIds.includes(Number(tag.id)) ? 'on' : ''}`} key={tag.id}>
                          <input type="checkbox" checked={selectedTagIds.includes(Number(tag.id))} onChange={() => toggleTag(tag.id)} />
                          <span>{tag.name}</span>
                          {tag.count || tag.usageCount ? <em>{tag.count || tag.usageCount}</em> : null}
                        </label>
                      ))}
                    </div>
                  </>
                ) : <span className="muted">暂无可选标签</span>}
              </div>
              ) : null}
            </div>
            <label className="semantic-filter-field">
              <span className="semantic-filter-label">匹配方式</span>
              <select value={tagMatch} onChange={event => setTagMatch(event.target.value)}>
                <option value="all">同时命中全部标签</option>
                <option value="any">命中任一标签</option>
              </select>
            </label>
            {selectedTagIds.length ? <button className="link semantic-clear-tags" type="button" onClick={() => setSelectedTagIds([])}>清空</button> : null}
            {selectedTags.length ? (
            <div className="semantic-selected-tags">
              {selectedTags.map(tag => (
                <button className="semantic-tag-token" type="button" key={tag.id} onClick={() => toggleTag(tag.id)} title="点击移除标签">
                  <span>{tag.groupName ? `${tag.groupName} / ` : ''}{tag.name}</span><b>×</b>
                </button>
              ))}
            </div>
            ) : null}
          </div>
          <FormRow label="召回上限">
            <input type="number" min="1" max="30" value={topN} onChange={event => setTopN(Number(event.target.value || 1))} />
          </FormRow>
          <FormRow label="每页数量">
            <input type="number" min="1" max="30" value={pageSize} onChange={event => setPageSize(Number(event.target.value || 1))} />
          </FormRow>
        </div>
        <div className="semantic-query-main">
          <div className="semantic-field-head">
            <label htmlFor="semantic-admin-query">自然语言查询</label>
            {query.trim() ? <span>{query.trim().length} 字</span> : null}
          </div>
          <div className="semantic-query-input-shell">
            <textarea
              id="semantic-admin-query"
              value={query}
              rows={4}
              placeholder="例如：找图里有前后对比、评论关注敏感肌的美白样本，不要母婴内容"
              onChange={event => setQuery(event.target.value)}
            />
          </div>
          <div className="semantic-query-action-row">
            <button className="btn primary" type="button" disabled={!canSearch || loading} onClick={() => runSearch(1)}>{loading ? '查询中' : (query.trim() ? '运行语义查询' : (selectedTagIds.length ? '运行标签筛选' : '查询内容池'))}</button>
          </div>
        </div>
      </div>

      {error ? <div className="banner danger">{error}</div> : null}
      {filtersChanged ? <div className="banner semantic-stale-banner">查询条件已修改，请重新运行查询。</div> : null}

      <div className="semantic-result-layout">
        <section className="table-card semantic-results-card">
          <div className="table-toolbar">
            <div>
              <strong>检索结果</strong>
              <span>{resultCountLabel}</span>
            </div>
            <div className="filterbar">
              <button className="btn ghost sm" type="button" disabled={!result || page <= 1 || loading} onClick={() => runSearch(page - 1)}>上一页</button>
              <button className="btn ghost sm" type="button" disabled={!result?.hasMore || loading} onClick={() => runSearch(page + 1)}>下一页</button>
            </div>
          </div>
          <table className="dtable semantic-result-table">
            <thead>
              <tr>
                <th>样本</th>
                <th>得分</th>
                <th>匹配原因</th>
                <th>tag_signal</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(item => (
                <tr key={item.poolNoteId}>
                  <td>
                    <button className="note-cell note-cell-text" type="button" onClick={() => openResultNoteDetail(item)}>
                      <span>{item.note?.title || `内容池样本 #${item.poolNoteId}`}</span>
                      <em>{item.note?.author || item.note?.note_id || '-'}</em>
                    </button>
                  </td>
                  <td>
                    <div className="semantic-score">
                      <b>{Number(item.match?.score || 0).toFixed(2)}</b>
                      <span>语义 {Number(item.match?.semanticScore || 0).toFixed(2)}</span>
                    </div>
                  </td>
                  <td><ReasonList reasons={item.match?.reasons || []} /></td>
                  <td><TagSignal tags={item.tags || []} /></td>
                  <td><button className="link" type="button" disabled={indexLogLoading} onClick={() => openIndexLog(item.poolNoteId)}>{indexLogLoading ? '加载中' : '索引日志'}</button></td>
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan={5}>
                    <div className="semantic-empty-state">
                      <strong>{loading ? '正在检索内容池' : (result ? '当前条件没有命中样本' : '先运行一次查询')}</strong>
                      <span>{loading ? '正在解析条件并召回样本。' : (result ? '可以放宽标签匹配、减少排除条件，或到向量索引检查样本覆盖。' : '输入一句自然语言，或直接选择标签后查询内容池。')}</span>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </section>

        <aside className="panel semantic-diagnostics-panel">
          <div className="panel-head semantic-summary-head"><h3>查询摘要</h3><span className="hint">{semanticQueryStatusLabel(result?.status)}</span></div>
          <SemanticQuerySummary
            result={result}
            rows={rows}
            query={query}
            tagMatch={tagMatch}
            selectedTagLabel={selectedTagLabel}
            selectedTagCount={selectedTagIds.length}
            semanticQuery={semanticQuery}
            diagnostics={diagnostics}
            intentStatus={intentStatus}
            elapsedLabel={elapsedLabel}
            recallDimensionLabel={recallDimensionLabel}
          />
        </aside>
      </div>

      {indexLogActive ? (
        <Drawer
          title="向量记录处理日志"
          subtitle={indexLogActive.note?.title || indexLogActive.noteId || `样本 #${indexLogActive.poolNoteId}`}
          onClose={() => setIndexLogActive(null)}
          className="semantic-drawer"
        >
          <SemanticIndexLogContent
            active={indexLogActive}
            events={indexLogEvents}
            issueText={latestSemanticIndexIssueText(indexLogActive)}
            onCopyTrace={(traceId) => {
              if (navigator.clipboard?.writeText) navigator.clipboard.writeText(traceId).catch(() => {})
              flash(`已复制追踪编号 ${traceId}`)
            }}
          />
        </Drawer>
      ) : null}
    </section>
  )
}

function SemanticQuerySummary({ result, rows, query, tagMatch, selectedTagLabel, selectedTagCount, semanticQuery, diagnostics, intentStatus, elapsedLabel, recallDimensionLabel }) {
  const logs = buildSemanticQueryLogs({ result, query, tagMatch, selectedTagLabel, selectedTagCount, semanticQuery, diagnostics, intentStatus, rows })
  const positiveText = String(semanticQuery.positiveText || query || '').trim()
  const negativeText = String(semanticQuery.negativeText || '').trim()
  const degradation = (result?.degradationReasons || []).filter(Boolean)
  const recallSummary = formatRecallSourceSummary(diagnostics.recallDimensions)
  const recallDecision = formatRecallDecision({ result, rows, diagnostics })
  if (!result) {
    return (
      <div className="semantic-query-summary-empty">
        <strong>运行后显示查询解读</strong>
        <span>这里会展示系统理解的需求、标签过滤、召回过程和日志信息。</span>
      </div>
    )
  }

  return (
    <div className="semantic-query-summary">
      <div className="semantic-summary-grid">
        <div><span>结果</span><b>{result.resultCount || 0}</b></div>
        <div><span>耗时</span><b>{elapsedLabel}</b></div>
      </div>

      <section className="semantic-query-meaning" aria-labelledby="semantic-query-meaning-title">
        <h4 id="semantic-query-meaning-title">运营解读</h4>
        <dl className="semantic-output-brief">
          <div>
            <dt>要找什么</dt>
            <dd>{positiveText || (result.mode === 'tag_filter' ? '未输入自然语言，仅按标签过滤' : '-')}</dd>
          </div>
          <div>
            <dt>不要什么</dt>
            <dd>{negativeText || '无明确排除条件'}</dd>
          </div>
          <div>
            <dt>标签限制</dt>
            <dd>{selectedTagLabel}{selectedTagCount ? ` · ${tagMatch === 'all' ? '必须同时命中' : '命中任一即可'}` : ''}</dd>
          </div>
          <div>
            <dt>检索方式</dt>
            <dd>{intentStatus}</dd>
          </div>
          <div>
            <dt>召回来源</dt>
            <dd>{recallDecision.basis}</dd>
          </div>
          <div>
            <dt>综合结果</dt>
            <dd>{recallDecision.outcome}</dd>
          </div>
        </dl>
        <SemanticDimensionSignals semanticQuery={semanticQuery} diagnostics={diagnostics} recallDimensionLabel={recallDimensionLabel} />
      </section>

      <section className="semantic-query-log-panel" aria-labelledby="semantic-query-log-title">
        <h4 id="semantic-query-log-title">运行日志</h4>
        <div className="semantic-query-log-list">
          {logs.map(item => (
            <div className={`semantic-query-log-row ${item.tone || ''}`} key={item.title}>
              <span>{item.step}</span>
              <div>
                <b>{item.title}</b>
                <em>{item.detail}</em>
              </div>
            </div>
          ))}
        </div>
      </section>

      <details className="semantic-diagnostics-details">
        <summary>调试详情</summary>
        <div className="semantic-diagnostics-inline">
          <SemanticRuntimeDiagnostic
            diagnostics={diagnostics}
            result={result}
            recallSummary={recallSummary}
            recallDecision={recallDecision}
            degradation={degradation}
          />
          <SemanticLlmDiagnostic llm={diagnostics.llm} intentSource={semanticQuery.intentSource || result.mode} />
        </div>
      </details>
    </div>
  )
}

function SemanticRuntimeDiagnostic({ diagnostics, result, recallSummary, recallDecision, degradation }) {
  const modelSummary = diagnostics.modelSummary
    ? Object.entries(diagnostics.modelSummary).map(([key, value]) => `${key}:${value}`).join(' · ')
    : '-'
  return (
    <section>
      <h4>运行凭证</h4>
      <dl className="semantic-dl">
        <dt>调用方</dt><dd>{diagnostics.caller || '-'}</dd>
        <dt>结果快照</dt><dd>{diagnostics.persistence || '-'}</dd>
        <dt>结果数</dt><dd>{result.resultCount || 0}</dd>
        <dt>召回依据</dt><dd>{recallDecision.basis}</dd>
        <dt>标签信号</dt><dd>{recallSummary.tagText || '未参与召回'}</dd>
        <dt>成本</dt><dd>{diagnostics.cost?.available ? `${diagnostics.cost.estimatedCost} ${diagnostics.cost.currency || ''}` : '本次响应未直接暴露，查看调用审计'}</dd>
        <dt>模型摘要</dt><dd>{modelSummary}</dd>
        <dt>降级</dt><dd>{degradation.join('、') || '无'}</dd>
      </dl>
    </section>
  )
}

function SemanticLlmDiagnostic({ llm, intentSource }) {
  if (!llm) {
    const usedLlm = String(intentSource || '') === 'llm'
    return (
      <section>
        <h4>LLM 输入/输出</h4>
        <div className="semantic-llm-empty">
          {usedLlm
            ? '意图识别结果来自 LLM，但本次响应未返回调用审计；可重新查询或检查后端 semantic_ai_invocation 关联。'
            : '本次响应未返回 LLM 调用审计；标签筛选或内容池浏览通常不会调用 LLM。'}
        </div>
      </section>
    )
  }
  const outputText = String(llm.output?.text || '').trim()
  const outputJson = llm.output?.json && typeof llm.output.json === 'object' ? llm.output.json : {}
  const outputDisplay = Object.keys(outputJson).length ? JSON.stringify(outputJson, null, 2) : outputText
  return (
    <section>
      <h4>LLM 输入/输出</h4>
      <dl className="semantic-dl semantic-llm-meta">
        <dt>节点</dt><dd>{llm.promptNodeKey || '-'}</dd>
        <dt>版本</dt><dd>{llm.promptVersion || '-'}</dd>
        <dt>状态</dt><dd>{llm.status || '-'}</dd>
        <dt>耗时</dt><dd>{llm.latencyMs !== undefined ? `${llm.latencyMs}ms` : '-'}</dd>
        <dt>Trace ID</dt><dd>{llm.traceId || '-'}</dd>
      </dl>
      <div className="semantic-llm-blocks">
        <details>
          <summary>系统提示词</summary>
          <pre>{llm.input?.systemPrompt || '-'}</pre>
        </details>
        <details open>
          <summary>用户提示词</summary>
          <pre>{llm.input?.userPrompt || '-'}</pre>
        </details>
        <details open>
          <summary>输出 JSON</summary>
          <pre>{outputDisplay || '-'}</pre>
        </details>
      </div>
    </section>
  )
}

function SemanticDimensionSignals({ semanticQuery, diagnostics, recallDimensionLabel }) {
  const weights = semanticQuery.dimensionWeights && typeof semanticQuery.dimensionWeights === 'object' ? semanticQuery.dimensionWeights : {}
  const entries = semanticDimensions.map(item => {
    const weight = normalizedWeight(weights[item.value])
    const recall = (diagnostics.recallDimensions || []).find(dimension => dimension.dimension === item.value)
    return { key: item.value, label: item.label, weight, count: Number(recall?.count || 0) }
  })
  const hasSignals = entries.some(item => item.weight > 0 || item.count > 0)
  return (
    <div className="semantic-output-weights">
      <h5>召回侧重点</h5>
      {hasSignals ? entries.map(item => (
        <div className="semantic-output-weight" key={item.key}>
          <span>{item.label}</span>
          <i><b style={{ width: `${Math.max(4, Math.round(item.weight * 100))}%` }} /></i>
          <em>{item.weight > 0 ? `${Math.round(item.weight * 100)}%` : `${item.count} 条`}</em>
        </div>
      )) : <p>{recallDimensionLabel === '-' ? '本次响应未返回维度权重。' : recallDimensionLabel}</p>}
    </div>
  )
}

function buildSemanticQueryLogs({ result, query, tagMatch, selectedTagLabel, selectedTagCount, semanticQuery, diagnostics, intentStatus, rows }) {
  if (!result) return []
  const logs = []
  const recallSummary = formatRecallSourceSummary(diagnostics.recallDimensions)
  const mode = result.mode === 'tag_filter' || semanticQuery.intentSource === 'tag_filter'
    ? '按标签过滤内容池'
    : (query.trim() ? '解析自然语言并召回样本' : '浏览内容池候选样本')
  logs.push({ step: '1', title: '确定查询模式', detail: mode })
  logs.push({
    step: '2',
    title: '理解查询意图',
    detail: intentStatus,
    tone: result.mode === 'tag_filter' || semanticQuery.intentSource === 'tag_filter' ? 'muted' : '',
  })
  logs.push({
    step: '3',
    title: '应用标签条件',
    detail: selectedTagCount ? `${selectedTagLabel} · ${tagMatch === 'all' ? '同时命中全部标签' : '命中任一标签'}` : '不限标签',
  })
  logs.push({
    step: '4',
    title: '召回候选样本',
    detail: recallSummary.logText
      ? recallSummary.logText
      : '本次响应未返回分维度召回数',
  })
  logs.push({
    step: '5',
    title: '整理查询结果',
    detail: `${result.resultCount || 0} 条命中${result.hasMore ? '，还有下一页' : ''}${rows.length ? `，当前页展示 ${rows.length} 条` : ''}`,
    tone: result.resultCount ? 'ok' : 'muted',
  })
  if ((result.degradationReasons || []).length) {
    logs.push({ step: '!', title: '降级提醒', detail: result.degradationReasons.join('、'), tone: 'warn' })
  }
  return logs
}

function formatRecallDecision({ result, rows, diagnostics }) {
  const summary = formatRecallSourceSummary(diagnostics.recallDimensions)
  const total = Number(result?.resultCount || 0)
  const pageCount = rows.length
  const basisParts = []
  if (summary.sourceText) basisParts.push(summary.sourceText)
  if (summary.tagText) basisParts.push(summary.tagText)
  const basis = basisParts.length
    ? basisParts.join('；')
    : (result?.mode === 'tag_filter' ? '仅按内容池和标签条件筛选' : '本次响应未返回分维度召回统计')
  const tagTail = summary.tagText ? `，其中${summary.tagText}` : ''
  const outcome = total
    ? `${total} 条样本进入结果集，当前页展示 ${pageCount} 条${tagTail}。`
    : `没有样本进入结果集；${summary.tagText ? `${summary.tagText}，但综合条件未通过。` : '可放宽标签或减少排除条件。'}`
  return { basis, outcome }
}

function formatRecallSourceSummary(dimensions = []) {
  const known = []
  const tagSignals = []
  const other = []
  ;(dimensions || []).forEach(item => {
    const key = String(item?.dimension || '').trim()
    const count = Number(item?.count || 0)
    if (!key) return
    if (['tag', 'tags', 'tag_signal'].includes(key)) {
      tagSignals.push({ label: '标签命中', count })
      return
    }
    const label = semanticDimensionLabels[key]
    if (label) {
      known.push({ label, count })
      return
    }
    other.push({ label: '其他来源', count })
  })
  const sourceText = [...known, ...other].map(item => `${item.label} ${item.count} 条`).join('、')
  const tagText = tagSignals.map(item => `${item.label} ${item.count} 条`).join('、')
  return {
    sourceText,
    tagText,
    logText: [sourceText, tagText].filter(Boolean).join('；'),
  }
}

function semanticQueryStatusLabel(status) {
  const labels = {
    ready: '已完成',
    running: '查询中',
    failed: '查询失败',
    partial: '部分完成',
  }
  return labels[status] || '未查询'
}

function normalizedWeight(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return 0
  return Math.max(0, Math.min(1, number))
}

function SemanticIndexWorkspace({ pool, flash }) {
  const [vectors, setVectors] = useState([])
  const [summary, setSummary] = useState(null)
  const [events, setEvents] = useState([])
  const [active, setActive] = useState(null)
  const [imageSemanticTarget, setImageSemanticTarget] = useState(null)
  const [status, setStatus] = useState('')
  const [dimension, setDimension] = useState('')
  const [dimensionStatus, setDimensionStatus] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async (options = {}) => {
    if (!pool?.id) return false
    const notify = Boolean(options?.notify)
    setLoading(true)
    setError('')
    try {
      const params = { poolId: pool.id, pageSize: 80 }
      if (status && !['issues', 'searchable_any'].includes(status)) params.status = status
      if (status === 'issues') params.statusGroup = 'issues'
      if (dimension) params.dimension = dimension
      if (dimensionStatus) params.dimensionStatus = dimensionStatus
      const res = await api.get('/semantic/admin/note-vectors', { params })
      const data = res.data?.data || res.data || {}
      setVectors(data.data || [])
      setSummary(data.summary || null)
      if (notify) flash('索引状态已刷新')
      return true
    } catch (e) {
      const message = e.response?.data?.message || e.message || '向量索引加载失败'
      setError(message)
      flash(message)
      return false
    } finally {
      setLoading(false)
    }
  }, [pool?.id, status, dimension, dimensionStatus, flash])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!active?.poolNoteId) {
      setEvents([])
      return
    }
    api.get('/semantic/admin/events', { params: { poolNoteId: active.poolNoteId, limit: 80 } })
      .then(res => setEvents((res.data?.data?.data || res.data?.data || [])))
      .catch(() => setEvents([]))
  }, [active?.poolNoteId])

  async function retry(poolNoteId, targetDimension) {
    try {
      const res = await api.post(`/semantic/admin/note-vectors/${poolNoteId}/retry`, { dimension: targetDimension, operator: 'tuber-admin' })
      const updated = res.data?.data || res.data
      setActive(current => Number(current?.poolNoteId || 0) === Number(poolNoteId) ? updated : current)
      await load()
      flash(`${semanticDimensionLabels[targetDimension]}已提交重试`)
    } catch (e) {
      flash(e.response?.data?.message || e.message || '重试失败')
    }
  }

  async function skip(poolNoteId, targetDimension) {
    const reason = window.prompt(`填写跳过「${semanticDimensionLabels[targetDimension]}」的原因`)
    if (!reason?.trim()) return
    try {
      const res = await api.post(`/semantic/admin/note-vectors/${poolNoteId}/skip`, { dimension: targetDimension, reason, operator: 'tuber-admin' })
      const updated = res.data?.data || res.data
      setActive(current => Number(current?.poolNoteId || 0) === Number(poolNoteId) ? updated : current)
      await load()
      flash(`${semanticDimensionLabels[targetDimension]}已标记跳过`)
    } catch (e) {
      flash(e.response?.data?.message || e.message || '标记跳过失败')
    }
  }

  const totalVectors = Number(summary?.total || vectors.length || 0)
  const overallSummary = summary?.overall || {}
  const dimensionSummary = summary?.dimensions || {}
  const searchableTotal = Number(overallSummary.searchable || 0) + Number(overallSummary.partial_searchable || 0)
  const hasIndexIssue = item => {
    const lifecycleIssue = ['manual_required', 'retry_scheduled', 'stale'].includes(String(item.lifecycleStatus || ''))
    const imageIssue = item.imageStatus?.status && !['ready', 'skipped'].includes(String(item.imageStatus.status))
    const dimensionIssue = semanticDimensions.some(dim => ['stale', 'retry_scheduled', 'manual_required', 'failed'].includes(String(item.dimensionStatuses?.[dim.value]?.status || '')))
    return lifecycleIssue || imageIssue || dimensionIssue
  }
  const rowIssueTotal = vectors.filter(hasIndexIssue).length
  const issueTotal = Number(summary?.issues ?? rowIssueTotal)
  const statusFilterLabel = status === 'issues'
    ? '待处理问题'
    : status === 'searchable_any'
      ? '可检索样本'
      : status
        ? semanticStatusLabels[status] || status
        : dimension
          ? `${semanticDimensionLabels[dimension]} · ${dimensionStatus ? semanticDimensionStatusGroupLabels[dimensionStatus] || semanticStatusLabels[dimensionStatus] || dimensionStatus : '全部状态'}`
          : '全部索引明细'
  const dimensionStatusOptions = useMemo(() => {
    if (!dimension) return []
    const stats = dimensionSummary[dimension] || null
    const covered = stats
      ? Number(stats.searchable || 0)
      : vectors.filter(item => String(item.dimensionStatuses?.[dimension]?.status || '') === 'searchable').length
    const notSearchable = Math.max(0, totalVectors - covered)
    const groupOptions = [
      {
        value: semanticDimensionStatusNotSearchable,
        label: semanticDimensionStatusGroupLabels[semanticDimensionStatusNotSearchable],
        count: notSearchable,
      },
    ]
    const statusOptions = semanticDimensionStatusFilters
      .map(value => {
        const count = stats
          ? Number(stats[value] || 0)
          : vectors.filter(item => String(item.dimensionStatuses?.[dimension]?.status || '') === value).length
        return { value, label: semanticStatusLabels[value] || value, count }
      })
      .filter(option => option.count > 0 || option.value === dimensionStatus)
    return groupOptions.concat(statusOptions)
      .filter(option => option.count > 0 || option.value === dimensionStatus)
  }, [dimension, dimensionStatus, dimensionSummary, totalVectors, vectors])
  const displayVectors = status === 'searchable_any'
      ? vectors.filter(item => ['searchable', 'partial_searchable'].includes(String(item.lifecycleStatus || '')))
      : vectors

  function dimensionCoveredCount(dimensionKey) {
    const stats = dimensionSummary[dimensionKey]
    if (stats) return Number(stats.searchable || 0)
    return vectors.filter(item => String(item.dimensionStatuses?.[dimensionKey]?.status || '') === 'searchable').length
  }

  function dimensionSevereCount(dimensionKey) {
    const stats = dimensionSummary[dimensionKey]
    if (stats) {
      return ['stale', 'retry_scheduled', 'manual_required'].reduce((total, key) => total + Number(stats[key] || 0), 0)
    }
    return vectors.filter(item => ['stale', 'retry_scheduled', 'manual_required'].includes(String(item.dimensionStatuses?.[dimensionKey]?.status || ''))).length
  }

  function dimensionStats(dimensionKey) {
    const covered = dimensionCoveredCount(dimensionKey)
    const notCovered = Math.max(0, totalVectors - covered)
    return { notCovered, covered }
  }

  function showAllIndexRows() {
    setStatus('')
    setDimension('')
    setDimensionStatus('')
  }

  function showStatusRows(nextStatus) {
    setStatus(nextStatus)
    setDimension('')
    setDimensionStatus('')
  }

  function showIssueRows() {
    showStatusRows('issues')
  }

  function showDimensionRows(nextDimension, nextDimensionStatus = '') {
    setStatus('')
    setDimension(nextDimension)
    setDimensionStatus(nextDimensionStatus)
  }

  function vectorDetailNote(item) {
    return {
      ...(item.note || {}),
      id: item.note?.id || item.noteRowId || item.poolNoteId,
      note_id: item.note?.note_id || item.noteId || item.noteKey || '',
      noteId: item.note?.note_id || item.noteId || item.noteKey || '',
      noteKey: item.noteKey || item.note?.noteKey || '',
      title: item.note?.title || `样本 #${item.poolNoteId}`,
      poolId: item.poolId,
      poolStatus: item.reviewStatus || '',
      reviewRemark: item.reviewRemark || '',
    }
  }

  const vectorNavigationNotes = displayVectors.map(vectorDetailNote)

  return (
    <section className="semantic-index-workspace">
      {error ? <div className="banner danger">{error}</div> : null}
      <div className="semantic-health-grid">
        <SemanticHealthCard label="全部样本" value={totalVectors} hint="查看全部索引明细" active={!status && !dimension} onClick={showAllIndexRows} />
        <SemanticHealthCard label="可检索样本" value={`${searchableTotal} / ${totalVectors}`} hint="至少一个核心维度已完成" active={status === 'searchable_any'} onClick={() => showStatusRows('searchable_any')} />
        {semanticDimensions.map(dim => {
          const stats = dimensionStats(dim.value)
          return (
            <SemanticHealthCard
              key={dim.value}
              label={dim.label}
              value={stats.notCovered}
              hint={`${semanticDimensionUi[dim.value]?.cardHint || '待处理'} · ${stats.covered}/${totalVectors} 已覆盖`}
              active={dimension === dim.value}
              onClick={() => showDimensionRows(dim.value, semanticDimensionStatusNotSearchable)}
            />
          )
        })}
        <SemanticHealthCard label="待处理问题" value={issueTotal} hint="失败、待人工、超时等待" danger active={status === 'issues'} onClick={showIssueRows} />
      </div>

      <div className="semantic-index-filter-row">
        <div className="semantic-index-filter-main">
          <span>当前查看：{statusFilterLabel}</span>
          {dimension ? (
            <label>
              <span>状态</span>
              <select value={dimensionStatus} onChange={event => setDimensionStatus(event.target.value)}>
                <option value="">全部状态</option>
                {dimensionStatusOptions.map(option => (
                  <option value={option.value} key={option.value}>{option.label}（{option.count}）</option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        <button className="btn ghost semantic-index-refresh" type="button" disabled={loading} onClick={() => load({ notify: true })}>{loading ? '刷新中' : '刷新索引状态'}</button>
      </div>

      <section className="table-card semantic-vector-card">
        <div className="table-scroll semantic-vector-scroll">
          <table className="dtable semantic-vector-table">
            <thead>
              <tr>
                <th>笔记</th>
                <th>可检索</th>
                {semanticDimensions.map(item => <th key={item.value}>{item.label}</th>)}
                <th>最近问题</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {displayVectors.map((item, index) => (
                <tr key={item.poolNoteId} className={active?.poolNoteId === item.poolNoteId ? 'active-row' : ''}>
                  <td>
                    <button className="note-cell note-cell-text" type="button" onClick={() => openNoteDetail(vectorNavigationNotes[index], { notes: vectorNavigationNotes })}>
                      <span>{item.note?.title || `样本 #${item.poolNoteId}`}</span>
                      <em>样本 #{item.poolNoteId}</em>
                    </button>
                  </td>
                  <td><SemanticOverallStatus status={item.lifecycleStatus} /></td>
                  {semanticDimensions.map(dim => (
                    <td key={dim.value}>
                      <SemanticDimensionCell
                        value={item.dimensionStatuses?.[dim.value]}
                        dimension={dim.value}
                        poolNoteId={item.poolNoteId}
                        imageStatus={dim.value === 'image' ? item.imageStatus : null}
                        onRetry={retry}
                        onViewImage={dim.value === 'image' && hasImageSemanticModalContent(item) ? () => setImageSemanticTarget(item) : null}
                      />
                    </td>
                  ))}
                  <td>{latestSemanticIndexIssueText(item)}</td>
                  <td>
                    <div className="semantic-vector-actions">
                      <button className="link" type="button" onClick={() => setActive(item)}>日志</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!displayVectors.length ? <tr><td colSpan={8}><Empty text={loading ? '加载中' : '暂无索引记录'} /></td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>

      {active ? (
        <Drawer
          title="向量记录处理日志"
          subtitle={active.note?.title || active.noteId || `样本 #${active.poolNoteId}`}
          onClose={() => setActive(null)}
          className="semantic-drawer"
        >
          <SemanticIndexLogContent
            active={active}
            events={events}
            issueText={latestSemanticIndexIssueText(active)}
            onCopyTrace={(traceId) => {
              if (navigator.clipboard?.writeText) navigator.clipboard.writeText(traceId).catch(() => {})
              flash(`已复制追踪编号 ${traceId}`)
            }}
          />
        </Drawer>
      ) : null}

      {imageSemanticTarget ? (
        <ImageSemanticModal
          vector={imageSemanticTarget}
          onClose={() => setImageSemanticTarget(null)}
        />
      ) : null}

    </section>
  )
}

function latestSemanticIndexIssueText(item = {}) {
  const imageIssue = item.imageStatus?.status && !['ready', 'skipped'].includes(item.imageStatus.status)
    ? item.imageStatus.lastError || semanticStatusLabels[item.imageStatus.status] || item.imageStatus.status
    : ''
  if (imageIssue) return imageIssue
  const dimensionIssue = semanticDimensions
    .map(dim => ({ label: dim.label, value: item.dimensionStatuses?.[dim.value] }))
    .find(dim => ['stale', 'retry_scheduled', 'manual_required', 'failed'].includes(String(dim.value?.status || '')))
  if (dimensionIssue) {
    return `${dimensionIssue.label} · ${dimensionIssue.value?.lastError || semanticStatusLabels[dimensionIssue.value?.status] || dimensionIssue.value?.status}`
  }
  return '-'
}

function SemanticIndexLogContent({ active, events, issueText, onCopyTrace }) {
  const logEvents = buildSemanticLogEvents(active, events)
  const traceId = logEvents.find(event => event.traceId)?.traceId || `idx_pool_note_${active.poolNoteId}`
  return (
    <>
      <section className="drawer-section">
        <h3>事件处理链路</h3>
        <dl className="semantic-dl semantic-log-summary">
          <dt>当前问题</dt><dd>{issueText === '-' ? '当前没有待处理问题' : issueText}</dd>
          <dt>事件数量</dt><dd>{logEvents.length} 个</dd>
          <dt>排查方式</dt><dd>逐个展开事件，先确认维度和时间，再看模型调用输入、模型输出和日志凭证。</dd>
        </dl>
      </section>
      <div className="event-stack">
        {logEvents.map((event, index) => <SemanticLogEventCard event={event} defaultOpen={index === 0} key={event.key} />)}
      </div>
      <button className="btn ghost" type="button" onClick={() => onCopyTrace(traceId)}>复制追踪编号</button>
    </>
  )
}

function SemanticLogEventCard({ event, defaultOpen = false }) {
  return (
    <details className="event-card" open={defaultOpen}>
      <summary>
        <span className="event-time">{event.time}</span>
        <span className="event-dimension">{event.dimensionLabel}</span>
        <strong>{event.title}</strong>
        <span className={`event-status ${event.tone || ''}`}>{event.status}</span>
      </summary>
      <section className="event-body">
        <div className="event-result"><span>节点结论</span><strong>{event.result}</strong></div>
        <details className="event-subcard" open>
          <summary>事件上下文</summary>
          <SemanticEventDl items={event.context} />
        </details>
        {event.llm ? (
          <details className="event-subcard" open>
            <summary>模型调用输入 / 输出</summary>
            <p className="node-meta">{event.llm.meta}</p>
            <SemanticEventLlmBlocks llm={event.llm} />
          </details>
        ) : null}
        <details className="event-subcard" open>
          <summary>日志凭证</summary>
          <SemanticEventDl items={event.credential} />
        </details>
      </section>
    </details>
  )
}

function SemanticEventLlmBlocks({ llm }) {
  const blocks = [
    { title: '系统提示词', value: llm.systemPrompt },
    { title: '用户提示词', value: llm.userPrompt || llm.input },
    { title: '图片审计', value: llm.imageAudit },
    { title: '输出 JSON', value: llm.outputJson || llm.output },
  ].filter(item => item.title !== '图片审计' || String(item.value || '').trim()).map(item => ({
    ...item,
    value: String(item.value || '').trim() || '-',
  }))
  return (
    <div className="semantic-llm-blocks event-llm-blocks">
      {blocks.map(item => (
        <details key={item.title}>
          <summary>{item.title}</summary>
          <pre>{item.value}</pre>
        </details>
      ))}
    </div>
  )
}

function SemanticEventDl({ items }) {
  return (
    <dl>
      {items.map(item => (
        <React.Fragment key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </React.Fragment>
      ))}
    </dl>
  )
}

function buildSemanticLogEvents(active, events = []) {
  const rows = [...(events || [])].sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0) || Number(b.id || 0) - Number(a.id || 0))
  if (rows.length) return rows.map(event => buildSemanticLogEvent(active, event))
  return [buildSemanticSnapshotEvent(active)]
}

function buildSemanticLogEvent(active, event) {
  const metadata = event.metadata || {}
  const dimensionLabel = semanticDimensionLabels[event.dimensionKey] || event.dimensionKey || '整体索引'
  const from = semanticStatusLabels[event.fromStatus] || event.fromStatus || '-'
  const to = semanticStatusLabels[event.toStatus] || event.toStatus || '-'
  const failed = Boolean(event.errorCode || String(event.toStatus || '').includes('failed') || String(event.toStatus || '') === 'manual_required')
  const title = semanticEventTypeLabels[event.eventType] || event.eventType || '索引事件'
  const providerMeta = [event.model, metadata.promptConfigId || metadata.promptVersion || metadata.promptKey].filter(Boolean).join(' · ')
  const hasLlm = Boolean(event.model || event.provider || metadata.promptConfigId || metadata.promptVersion || metadata.promptKey || metadata.input || metadata.output || metadata.inputPreview || metadata.outputPreview || metadata.auditJson)
  const llmInput = semanticEventInputPreview(active, event, metadata)
  const llmOutput = semanticEventOutputPreview(event, metadata)
  const renderedPrompt = metadata.renderedPrompt && typeof metadata.renderedPrompt === 'object' ? metadata.renderedPrompt : {}
  const outputJson = metadata.outputJson && typeof metadata.outputJson === 'object' && Object.keys(metadata.outputJson).length
    ? JSON.stringify(metadata.outputJson, null, 2)
    : ''
  const auditJson = metadata.auditJson && typeof metadata.auditJson === 'object' && Object.keys(metadata.auditJson).length
    ? JSON.stringify(metadata.auditJson, null, 2)
    : ''
  const errorText = formatSemanticError(event.errorCode, event.errorMessage)
  return {
    key: event.id || `${event.eventType}-${event.createdAt}`,
    traceId: event.traceId,
    time: formatEventTime(event.createdAt),
    dimensionLabel,
    title,
    status: failed ? '待处理' : (semanticStatusLabels[event.toStatus] || '完成'),
    tone: failed ? 'warning' : '',
    result: event.errorCode
      ? errorText
      : `${dimensionLabel} ${from} → ${to}`,
    context: [
      { label: '样本', value: `内容池样本 #${active.poolNoteId} / ${active.note?.title || active.noteId || '-'}` },
      { label: '维度', value: dimensionLabel },
      { label: '时间', value: formatEventDateTime(event.createdAt) },
      { label: '事件类型', value: event.eventType || '-' },
      { label: '状态变化', value: `${from} → ${to}` },
    ],
    llm: hasLlm ? {
      meta: [dimensionLabel, providerMeta || '调用审计未暴露完整模型信息', event.providerRequestId ? `request ${event.providerRequestId}` : ''].filter(Boolean).join(' · '),
      systemPrompt: renderedPrompt.systemPrompt || metadata.systemPrompt || '',
      userPrompt: renderedPrompt.userPrompt || metadata.userPrompt || metadata.promptInput || '',
      input: llmInput,
      output: llmOutput,
      imageAudit: auditJson,
      outputJson: outputJson || metadata.outputText || metadata.output || metadata.outputPreview || '',
    } : null,
    credential: buildSemanticCredentialRows(event, metadata),
  }
}

function semanticEventInputPreview(active, event, metadata) {
  if (metadata.input || metadata.inputPreview || metadata.promptInput) return metadata.input || metadata.inputPreview || metadata.promptInput
  if (event.dimensionKey === 'image') {
    const parts = [
      metadata.mediaHash ? `图片指纹：${metadata.mediaHash}` : '',
      metadata.analysisId ? `调用记录 #${metadata.analysisId}` : '',
      metadata.promptConfigId ? `提示词版本 #${metadata.promptConfigId}` : '',
    ].filter(Boolean)
    return parts.length ? `未关联到本次调用的完整提示词；${parts.join(' · ')}` : '图片语义提取任务未记录完整提示词。'
  }
  return active.dimensionStatuses?.[event.dimensionKey]?.textPreview || '本事件未记录模型输入。'
}

function semanticEventOutputPreview(event, metadata) {
  if (metadata.output || metadata.outputPreview || metadata.modelOutput) return metadata.output || metadata.outputPreview || metadata.modelOutput
  if (event.dimensionKey === 'image') {
    if (event.errorMessage) return `图片语义提取失败：${event.errorMessage}`
    const count = metadata.successfulCount ?? metadata.selectedCount ?? metadata.imageCount
    if (count !== undefined) return `图片语义提取完成，成功图片 ${count} 张。`
    if (event.eventType === 'IMAGE_ANALYSIS_CLAIMED') return '已领取图片语义提取任务，等待 provider 返回。'
  }
  return event.errorMessage || '本事件未记录模型输出。'
}

function buildSemanticSnapshotEvent(active) {
  return {
    key: `snapshot-${active.poolNoteId}`,
    traceId: `idx_pool_note_${active.poolNoteId}`,
    time: '当前',
    title: '当前索引状态快照',
    status: semanticStatusLabels[active.lifecycleStatus] || active.lifecycleStatus || '待处理',
    result: `整体状态：${semanticStatusLabels[active.lifecycleStatus] || active.lifecycleStatus || '-'}`,
    dimensionLabel: '整体索引',
    context: [
      { label: '样本', value: `内容池样本 #${active.poolNoteId} / ${active.note?.title || active.noteId || '-'}` },
      { label: 'noteRowId', value: active.noteRowId || '-' },
      { label: '图片产物', value: active.imageStatus ? `${semanticStatusLabels[active.imageStatus.status] || active.imageStatus.status} · ${active.imageStatus.lastError || '无错误'}` : '-' },
    ],
    llm: null,
    credential: semanticDimensions.map(dim => ({
      label: dim.label,
      value: describeDimensionStatus(active.dimensionStatuses?.[dim.value]),
    })).concat([{ label: 'tag_signal', value: active.tagSignal ? '已记录标签信号' : '暂无标签信号' }]),
  }
}

function buildSemanticCredentialRows(event, metadata) {
  const rows = [
    { label: 'trace_id', value: event.traceId || '-' },
    { label: 'event_id', value: event.id || '-' },
    { label: 'provider', value: event.provider || '-' },
    { label: 'model', value: event.model || '-' },
    { label: 'request_id', value: event.providerRequestId || '-' },
    { label: 'operator', value: event.operator || metadata.source || '-' },
    { label: 'reason', value: event.reason || metadata.reason || '-' },
  ]
  if (metadata.sourceHash) rows.push({ label: 'source_hash', value: metadata.sourceHash })
  if (metadata.inputLength !== undefined) rows.push({ label: 'input_length', value: metadata.inputLength })
  if (metadata.nextRetryAt) rows.push({ label: '下次重试', value: formatShortDate(metadata.nextRetryAt) })
  if (metadata.leaseVersion) rows.push({ label: 'lease_version', value: metadata.leaseVersion })
  if (event.errorCode) rows.push({ label: '错误', value: formatSemanticError(event.errorCode, event.errorMessage) })
  return rows
}

function formatSemanticError(code, message = '') {
  if (!code) return message || '-'
  const label = semanticErrorLabels[code] || code
  return message && message !== code ? `${label}（${message}）` : label
}

function describeDimensionStatus(value) {
  if (!value) return '暂无记录'
  const status = semanticStatusLabels[value.status] || value.status || '未知'
  const current = value.current ? '，当前版本' : ''
  const preview = value.textPreview ? `，${value.textPreview}` : ''
  const error = value.lastError ? `，${value.lastError}` : ''
  return `${status}${current}${preview}${error}`
}

function formatEventTime(value) {
  if (!value) return '-'
  const raw = String(value)
  const time = raw.length === 10 ? Number(raw) * 1000 : Number(raw)
  const date = Number.isFinite(time) ? new Date(time) : new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleTimeString('zh-CN', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function formatEventDateTime(value) {
  if (!value) return '-'
  const raw = String(value)
  const time = raw.length === 10 ? Number(raw) * 1000 : Number(raw)
  const date = Number.isFinite(time) ? new Date(time) : new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('zh-CN', { hour12: false })
}

function SemanticHealthCard({ label, value, hint, active = false, danger = false, onClick }) {
  const className = `semantic-health-card ${active ? 'active' : ''} ${danger ? 'danger' : ''}`
  if (onClick) {
    return <button className={className} type="button" onClick={onClick}><span>{label}</span><b>{value}</b><em>{hint}</em></button>
  }
  return <div className={className}><span>{label}</span><b>{value}</b><em>{hint}</em></div>
}

function DimensionStatusBadge({ value }) {
  const status = value?.status || 'source_pending'
  return <Pill tone={statusTone(status)}>{semanticStatusLabels[status] || status}{value?.current ? ' · 当前' : ''}</Pill>
}

function SemanticOverallStatus({ status }) {
  const map = {
    searchable: ['已覆盖', 'covered'],
    partial_searchable: ['部分可检索', 'partial'],
    stale: ['待更新', 'pending'],
    retry_scheduled: ['待处理', 'pending'],
    manual_required: ['需处理', 'manual'],
    pending: ['待更新', 'pending'],
    disabled: ['不参与', 'covered'],
    skipped: ['不参与', 'covered'],
  }
  const [label, tone] = map[status] || [semanticStatusLabels[status] || status || '待更新', 'pending']
  return <span className={`semantic-status-chip ${tone}`}>{label}</span>
}

function semanticDimensionPresentation(value, dimension, imageStatus) {
  const status = String(value?.status || 'source_pending')
  const config = semanticDimensionUi[dimension] || {}
  if (dimension === 'image' && imageStatus && ['failed', 'manual_required', 'retry_scheduled'].includes(String(imageStatus.status || ''))) {
    return {
      label: imageStatus.status === 'failed' ? '待人工' : semanticStatusLabels[imageStatus.status] || '待人工',
      tone: 'manual',
      hint: imageStatus.lastError || config.pendingHint || '图片分析节点',
      action: config.action,
      actionable: true,
      skip: true,
    }
  }
  if (status === 'searchable') {
    return { label: '已覆盖', tone: 'covered', hint: config.coveredHint || '已覆盖', actionable: false }
  }
  if (['disabled', 'skipped'].includes(status)) {
    return { label: config.inactiveLabel || '不参与', tone: 'covered', hint: config.inactiveHint || '不参与检索', actionable: false }
  }
  if (dimension === 'comment' && ['source_pending', 'disabled'].includes(status)) {
    return { label: '不参与', tone: 'covered', hint: config.inactiveHint || '不参与检索', actionable: false }
  }
  if (status === 'failed' || (dimension === 'comment' && ['retry_scheduled', 'manual_required'].includes(status) && value?.lastError)) {
    const commentFailed = dimension === 'comment' && value?.lastError
    return { label: status === 'failed' || commentFailed ? 'failed' : '待人工', tone: status === 'failed' || commentFailed ? 'failed' : 'manual', hint: value?.lastError || config.pendingHint || '处理节点', action: config.action, actionable: true, skip: status !== 'failed' && !commentFailed }
  }
  if (status === 'manual_required') {
    return { label: '待人工', tone: 'manual', hint: value?.lastError || config.pendingHint || '处理节点', action: config.action, actionable: true, skip: true }
  }
  if (status === 'retry_scheduled') {
    return { label: '等待中', tone: 'pending', hint: value?.lastError || config.pendingHint || '等待处理', action: config.action, actionable: true }
  }
  if (['stale', 'text_ready', 'embedding', 'source_pending', 'pending'].includes(status)) {
    return { label: '待更新', tone: 'pending', hint: config.pendingHint || '等待更新', action: config.action, actionable: true }
  }
  return { label: semanticStatusLabels[status] || status, tone: 'pending', hint: config.pendingHint || '-', action: config.action, actionable: true }
}

function SemanticDimensionCell({ value, dimension, poolNoteId, imageStatus, onRetry, onViewImage }) {
  const presentation = semanticDimensionPresentation(value, dimension, imageStatus)
  return (
    <div className="semantic-dimension-mini">
      <span className={`semantic-status-bar ${presentation.tone}`}>{presentation.label}</span>
      <small>{presentation.hint}</small>
      {onViewImage ? (
        <span>
          <button className="mini-action" type="button" onClick={onViewImage}>{imageSemanticActionLabel(imageStatus)}</button>
        </span>
      ) : null}
      {presentation.actionable ? (
        <span>
          <button className="mini-action" type="button" onClick={() => onRetry(poolNoteId, dimension)}>{presentation.action || '重试'}</button>
        </span>
      ) : null}
    </div>
  )
}

function ImageSemanticModal({ vector, onClose }) {
  const status = vector?.imageStatus || {}
  const analysis = status.analysis && typeof status.analysis === 'object' ? status.analysis : {}
  const items = imageSemanticItems(analysis)
  const searchText = String(status.searchText || analysis.searchText || '').trim()
  const statusText = semanticStatusLabels[status.status] || status.status || '未创建'
  const dimensionStatus = vector?.dimensionStatuses?.image?.status || ''
  const dimensionText = semanticStatusLabels[dimensionStatus] || dimensionStatus || '-'
  const modelMeta = [status.provider, status.model].filter(Boolean).join(' · ') || '-'
  const llm = status.llm && typeof status.llm === 'object' ? status.llm : null
  return (
    <Modal title="图片语义结果" onClose={onClose} className="image-semantic-modal">
      <div className="image-semantic-stack">
        <ImageSemanticCard title="图片语义摘要" meta={searchText ? '已生成可检索摘要' : '暂无摘要'} defaultOpen>
          <p className="image-semantic-summary">{searchText || '暂无可检索图片语义。'}</p>
        </ImageSemanticCard>

        <ImageSemanticCard title="检索状态" meta={`${statusText} · ${dimensionText}`}>
          <dl className="semantic-dl image-semantic-dl">
            <dt>分析状态</dt><dd>{statusText}</dd>
            <dt>图片向量</dt><dd>{dimensionText}</dd>
            <dt>图片数量</dt><dd>{Number(status.imageCount || analysis.selectedCount || items.length || 0)}</dd>
            <dt>分析时间</dt><dd>{formatEventDateTime(status.analyzedAt || status.updatedAt)}</dd>
            <dt>模型来源</dt><dd>{modelMeta}</dd>
            <dt>Prompt 版本</dt><dd>{status.promptVersion || '-'}</dd>
            <dt>失败原因</dt><dd>{status.lastError || '-'}</dd>
          </dl>
        </ImageSemanticCard>

        <ImageSemanticCard title="逐图语义" meta={items.length ? `${items.length} 张图片` : '暂无明细'}>
          {items.length ? (
            <div className="image-semantic-items">
              {items.map((item, index) => (
                <article className="image-semantic-item" key={`${item.index ?? index}-${item.url || index}`}>
                  <b>#{Number.isFinite(Number(item.index)) ? Number(item.index) + 1 : index + 1}</b>
                  <div>
                    <p>{String(item.summary || item.searchText || item.error || '').trim() || '未返回逐图摘要。'}</p>
                    {item.url ? <code>{item.url}</code> : null}
                  </div>
                </article>
              ))}
            </div>
          ) : <p className="muted">暂无逐图明细。</p>}
        </ImageSemanticCard>

        <ImageSemanticCard title="LLM 输入/输出" meta={llm ? `${llm.promptNodeKey || 'image_analyze'} · ${llm.promptVersion || '-'}` : '未关联调用审计'}>
          <ImageSemanticLlmDiagnostic llm={llm} />
        </ImageSemanticCard>
      </div>
    </Modal>
  )
}

function ImageSemanticCard({ title, meta, defaultOpen = false, children }) {
  return (
    <details className="image-semantic-card" open={defaultOpen}>
      <summary>
        <span>
          <h3>{title}</h3>
          {meta ? <small>{meta}</small> : null}
        </span>
      </summary>
      <div className="image-semantic-card-body">{children}</div>
    </details>
  )
}

function ImageSemanticLlmDiagnostic({ llm }) {
  if (!llm) {
    return (
      <p className="image-semantic-empty">
        本次图片语义结果未关联到 LLM 调用审计。可在处理日志中按图片分析事件继续排查。
      </p>
    )
  }
  const outputText = String(llm.output?.text || '').trim()
  const outputJson = llm.output?.json && typeof llm.output.json === 'object' && Object.keys(llm.output.json).length
    ? JSON.stringify(llm.output.json, null, 2)
    : ''
  const auditJson = llm.audit && typeof llm.audit === 'object' && Object.keys(llm.audit).length
    ? JSON.stringify(llm.audit, null, 2)
    : ''
  const blocks = [
    { title: '系统提示词', value: llm.input?.systemPrompt },
    { title: '用户提示词', value: llm.input?.userPrompt },
    { title: '输出 JSON', value: outputJson || outputText },
    { title: '调用审计', value: auditJson },
  ].filter(item => item.title !== '调用审计' || String(item.value || '').trim()).map(item => ({
    ...item,
    value: String(item.value || '').trim() || '-',
  }))
  return (
    <div className="image-semantic-llm">
      <dl className="semantic-dl image-semantic-dl">
        <dt>节点</dt><dd>{llm.promptNodeKey || '-'}</dd>
        <dt>版本</dt><dd>{llm.promptVersion || '-'}</dd>
        <dt>状态</dt><dd>{llm.status || '-'}</dd>
        <dt>耗时</dt><dd>{llm.latencyMs !== undefined ? `${llm.latencyMs}ms` : '-'}</dd>
        <dt>Trace ID</dt><dd>{llm.traceId || '-'}</dd>
      </dl>
      <div className="semantic-llm-blocks image-semantic-llm-blocks">
        {blocks.map((item, index) => (
          <details key={item.title} open={index > 0 && index < 3}>
            <summary>{item.title}</summary>
            <pre>{item.value}</pre>
          </details>
        ))}
      </div>
    </div>
  )
}

function imageSemanticActionLabel(imageStatus) {
  const status = String(imageStatus?.status || '')
  if (status === 'ready') return '查看语义'
  if (['failed', 'manual_required'].includes(status)) return '查看原因'
  if (status === 'skipped') return '查看状态'
  return '查看'
}

function hasImageSemanticModalContent(vector) {
  const status = vector?.imageStatus
  if (!status || typeof status !== 'object') return false
  const analysis = status.analysis && typeof status.analysis === 'object' ? status.analysis : {}
  const llm = status.llm && typeof status.llm === 'object' && Object.keys(status.llm).length ? status.llm : null
  const searchText = String(status.searchText || analysis.searchText || '').trim()
  const items = imageSemanticItems(analysis)
  const imageCount = Number(status.imageCount || analysis.selectedCount || items.length || 0)
  const hasDiagnostic = [
    status.lastError,
    status.promptVersion,
    status.provider,
    status.model,
  ].some(value => String(value || '').trim())
  return Boolean(searchText || items.length || llm || hasDiagnostic || imageCount > 0)
}

function imageSemanticItems(analysis = {}) {
  const rawItems = Array.isArray(analysis.items)
    ? analysis.items
    : Array.isArray(analysis.images)
      ? analysis.images
      : []
  return rawItems.map((item, index) => {
    if (item && typeof item === 'object') return { index, ...item }
    return { index, url: String(item || '') }
  })
}

function statusTone(status) {
  if (['searchable', 'partial_searchable'].includes(status)) return 'ok'
  if (['manual_required', 'failed'].includes(status)) return 'danger'
  return 'wait'
}

function ReasonList({ reasons }) {
  if (!reasons.length) return <span className="muted">暂无原因</span>
  return <div className="semantic-reasons">{reasons.slice(0, 3).map((reason, index) => <span key={`${reason.type || index}-${index}`}>{semanticDimensionLabels[reason.type] || reason.type || 'match'} {reason.score !== undefined ? Number(reason.score).toFixed(2) : ''}</span>)}</div>
}

function TagSignal({ tags }) {
  if (!tags.length) return <span className="muted">未命中标签</span>
  return <div className="semantic-tags">{tags.slice(0, 4).map(tag => <span key={tag.tagId || tag.id || tag.name}>{tag.name || `#${tag.tagId || tag.id}`}</span>)}</div>
}

function ReviewRemarkModal({ dialog, onClose, onSubmit }) {
  const [value, setValue] = useState(dialog.value || '')
  const canSubmit = !dialog.required || value.trim()
  return (
    <Modal title={dialog.title} onClose={onClose}>
      <FormRow label={dialog.label}>
        <textarea
          className="remark-textarea"
          value={value}
          placeholder={dialog.placeholder}
          rows={5}
          autoFocus
          onChange={event => setValue(event.target.value)}
        />
      </FormRow>
      <div className="modal-actions">
        <button className="btn ghost" type="button" onClick={onClose}>取消</button>
        <button className="btn primary" type="button" disabled={!canSubmit} onClick={() => onSubmit(value)}>确定</button>
      </div>
    </Modal>
  )
}

function PoolCreateModal({ mode = 'create', form, setForm, onClose, onSubmit }) {
  const canSubmit = form.name.trim()
  return (
    <Modal title={mode === 'edit' ? '编辑内容池' : '新建内容池'} onClose={onClose}>
      <FormRow label="内容池名称"><input value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} placeholder="例如 美白内容池" /></FormRow>
      <FormRow label="描述"><input value={form.description} onChange={event => setForm({ ...form, description: event.target.value })} placeholder="内容池说明" /></FormRow>
      <div className="modal-actions"><button className="btn ghost" type="button" onClick={onClose}>取消</button><button className="btn primary" type="button" disabled={!canSubmit} onClick={onSubmit}>{mode === 'edit' ? '保存内容池' : '创建内容池'}</button></div>
    </Modal>
  )
}

function PoolLifecycleConfirmModal({ action, pool, loading, onClose, onConfirm }) {
  const isArchive = action === 'archive'
  const copy = isArchive
    ? {
        title: '归档内容池',
        label: '停止继续入池',
        description: '归档后，这个内容池不会再出现在笔记入池选择里；历史笔记、审核记录和统计数据仍会保留。',
        confirmText: '归档内容池',
        tone: 'danger',
        points: ['不再允许新笔记选择这个内容池', '已入池笔记和审核结论不会被删除'],
      }
    : {
        title: '发布内容池',
        label: '发布后可被选择',
        description: '发布后，这个内容池会出现在笔记入池选择里；不会自动加入任何笔记。',
        confirmText: '发布内容池',
        tone: 'primary',
        points: [],
      }
  const stats = [
    ['笔记', pool.noteCount || 0],
    ['待审核', pool.reviewCount || 0],
    ['已通过', pool.approvedCount || 0],
  ]
  return (
    <Modal title={copy.title} onClose={loading ? undefined : onClose} className={`confirm-modal ${isArchive ? 'danger' : 'publish'}`}>
      <div className="confirm-summary">
        <span>{copy.label}</span>
        <strong>{pool.name || '未命名内容池'}</strong>
        {pool.description ? <p>{pool.description}</p> : null}
      </div>
      {isArchive ? (
        <div className="confirm-stats">
          {stats.map(([label, value]) => <span key={label}><b>{value}</b>{label}</span>)}
        </div>
      ) : null}
      <p className="confirm-copy">{copy.description}</p>
      {copy.points.length ? (
        <ul className="confirm-points">
          {copy.points.map(point => <li key={point}>{point}</li>)}
        </ul>
      ) : null}
      <div className="modal-actions confirm-actions">
        <button className="btn ghost" type="button" disabled={loading} onClick={onClose}>取消</button>
        <button className={`btn ${copy.tone}`} type="button" disabled={loading} onClick={onConfirm}>
          {loading ? '处理中' : copy.confirmText}
        </button>
      </div>
    </Modal>
  )
}

function topicStatusTone(status) {
  return Number(status) === 1 || status === 'active' ? 'ok' : 'wait'
}

function topicStatusText(status) {
  return Number(status) === 1 || status === 'active' ? '启用' : '待发布'
}

function isTopicActive(topic) {
  return Number(topic?.status) === 1 || topic?.status === 'active'
}

function slotTabLabel(slot = {}) {
  if (slot.domain === 'shared') return '公共'
  if (slot.domain === 'note') return '笔记'
  if (slot.domain === 'image') return '生图'
  if (slot.domain === 'text') return '生文'
  return '其他'
}

function KeywordsPage({ collectionTasks, notes, contentPools, reload, flash, onJump }) {
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyKeywordForm)
  const [taskFilters, setTaskFilters] = useState({ status: '', taskType: '' })
  const activePools = contentPools.length ? contentPools.filter(isPoolActive) : fallbackContentPools
  const isEditingExistingTask = Boolean(editing?.id)
  const canEditTaskSource = !isEditingExistingTask || form.status === 'draft'
  const visibleTasks = collectionTasks.filter(task => {
    if (taskFilters.status && String(task.status) !== taskFilters.status) return false
    if (taskFilters.taskType && String(task.taskType || 'keyword') !== taskFilters.taskType) return false
    return true
  })

  function keywordStats(keyword) {
    const target = Number(keyword.noteNum || 0)
    const count = Number(keyword.noteCount || 0)
    const pctValue = target ? Math.min(100, Math.round((count / target) * 100)) : 0
    const latestTs = Number(keyword.lastCollectedTime || 0)
    return { count, target, pctValue, latestTs }
  }

  function open(keyword) {
    setEditing(keyword || {})
    setForm(keyword ? {
      name: keyword.name || '',
      keyword: ['keyword', 'note'].includes(keyword.taskType) && keyword.keywordItems?.length ? keyword.keywordItems.join('\n') : keyword.keyword || '',
      keywordItems: keyword.keywordItems || [],
      taskType: keyword.taskType || 'keyword',
      status: keyword.status || (Number(keyword.status) === 1 ? 'active' : 'paused'),
      noteNum: Number(keyword.noteNum || 20),
      searchType: keyword.searchType || 'all',
      sortRule: keyword.sortRule || 'composite',
      publishTime: keyword.publishTime || 'all',
      minLikes: Number(keyword.minLikes || 0),
      minComments: Number(keyword.minComments || 0),
      minCollects: Number(keyword.minCollects || 0),
      minShares: Number(keyword.minShares || 0),
      defaultPoolId: Number(keyword.defaultPoolId || 0),
      copiedFromId: 0,
    } : emptyKeywordForm)
  }

  function copyTask(keyword) {
    setEditing({ copiedFromId: keyword.id })
    setForm({
      name: `${keyword.name || keyword.keyword || '采集任务'} 复制`,
      keyword: ['keyword', 'note'].includes(keyword.taskType) && keyword.keywordItems?.length ? keyword.keywordItems.join('\n') : keyword.keyword || keyword.name || '',
      keywordItems: keyword.keywordItems || [],
      taskType: keyword.taskType || 'keyword',
      status: 'draft',
      noteNum: Number(keyword.noteNum || 20),
      searchType: keyword.searchType || 'all',
      sortRule: keyword.sortRule || 'composite',
      publishTime: keyword.publishTime || 'all',
      minLikes: Number(keyword.minLikes || 0),
      minComments: Number(keyword.minComments || 0),
      minCollects: Number(keyword.minCollects || 0),
      minShares: Number(keyword.minShares || 0),
      defaultPoolId: Number(keyword.defaultPoolId || 0),
      copiedFromId: Number(keyword.id || 0),
    })
  }

  async function save() {
    const sourceLocked = isEditingExistingTask && !canEditTaskSource
    const keywordItems = !sourceLocked && ['keyword', 'note'].includes(form.taskType)
      ? Array.from(new Set(form.keyword.split(/[\s,，、;；]+/).map(item => item.trim()).filter(Boolean)))
      : []
    const source = sourceLocked
      ? String(editing?.keyword || form.keyword || '').trim()
      : form.taskType === 'keyword' ? keywordItems.join(' ') : form.taskType === 'note' ? keywordItems[0] || '' : form.keyword.trim()
    if (!form.name.trim() || !source) {
      flash(`任务名和${form.taskType === 'note' ? '笔记链接' : '采集内容'}不能为空`)
      return
    }
    if (!sourceLocked && form.taskType === 'note' && keywordItems.some(item => !isXhsNoteLink(item))) {
      flash('仅支持小红书笔记链接，不支持作者主页链接')
      return
    }
    if (!sourceLocked && form.taskType === 'account' && !/^[a-zA-Z0-9_-]+$/.test(source)) {
      flash('请输入正确的小红书账号 ID')
      return
    }
    try {
      const payload = {
        ...form,
        keyword: source,
        keywordItems,
        name: form.name.trim(),
        noteNum: form.taskType === 'note' ? keywordItems.length : form.noteNum,
      }
      if (sourceLocked) {
        delete payload.name
        delete payload.taskType
        delete payload.keyword
        delete payload.keywordItems
        delete payload.copiedFromId
      }
      if (editing?.id) await api.put(`/keyword/update/${editing.id}`, payload)
      else await api.post('/keyword/create', { ...payload, status: 'draft' })
      setEditing(null)
      flash('采集任务已保存')
      reload()
    } catch (e) {
      flash(e.message || '采集任务保存失败')
    }
  }

  async function remove(keyword) {
    if (keyword.status !== 'draft') {
      flash('只有草稿任务可以删除')
      return
    }
    if (!window.confirm(`删除采集任务「${keyword.name}」？`)) return
    await api.delete(`/keyword/delete/${keyword.id}`)
    flash('采集任务已删除')
    reload()
  }

  async function setStatus(keyword, status) {
    try {
      await api.put(`/keyword/update/${keyword.id}`, { status })
      reload()
    } catch (e) {
      flash(e.message || '任务状态更新失败')
    }
  }

  function taskTypeLabel(task) {
    return taskTypeOptions.find(item => item.value === (task.taskType || 'keyword'))?.label || '关键词采集'
  }

  function searchTypeLabel(task) {
    return keywordSearchTypes.find(item => item.value === (task.searchType || 'all'))?.label || '图文+视频'
  }

  function sortRuleLabel(task) {
    return keywordSortRules.find(item => item.value === (task.sortRule || 'composite'))?.label || '综合'
  }

  function publishTimeLabel(task) {
    return keywordPublishTimes.find(item => item.value === (task.publishTime || 'all'))?.label || '不限'
  }

  function poolName(task) {
    return activePools.find(pool => Number(pool.id) === Number(task.defaultPoolId))?.name || '不指定内容池'
  }

  function renderTaskActions(keyword) {
    const status = keyword.status || 'paused'
    const actions = []
    if (status === 'draft') {
      actions.push(<button className="btn ghost sm" type="button" key="edit" onClick={() => open(keyword)}>编辑</button>)
      actions.push(<button className="btn primary sm" type="button" key="active" onClick={() => setStatus(keyword, 'active')}>启用</button>)
      actions.push(<button className="btn ghost sm danger-text" type="button" key="delete" onClick={() => remove(keyword)}>删除</button>)
    } else if (status === 'active') {
      actions.push(<button className="btn ghost sm" type="button" key="pause" onClick={() => setStatus(keyword, 'paused')}>暂停</button>)
    } else if (status === 'paused') {
      actions.push(<button className="btn ghost sm" type="button" key="edit" onClick={() => open(keyword)}>编辑</button>)
      actions.push(<button className="btn primary sm" type="button" key="active" onClick={() => setStatus(keyword, 'active')}>启用</button>)
      actions.push(<button className="btn ghost sm" type="button" key="copy" onClick={() => copyTask(keyword)}>复制</button>)
      actions.push(<button className="btn ghost sm" type="button" key="archive" onClick={() => setStatus(keyword, 'archived')}>归档</button>)
    } else if (status === 'completed') {
      actions.push(<button className="btn primary sm" type="button" key="copy" onClick={() => copyTask(keyword)}>复制</button>)
      actions.push(<button className="btn ghost sm" type="button" key="archive" onClick={() => setStatus(keyword, 'archived')}>归档</button>)
    }
    return actions.length ? actions : <span className="muted">-</span>
  }

  return (
    <div className="task-page">
      <div className="task-page-head">
        <div className="task-page-copy">
          <b>采集任务</b>
          <span>启用后由插件执行；完成后可复制新任务继续采集，归档后仅保留查看。</span>
        </div>
        <div className="task-filters">
          <select value={taskFilters.status} onChange={event => setTaskFilters({ ...taskFilters, status: event.target.value })}>
            {taskStatusOptions.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
          </select>
          <select value={taskFilters.taskType} onChange={event => setTaskFilters({ ...taskFilters, taskType: event.target.value })}>
            {taskTypeOptions.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
          </select>
          <button className="btn primary" type="button" onClick={() => open(null)}>新建采集任务</button>
        </div>
      </div>
      <div className="task-table-card">
        <table className="task-table">
          <colgroup>
            <col className="task-col-name" />
            <col className="task-col-status" />
            <col className="task-col-type" />
            <col className="task-col-keyword" />
            <col className="task-col-rule" />
            <col className="task-col-pool" />
            <col className="task-col-progress" />
            <col className="task-col-latest" />
            <col className="task-col-actions" />
          </colgroup>
          <thead>
            <tr>
              <th>任务</th>
              <th>状态</th>
              <th>采集类型</th>
              <th>采集内容</th>
              <th>采集规则</th>
              <th>内容池</th>
              <th>目标与进度</th>
              <th>最近采集</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleTasks.map(keyword => {
              const stats = keywordStats(keyword)
              const thresholds = [
                Number(keyword.minLikes || 0) ? `点赞 >= ${compactNumber(keyword.minLikes)}` : '',
                Number(keyword.minComments || 0) ? `评论 >= ${compactNumber(keyword.minComments)}` : '',
                Number(keyword.minCollects || 0) ? `收藏 >= ${compactNumber(keyword.minCollects)}` : '',
                Number(keyword.minShares || 0) ? `分享 >= ${compactNumber(keyword.minShares)}` : '',
              ].filter(Boolean)
              const taskType = keyword.taskType || 'keyword'
              return (
                <tr key={keyword.id}>
                  <td className="task-sticky-left">
                    <div className="task-name-cell">
                      <button className="task-name-link" type="button" onClick={() => onJump('notes', { collectionTaskId: keyword.id })}>{keyword.name}</button>
                      <span>ID：{keyword.id} · 来源：{keyword.collectionTaskFromId || keyword.id}</span>
                    </div>
                  </td>
                  <td><Pill tone={taskStatusTone(keyword.status)}>{taskStatusLabel(keyword.status)}</Pill></td>
                  <td><span className={`task-type-badge type-${taskType}`}>{taskTypeLabel(keyword)}</span></td>
                  <td>
                    <CollectionContentTokens task={keyword} />
                  </td>
                  <td>
                    <div className="task-rule-pills">
                      {taskType === 'keyword' ? <>
                        <span>{searchTypeLabel(keyword)}</span>
                        <span>{sortRuleLabel(keyword)}</span>
                        <span>{publishTimeLabel(keyword)}</span>
                        {thresholds.length ? thresholds.map(item => <span className="threshold" key={item}>{item}</span>) : <span className="muted-pill">门槛不限</span>}
                      </> : null}
                      {taskType === 'account' ? <>
                        <span>主页笔记</span>
                        {thresholds.length ? thresholds.map(item => <span className="threshold" key={item}>{item}</span>) : <span className="muted-pill">门槛不限</span>}
                      </> : null}
                      {taskType === 'note' ? <span>指定链接</span> : null}
                    </div>
                  </td>
                  <td><span className="pool-token">{poolName(keyword)}</span></td>
                  <td>
                    <div className="task-progress-cell">
                      <div className="task-progress-meta">
                        <b>{stats.count}</b>
                        <span>/ {stats.target || '-'}</span>
                      </div>
                      <div className="prog-track"><i style={{ width: `${stats.pctValue}%` }} /></div>
                    </div>
                  </td>
                  <td className="muted nowrap">{stats.latestTs ? formatShortDate(stats.latestTs) : '-'}</td>
                  <td>
                    <div className="task-actions">
                      {renderTaskActions(keyword)}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {!visibleTasks.length ? <Empty text="暂无采集任务" /> : null}
      </div>
      {editing ? (
        <Modal className="task-create-modal" title={editing.id ? '编辑采集任务' : (editing.copiedFromId ? '复制采集任务' : '新建采集任务')} onClose={() => setEditing(null)}>
          <p className="task-form-intro">
            {editing.id
              ? (canEditTaskSource ? '草稿任务可修改任务名称、采集类型、采集来源和规则，保存后任务状态保持不变。' : '任务启用后采集来源不可修改；需要调整来源时请复制为新任务。')
              : '配置采集来源和筛选条件。新任务将保存为草稿，可在列表确认后启用。'}
          </p>
          <section className="task-form-section">
            <div className="task-form-section-head"><b>基础信息</b><span>用于识别和管理这次采集</span></div>
            <div className="task-form-grid basics">
              <FormRow label="任务名称"><input value={form.name} disabled={!canEditTaskSource} onChange={event => setForm({ ...form, name: event.target.value })} placeholder="例如：夏季防晒内容采集" /></FormRow>
              <FormRow label="采集类型"><select value={form.taskType} disabled={!canEditTaskSource} onChange={event => setForm({ ...form, taskType: event.target.value, keyword: '', keywordItems: [] })}>{taskTypeOptions.filter(item => item.value).map(item => <option value={item.value} key={item.value}>{item.label}</option>)}</select></FormRow>
            </div>
          </section>
          <section className="task-form-section">
            <div className="task-form-section-head"><b>采集来源</b><span>{form.taskType === 'keyword' ? '多个关键词将在执行时以空格拼接' : form.taskType === 'account' ? '填写作者主页中的账号 ID' : '支持一次添加多篇小红书笔记'}</span></div>
            <FormRow label={form.taskType === 'note' ? '笔记链接' : form.taskType === 'account' ? '账号 ID' : '关键词'}>
              <textarea className="task-source-input" rows={form.taskType === 'note' ? 4 : 3} value={form.keyword} disabled={!canEditTaskSource} onChange={event => setForm({ ...form, keyword: event.target.value })} placeholder={form.taskType === 'note' ? '每行粘贴一条小红书笔记链接' : form.taskType === 'account' ? '输入作者主页中的账号 ID，例如 5f123456000000000100abcd' : '输入多个关键词，用空格、换行、逗号或顿号分隔'} />
              {['keyword', 'note'].includes(form.taskType) && form.keyword.trim() ? <div className="keyword-preview">{Array.from(new Set(form.keyword.split(/[\s,，、;；]+/).map(item => item.trim()).filter(Boolean))).map(item => <span className={form.taskType === 'note' ? 'link-chip' : ''} title={item} key={item}>{form.taskType === 'note' ? compactNoteLink(item) : item}</span>)}</div> : null}
              <small className="field-help">{!canEditTaskSource ? '已启用任务的采集来源固定，用于保持历史归因和去重域稳定。' : form.taskType === 'note' ? '链接属于同一任务，插件将按顺序逐篇采集。' : form.taskType === 'account' ? '账号 ID 是作者主页 /user/profile/ 后面的标识；启用后由升级版插件采集该作者笔记。' : '这些关键词属于同一任务；执行时用空格拼接为一个小红书搜索词。'}</small>
            </FormRow>
          </section>
          {form.taskType !== 'note' ? <section className="task-form-section">
            <div className="task-form-section-head"><b>采集规则</b><span>控制结果范围和采集数量</span></div>
            <div className="task-form-grid rules">
              {form.taskType === 'keyword' ? <FormRow label="内容形式"><select value={form.searchType} onChange={event => setForm({ ...form, searchType: event.target.value })}>{keywordSearchTypes.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}</select></FormRow> : null}
              {form.taskType === 'keyword' ? <FormRow label="排序方式"><select value={form.sortRule} onChange={event => setForm({ ...form, sortRule: event.target.value })}>{keywordSortRules.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}</select></FormRow> : null}
              {form.taskType === 'keyword' ? <FormRow label="发布时间"><select value={form.publishTime} onChange={event => setForm({ ...form, publishTime: event.target.value })}>{keywordPublishTimes.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}</select></FormRow> : null}
              <FormRow label="目标笔记数"><input type="number" min="1" value={form.noteNum} onChange={event => setForm({ ...form, noteNum: Number(event.target.value) })} /></FormRow>
              <FormRow label="最低点赞"><input type="number" min="0" value={form.minLikes} onChange={event => setForm({ ...form, minLikes: Number(event.target.value) })} /></FormRow>
              <FormRow label="最低评论"><input type="number" min="0" value={form.minComments} onChange={event => setForm({ ...form, minComments: Number(event.target.value) })} /></FormRow>
              <FormRow label="最低收藏"><input type="number" min="0" value={form.minCollects} onChange={event => setForm({ ...form, minCollects: Number(event.target.value) })} /></FormRow>
              <FormRow label="最低分享"><input type="number" min="0" value={form.minShares} onChange={event => setForm({ ...form, minShares: Number(event.target.value) })} /></FormRow>
            </div>
          </section> : null}
          <section className="task-form-section pool-setting">
            <div className="task-form-section-head"><b>入池设置</b><span>可选，采集后仍可在笔记库调整</span></div>
            <FormRow label="默认内容池"><select value={form.defaultPoolId} onChange={event => setForm({ ...form, defaultPoolId: Number(event.target.value) })}><option value={0}>暂不指定</option>{activePools.map(pool => <option value={pool.id} key={pool.id}>{pool.name}</option>)}</select></FormRow>
          </section>
          <div className="modal-actions task-form-actions"><span>保存后状态：{editing.id ? taskStatusLabel(form.status) : '草稿'}</span><button className="btn ghost" type="button" onClick={() => setEditing(null)}>取消</button><button className="btn primary" type="button" onClick={save}>{editing.id ? '保存修改' : '保存草稿'}</button></div>
        </Modal>
      ) : null}
    </div>
  )
}

function AuthorsPage({ authors, notes }) {
  const derived = useMemo(() => {
    const map = new Map()
    notes.forEach(note => {
      if (!map.has(note.author)) map.set(note.author, { nickName: note.author, notes: 0, avator: note.authorAvator || note.authorAvatar || '', xhsId: '', code: note.authorCode || '' })
      map.get(note.author).notes += 1
    })
    authors.forEach(author => {
      const row = map.get(author.nickName) || { notes: 0 }
      map.set(author.nickName || author.code || author.id, { ...row, ...author })
    })
    return Array.from(map.values())
  }, [authors, notes])

  return (
    <div className="author-grid">
      {derived.map((author, index) => {
        const authorId = author.xhsId || author.code || ''
        return (
          <article className="author-card" key={author.id || author.code || author.nickName}>
            {author.avator ? <img className="author-ava img" src={author.avator} alt="" /> : <div className="author-ava">{String(author.nickName || '?').slice(0, 1)}</div>}
            <div className="author-name-b">{author.nickName || '未命名作者'}</div>
            <div className="author-xhs">{authorId ? `${author.xhsId ? '小红书号' : '用户ID'} ${authorId}` : <span>待补全</span>}</div>
            <div className="author-stat">产出 {author.notes || notes.filter(note => note.author === author.nickName).length || 0} 篇笔记</div>
            {author.profile ? <a className="author-foot" href={author.profile} target="_blank" rel="noreferrer">小红书主页</a> : null}
          </article>
        )
      })}
      {!derived.length ? <Empty text="暂无作者" /> : null}
    </div>
  )
}

function SettingsPage({ localState, updateLocal, reload, flash }) {
  const [settings, setSettings] = useState(localState.settings)

  function save() {
    updateLocal({ settings })
    flash('设置已保存')
  }

  return (
    <div className="settings-grid">
      <Panel title="接口配置">
        <FormRow label="API Base URL"><input value={settings.apiBase} onChange={event => setSettings({ ...settings, apiBase: event.target.value })} /></FormRow>
        <FormRow label="下游推送地址"><input value={settings.downstream} onChange={event => setSettings({ ...settings, downstream: event.target.value })} placeholder="https://..." /></FormRow>
        <button className="btn primary" type="button" onClick={save}>保存设置</button>
      </Panel>
      <Panel title="成员权限">
        <div className="member"><span className="user-ava">A</span><div><b>admin</b><em>超级管理员</em></div><Pill tone="ok">启用</Pill></div>
        <div className="member"><span className="user-ava">O</span><div><b>operator</b><em>运营成员</em></div><Pill tone="wait">待邀请</Pill></div>
      </Panel>
      <Panel title="数据维护">
        <div className="danger-zone">
          <button className="btn ghost" type="button" onClick={reload}>重新同步服务端</button>
          <button className="btn danger" type="button" onClick={() => { localStorage.removeItem('lume-admin-state'); location.reload() }}>清除本地运营状态</button>
        </div>
      </Panel>
    </div>
  )
}

function ExtensionPage({ flash }) {
  const env = currentExtensionEnv()
  const installSteps = [
    '下载 zip 后解压到本地目录',
    '打开 chrome://extensions/ 并开启开发者模式',
    '点击“加载已解压的扩展程序”，选择刚解压的目录',
    '打开插件面板，在接口配置里选择生产、测试或本地环境',
  ]

  async function copyApi() {
    try {
      await navigator.clipboard.writeText(env.apiBaseUrl)
      flash('API 地址已复制')
    } catch {
      flash('复制失败，请手动选择 API 地址')
    }
  }

  return (
    <div className="extension-page">
      <section className="extension-hero">
        <div>
          <span className={`env-pill ${env.key}`}>{env.label}</span>
          <h2>Lume Tuber 采集助手</h2>
          <p>当前页面匹配的插件 API 地址为 <b>{env.apiBaseUrl}</b></p>
        </div>
        <div className="extension-actions">
          {env.downloadUrl ? (
            <a className="btn primary" href={env.downloadUrl} download>下载插件 zip</a>
          ) : (
            <button className="btn primary" type="button" disabled>本地无远程包</button>
          )}
          <button className="btn ghost" type="button" onClick={copyApi}>复制 API 地址</button>
        </div>
      </section>

      <div className="extension-grid">
        <Panel title="安装">
          <ol className="extension-steps">
            {installSteps.map(step => <li key={step}>{step}</li>)}
          </ol>
        </Panel>
        <Panel title="环境">
          <div className="extension-env-list">
            <div><b>生产</b><span>https://tuber.lumeseed.com/lume-tuber/api</span></div>
            <div><b>测试</b><span>https://tuber-test.elingoai.com/lume-tuber/api</span></div>
            <div><b>本地</b><span>http://localhost:8099/lume-tuber/api</span></div>
          </div>
        </Panel>
        <Panel title="使用">
          <div className="extension-copy">
            <p>插件面板的环境按钮会立即保存到 Chrome 本地存储；任务执行、上报、评论采集都会使用当前选择的环境。</p>
            <p>Chrome 不允许普通网页静默安装扩展，所以这里提供 zip 包下载和加载说明。</p>
          </div>
        </Panel>
      </div>
    </div>
  )
}

function Kpi({ icon, value, label, trend }) {
  return <div className="kpi-card"><div className="kpi-top"><span className="kpi-ic">{icon}</span><span className="kpi-trend">{trend}</span></div><div className="kpi-val">{value}</div><div className="kpi-lbl">{label}</div></div>
}

function Panel({ title, action, children }) {
  return <section className="panel"><div className="panel-h"><h3>{title}</h3>{action}</div>{children}</section>
}

function Segment({ labels }) {
  return <span className="seg-mini">{labels.map((label, index) => <button className={index === 0 ? 'active' : ''} type="button" key={label}>{label}</button>)}</span>
}

function Funnel({ label, value, pct }) {
  return <div className="funnel-row"><span>{label}</span><div className="funnel-bar" style={{ width: `${Math.max(4, pct)}%` }}><i /></div><b>{compactNumber(value)}</b></div>
}

function ProgressRow({ name, count, target }) {
  const value = target ? Math.min(100, Math.round((count / target) * 100)) : 0
  return <div className="kw-prog-row"><div><b>{name}</b><em>{count} / {target || '-'}</em></div><div className="prog-track"><i style={{ width: `${value}%` }} /></div></div>
}

function RecentNote({ note, index }) {
  return <div className="recent-item"><Cover note={note} index={index} small /><div><b>{note.title}</b><span>{note.author} · {note.keyword || '-'}</span></div><em>❤ {compactNumber(note.likesNum)}</em></div>
}

function MiniStat({ label, value }) {
  return <div className="mini-stat"><b>{compactNumber(value)}</b><span>{label}</span></div>
}

function Metric({ value, label }) {
  return <div className="dmetric"><b>{value}</b><span>{label}</span></div>
}

function Cover({ note, index = 0, small = false, large = false }) {
  const fallbackStyle = { background: coverBg(index) }
  return (
    <span className={`cover ${small ? 'small' : ''} ${large ? 'large' : ''}`} style={note.coverImage ? undefined : fallbackStyle}>
      {note.coverImage ? <img src={note.coverImage} alt="" loading="lazy" /> : null}
      {note.isVideo ? <i>▶</i> : null}
    </span>
  )
}

function AuthorInline({ name, index = 0 }) {
  return <span className="author-inline"><span style={{ background: avaColor(index) }}>{String(name || '?').slice(0, 1)}</span>{name || '未知作者'}</span>
}

function Chip({ tag, tagGroups = seedTagGroups }) {
  const group = tagGroups.find(item => (item.tags || []).some(child => (typeof child === 'string' ? child : child.name) === tag))
  return <span className={`chip ${group?.color || ''}`}>{tag}</span>
}

function Pill({ tone = 'wait', children }) {
  return <span className={`pill ${tone}`}>{children}</span>
}

function Empty({ text }) {
  return <div className="empty">{text}</div>
}

const COMMENT_PAGE_SIZE = 6

function countCommentTree(comments = []) {
  return comments.reduce((sum, comment) => sum + 1 + countCommentTree(comment.subComments || []), 0)
}

function commentLikes(comment) {
  return Number(comment.likesCount ?? comment.likes_count ?? comment.likeCount ?? 0)
}

function CommentPreview({ comments, page = 1, onPageChange }) {
  const sortedComments = [...comments].sort((a, b) => commentLikes(b) - commentLikes(a))
  const totalRoots = comments.length
  const totalCount = countCommentTree(comments)
  const totalPages = Math.max(1, Math.ceil(totalRoots / COMMENT_PAGE_SIZE))
  const currentPage = Math.min(Math.max(1, page), totalPages)
  const rows = sortedComments.slice((currentPage - 1) * COMMENT_PAGE_SIZE, currentPage * COMMENT_PAGE_SIZE)
  if (!rows.length) return <Empty text="暂无评论" />
  return (
    <div className="comment-block">
      <div className="comment-summary">
        <span>{totalRoots} 条根评论</span>
        <span>{Math.max(0, totalCount - totalRoots)} 条回复</span>
      </div>
      <div className="comment-prev">
        {rows.map(comment => <CommentNode comment={comment} key={comment.id || comment.commentId} />)}
      </div>
      {totalPages > 1 ? (
        <div className="comment-pager">
          <button type="button" disabled={currentPage <= 1} onClick={() => onPageChange?.(currentPage - 1)}>上一页</button>
          <span>{currentPage} / {totalPages}</span>
          <button type="button" disabled={currentPage >= totalPages} onClick={() => onPageChange?.(currentPage + 1)}>下一页</button>
        </div>
      ) : null}
    </div>
  )
}

function CommentNode({ comment, depth = 0 }) {
  const replies = [...(comment.subComments || [])].sort((a, b) => commentLikes(b) - commentLikes(a))
  const likes = commentLikes(comment)
  return (
    <div className={`cmt ${depth ? 'reply' : ''}`}>
      <div className="cmt-head">
        <b>{comment.author || '匿名'}</b>
        <span>{likes ? `${compactNumber(likes)} 赞` : '0 赞'}{replies.length ? ` · ${replies.length} 条回复` : ''}</span>
      </div>
      <p>{comment.text || comment.content || ''}</p>
      {replies.length ? (
        <div className="comment-replies">
          {replies.map(reply => <CommentNode comment={reply} depth={depth + 1} key={reply.id || reply.commentId} />)}
        </div>
      ) : null}
    </div>
  )
}

function Modal({ title, children, onClose, className = '' }) {
  useEffect(() => {
    if (!onClose) return undefined
    const onKeyDown = event => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])
  const maskClassName = `modal-mask ${className.includes('semantic-drawer') ? 'drawer-mask' : ''}`
  return (
    <div className={maskClassName} onMouseDown={event => { if (event.target === event.currentTarget) onClose?.() }}>
      <div className={`modal ${className}`} role="dialog" aria-modal="true" aria-label={title} onMouseDown={event => event.stopPropagation()}>
        <div className="modal-head">
          <h2>{title}</h2>
          {onClose ? <button className="icon-btn" type="button" aria-label="关闭" onClick={onClose}>×</button> : null}
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  )
}

function Drawer({ title, subtitle, children, onClose, className = '' }) {
  useEffect(() => {
    if (!onClose) return undefined
    const onKeyDown = event => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])
  return (
    <>
      <div className="drawer-mask show" onMouseDown={event => { if (event.target === event.currentTarget) onClose?.() }} />
      <aside className={`drawer ${className}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer-head">
          <div>
            <span>{title}</span>
            {subtitle ? <b>{subtitle}</b> : null}
          </div>
          {onClose ? <button className="icon-btn" type="button" aria-label="关闭" onClick={onClose}>×</button> : null}
        </div>
        <div className="drawer-body">{children}</div>
      </aside>
    </>
  )
}

function FormRow({ label, children, className = '' }) {
  return <label className={`form-row ${className}`}><span>{label}</span>{children}</label>
}

function statusText(value) {
  const map = { 0: '待处理', 1: '处理中', 2: '已完成' }
  return map[Number(value)] || '-'
}

function pct(value, total) {
  if (!total) return 0
  return Math.round((value / total) * 100)
}

function todayCount(notes) {
  const today = new Date().toDateString()
  return notes.filter(note => {
    const time = Number(note.createdTime || note.updateTime || 0)
    return time && new Date(time).toDateString() === today
  }).length
}

function makeTrend(notes, comments) {
  const labels = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
  const noteBase = Math.max(1, Math.ceil(notes.length / 7))
  const commentBase = Math.max(1, Math.ceil(comments.length / 7))
  return labels.map((label, index) => {
    const noteCount = noteBase + ((index * 7) % Math.max(2, noteBase + 3))
    const commentCount = commentBase + ((index * 11) % Math.max(2, commentBase + 4))
    const max = Math.max(noteBase + 8, commentBase + 8, 10)
    return { label, notePct: Math.min(100, (noteCount / max) * 100), commentPct: Math.min(65, (commentCount / max) * 65) }
  })
}

function exportCsv(rows, flash) {
  const header = ['title', 'author', 'keyword', 'likes', 'comments', 'detailUrl']
  const body = rows.map(note => header.map(key => JSON.stringify(note[key] || note[`${key}Num`] || '')).join(','))
  const blob = new Blob([[header.join(','), ...body].join('\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `lume-notes-${Date.now()}.csv`
  a.click()
  URL.revokeObjectURL(url)
  flash('已导出 CSV')
}

function coverBg(index) {
  return [
    'linear-gradient(135deg,#ffd1dc,#ff6b8b)',
    'linear-gradient(135deg,#d7c8ff,#6d5efc)',
    'linear-gradient(135deg,#c8ecff,#3a8dff)',
    'linear-gradient(135deg,#c9f7d9,#16a34a)',
    'linear-gradient(135deg,#ffe6b3,#f59e0b)',
  ][index % 5]
}

function avaColor(index) {
  return ['#b85f40', '#5b6ee1', '#2f946d', '#ad7a22', '#7b4bc7', '#b8467b'][index % 6]
}

export default App
